import { toast } from 'sonner';

const API_URL = import.meta.env.VITE_API_URL || '/api';
export { API_URL };

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, string[]>;
  };
}

interface PredictionItem {
  variant_id: string;
  variant_label: string | null;
  stock_unit: string;
  product_id: string;
  product_name: string;
  category_name: string | null;
  current_stock: number;
  alert_threshold: number;
  weeklyConsumption: number;
  monthlyConsumption: number;
  trend: 'up' | 'down' | 'stable' | 'inactive';
  daysUntilRupture: number | null;
  ruptureDate: string | null;
  needForHorizon: number;
  estimatedCost: number;
  stockValue: number;
  lastConsumption: string | null;
  status: 'rupture_imminent' | 'low' | 'ok' | 'dormant';
  suggestedThreshold: number;
  thresholdDelta: number;
}

class ApiClient {
  private accessToken: string | null = null;
  private _refreshToken: string | null = null;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    // Load tokens from localStorage
    this.accessToken = localStorage.getItem('accessToken');
    this._refreshToken = localStorage.getItem('refreshToken');
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this._refreshToken = refreshToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  clearTokens() {
    this.accessToken = null;
    this._refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  getAccessToken() {
    return this.accessToken;
  }

  async refreshToken(): Promise<string | null> {
    await this.refreshAccessToken();
    return this.accessToken;
  }

  private async refreshAccessToken(): Promise<void> {
    if (!this._refreshToken) {
      throw new Error('No refresh token');
    }

    // Prevent multiple simultaneous refresh calls
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this._refreshToken }),
        });

        const data: ApiResponse<{ tokens: { accessToken: string; refreshToken: string } }> = await response.json();

        if (data.success && data.data) {
          this.setTokens(data.data.tokens.accessToken, data.data.tokens.refreshToken);
        } else {
          this.clearTokens();
          throw new Error('Token refresh failed');
        }
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs = 20_000
  ): Promise<ApiResponse<T>> {
    const isFormData = options.body instanceof FormData;
    const headers: HeadersInit = {
      ...options.headers,
    };

    // Only add Content-Type if not FormData (browser sets boundary for FormData)
    if (!(options.body instanceof FormData)) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    // Abort after timeoutMs (prevents infinite hang on mobile / slow networks)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    let response: Response;
    try {
      response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    // If 401 and we have a refresh token, try to refresh
    if (response.status === 401 && this._refreshToken) {
      // Clone response to read body without consuming it
      const responseClone = response.clone();

      try {
        const data = await responseClone.json();

        if (data.error?.code === 'TOKEN_EXPIRED') {
          await this.refreshAccessToken();

          // Retry the request with new token
          (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
          const retryController = new AbortController();
          const retryTimeoutId = setTimeout(() => retryController.abort(), timeoutMs);
          try {
            response = await fetch(`${API_URL}${endpoint}`, {
              ...options,
              headers,
              signal: retryController.signal,
            });
            return response.json();
          } catch (_e) {
            this.clearTokens();
            toast.warning('Session expirée — veuillez vous reconnecter');
            window.location.href = '/login';
            throw new Error('Session expired');
          } finally {
            clearTimeout(retryTimeoutId);
          }
        }
      } catch (error) {
        // If refresh fails or there's any error, clear tokens
        this.clearTokens();
        window.location.href = '/login';
        throw new Error('Session expired');
      }

      // Body already consumed — return parsed data directly
      return responseClone.json();
    }

    return response.json();
  }

  // Auth endpoints
  async register(data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    accountName: string;
    invitationToken?: string;
  }) {
    return this.request<{
      user: { id: string; email: string; first_name: string | null; last_name: string | null };
      account: { id: string; name: string; slug: string; enabled_modules: string[] };
      tokens: { accessToken: string; refreshToken: string; expiresIn: number };
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async login(email: string, password: string) {
    return this.request<{
      user: { id: string; email: string; first_name: string | null; last_name: string | null; profile_completed: boolean };
      account: { id: string; name: string; slug: string; enabled_modules: string[]; plan: string };
      tokens: { accessToken: string; refreshToken: string; expiresIn: number };
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout() {
    const result = await this.request('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: this._refreshToken }),
    });
    this.clearTokens();
    return result;
  }

  async getMe() {
    return this.request<{
      user: { id: string; email: string; first_name: string | null; last_name: string | null; profile_completed: boolean };
      accounts: Array<{ id: string; name: string; slug: string; enabled_modules: string[]; role: string }>;
      currentAccount: string;
    }>('/auth/me');
  }

  async getProfile() {
    return this.request<{ profile: {
      id: string; email: string; prefix: string | null; first_name: string | null;
      last_name: string | null; display_name: string | null; office_name: string | null;
      office_address: string | null; office_postal_code: string | null; office_city: string | null;
      office_phone: string | null; phone: string | null; avatar_url: string | null; email_verified: boolean;
      email_notifications_new_discussion: boolean; account_name: string | null;
    } }>('/auth/profile');
  }

  async updateProfile(data: {
    prefix?: string | null; firstName?: string | null; lastName?: string | null;
    displayName?: string | null; officeName?: string | null; officeAddress?: string | null;
    officePostalCode?: string | null; officeCity?: string | null; officePhone?: string | null;
    officeCountry?: string | null;
    phone?: string | null; emailNotificationsNewDiscussion?: boolean;
  }) {
    return this.request<{ profile: any }>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async uploadAccountLogo(file: File) {
    const formData = new FormData();
    formData.append('logo', file);
    return this.request<{ logo_url: string }>('/auth/account/logo', {
      method: 'POST',
      body: formData,
    });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async forgotPassword(email: string) {
    return this.request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // Stock endpoints
  async getProducts(params?: { page?: number; limit?: number; search?: string; categoryId?: string; status?: string; archived?: boolean; supplierId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.archived) searchParams.set('archived', 'true');
    if (params?.supplierId) searchParams.set('supplierId', params.supplierId);

    const query = searchParams.toString();
    return this.request<{
      products: Array<{
        id: string;
        name: string;
        description: string | null;
        category_id: string | null;
        manufacturer: string | null;
        manufacturer_registry_id: string | null;
        manufacturer_cabinet_id: string | null;
        image_url: string | null;
        is_unique: boolean;
        is_active: boolean;
        category_name: string | null;
        category_color: string | null;
        variant_count: number;
        total_stock: number;
        status: 'ok' | 'low' | 'out';
        unique_variant_id: string | null;
        has_barcode: boolean;
        variants: Array<{
          id: string;
          label: string | null;
          stock_unit: string;
          alert_threshold: number;
          current_stock: number;
          status: 'ok' | 'low' | 'out';
          has_barcode: boolean;
        }> | null;
      }>;
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/stock/products${query ? `?${query}` : ''}`);
  }

  async getProductBadgeCounts(params?: { search?: string; categoryId?: string; archived?: boolean }) {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
    if (params?.archived) searchParams.set('archived', 'true');

    const query = searchParams.toString();
    return this.request<{
      total: number;
      ok: number;
      low: number;
      out: number;
    }>(`/stock/products/badge-counts${query ? `?${query}` : ''}`);
  }

  async getProduct(id: string) {
    return this.request<{
      product: {
        id: string;
        name: string;
        description: string | null;
        reference: string;
        barcode: string | null;
        sku: string | null;
        manufacturer_reference: string | null;
        manufacturer: string | null;
        manufacturer_registry_id: string | null;
        manufacturer_cabinet_id: string | null;
        category_id: string | null;
        current_stock: number;
        min_stock: number;
        max_stock: number | null;
        unit: string;
        unit_price: number;
        tva_rate: number;
        supplier_id: string | null;
        location: string | null;
        track_expiry: boolean;
        expiry_date: string | null;
        unit_per_box: number;
        track_units_in_box: boolean;
        notes: string | null;
      };
    }>(`/stock/products/${id}`);
  }

  async checkProductName(name: string, excludeId?: string) {
    const params = new URLSearchParams({ name });
    if (excludeId) params.set('excludeId', excludeId);
    return this.request<{ exists: boolean; isArchived?: boolean; productId?: string }>(
      `/stock/products/check-name?${params}`
    );
  }

  async createProduct(data: {
    name: string;
    description?: string;
    categoryId?: string;
    manufacturerRegistryId?: string | null;
    manufacturerCabinetId?: string | null;
    imageUrl?: string;
    notes?: string;
    isUnique?: boolean;
    // Unique mode fields
    stockUnit?: string;
    alertThreshold?: number;
    supplierId?: string;
    supplierRef?: string;
    manufacturerRef?: string;
    barcode?: string;
    catalogPrice?: number;
    destockUnitaire?: boolean;
    unitesParBoite?: number;
    barcodeUnitaire?: string;
    storageLocationId?: string;
    // Multi mode fields
    variants?: Array<{
      label?: string;
      stockUnit?: string;
      alertThreshold?: number;
      destockUnitaire?: boolean;
      unitesParBoite?: number;
      barcodeUnitaire?: string;
      storageLocationId?: string;
      attributes?: string[];
      suppliers?: Array<{
        supplierId?: string;
        supplierRef?: string;
        catalogPrice?: number;
        isPrincipal?: boolean;
      }>;
      barcode?: string;
      manufacturerRef?: string;
      unitsPerPackage?: number;
    }>;
  }) {
    return this.request<{ product: { id: string } }>('/stock/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProduct(id: string, data: Partial<{
    name: string;
    description: string;
    categoryId: string;
    manufacturerRegistryId: string | null;
    manufacturerCabinetId: string | null;
    imageUrl: string;
    notes: string;
    isUnique: boolean;
    isActive: boolean;
  }>) {
    return this.request<{ product: { id: string } }>(`/stock/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProduct(id: string) {
    return this.request(`/stock/products/${id}`, { method: 'DELETE' });
  }

  async restoreProduct(id: string) {
    return this.request(`/stock/products/${id}/restore`, { method: 'PUT' });
  }

  async mergeProducts(targetProductId: string, sourceProductIds: string[], variantLabels?: Record<string, string>) {
    return this.request<{ mergedVariantCount: number }>('/stock/products/merge', {
      method: 'POST',
      body: JSON.stringify({ targetProductId, sourceProductIds, variantLabels }),
    });
  }

  async detachVariant(variantId: string, mode: 'new_product' | 'move_to_product', targetProductId?: string, newProductName?: string) {
    return this.request<{ newProductId?: string }>('/stock/products/detach-variant', {
      method: 'POST',
      body: JSON.stringify({ variantId, mode, targetProductId, newProductName }),
    });
  }

  async getProductsByGenericType(types: string[]) {
    return this.request<{
      groups: Array<{
        genericType: string;
        products: Array<{
          id: string;
          name: string;
          manufacturer: string | null;
          imageUrl: string | null;
          variants: Array<{
            id: string;
            label: string | null;
            stockUnit: string;
            currentStock: number;
            lots: Array<{
              id: string;
              lotNumber: string | null;
              expiresAt: string | null;
              quantity: number;
            }>;
          }>;
        }>;
      }>;
    }>(`/stock/products/by-generic-type?types=${types.join(',')}`);
  }

  async parseGS1Barcode(barcode: string) {
    return this.request<{
      gtin: string | null;
      lot: string | null;
      expiry: string | null;
      serial: string | null;
      raw: string;
    }>('/stock/products/parse-gs1', {
      method: 'POST',
      body: JSON.stringify({ barcode }),
    });
  }

  async getArchivedVariants() {
    return this.request<{
      products: Array<{
        id: string;
        name: string;
        manufacturer: string | null;
        image_url: string | null;
        variants: Array<{
          id: string;
          label: string;
          stock_unit: string;
        }>;
      }>;
    }>('/stock/products/archived-variants');
  }

  async adjustStock(lotId: string, newQuantity: number, reason?: string) {
    return this.request<{
      adjustment: {
        lotId: string;
        previousQuantity: number;
        newQuantity: number;
        delta: number;
      };
    }>('/stock/adjust', {
      method: 'POST',
      body: JSON.stringify({ lotId, newQuantity, reason }),
    });
  }

  async adjustStockGroup(lotIds: string[], newTotal: number, reason?: string) {
    return this.request<{
      adjustment: {
        previousTotal: number;
        newTotal: number;
        affectedLots: number;
      };
    }>('/stock/adjust-group', {
      method: 'POST',
      body: JSON.stringify({ lotIds, newTotal, reason }),
    });
  }

  // Supplier Pricing Variants (old product_variants table, now renamed to product_supplier_prices)
  async getSupplierPrices(productId: string) {
    return this.request<{
      variants: Array<{
        id: string;
        product_id: string;
        supplier_id: string;
        variant_name: string | null;
        variant_type: string | null;
        sku: string | null;
        unit_price: number;
        currency: string;
        last_price_check: string | null;
        previous_price: number | null;
        price_changed_at: string | null;
        is_available: boolean;
        stock_status: string | null;
        supplier_name?: string;
      }>;
    }>(`/stock/products/${productId}/supplier-prices`);
  }

  async getSupplierPricesBySupplier(productId: string, supplierId: string) {
    return this.request<{
      variants: Array<{
        id: string;
        product_id: string;
        supplier_id: string;
        variant_name: string | null;
        variant_type: string | null;
        sku: string | null;
        unit_price: number;
        currency: string;
        last_price_check: string | null;
        previous_price: number | null;
        is_available: boolean;
        stock_status: string | null;
      }>;
    }>(`/stock/products/${productId}/supplier-prices/supplier/${supplierId}`);
  }

  async createSupplierPrice(data: {
    productId: string;
    supplierId: string;
    variantName?: string;
    variantType?: string;
    sku?: string;
    unitPrice: number;
    currency?: string;
    isAvailable?: boolean;
    stockStatus?: string;
  }) {
    return this.request<{ variant: { id: string } }>('/stock/supplier-prices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSupplierPrice(variantId: string, data: {
    variantName?: string;
    variantType?: string;
    sku?: string;
    unitPrice?: number;
    currency?: string;
    isAvailable?: boolean;
    stockStatus?: string;
  }) {
    return this.request<{ variant: { id: string } }>(`/stock/supplier-prices/${variantId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSupplierPrice(variantId: string) {
    return this.request(`/stock/supplier-prices/${variantId}`, { method: 'DELETE' });
  }

  async bulkUpdateSupplierPrices(productId: string, data: {
    supplierId: string;
    variants: Array<{
      variantName: string;
      sku?: string;
      unitPrice: number;
      isAvailable?: boolean;
      stockStatus?: string;
    }>;
  }) {
    return this.request<{
      variants: Array<{
        id: string;
        variant_name: string;
        unit_price: number;
        previous_price: number | null;
      }>;
    }>(`/stock/products/${productId}/supplier-prices/bulk-update`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Product-Supplier Relationships
  async getProductSuppliers(productId: string) {
    return this.request<{
      suppliers: Array<{
        id: string;
        product_id: string;
        supplier_id: string;
        product_url: string | null;
        supplier_sku: string | null;
        is_primary: boolean;
        lead_time_days: number | null;
        minimum_order_quantity: number | null;
        notes: string | null;
        supplier_name: string;
        supplier_code: string | null;
      }>;
    }>(`/stock/products/${productId}/suppliers`);
  }

  async addProductSupplier(productId: string, data: {
    supplierId: string;
    productUrl?: string;
    supplierSku?: string;
    isPrimary?: boolean;
    leadTimeDays?: number;
    minimumOrderQuantity?: number;
    notes?: string;
  }) {
    return this.request<{ supplier: { id: string } }>(`/stock/products/${productId}/suppliers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProductSupplier(productSupplierId: string, data: {
    productUrl?: string;
    supplierSku?: string;
    isPrimary?: boolean;
    leadTimeDays?: number;
    minimumOrderQuantity?: number;
    notes?: string;
  }) {
    return this.request<{ supplier: { id: string } }>(`/stock/product-suppliers/${productSupplierId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async removeProductSupplier(productSupplierId: string) {
    return this.request(`/stock/product-suppliers/${productSupplierId}`, {
      method: 'DELETE',
    });
  }

  async getProductHistory(
    productId: string,
    period: '30' | '90' | '180' | '365' | 'all'
  ) {
    return this.request<{
      period: string;
      startDate: string;
      endDate: string;
      stockHistory: Array<{
        date: string;
        quantity: number;
        delta: number;
      }>;
      priceHistory: Array<{
        date: string;
        unitPrice: number;
        lotNumber: string;
        quantity: number;
      }>;
    }>(`/stock/products/${productId}/history?period=${period}`);
  }

  async getCategories() {
    return this.request<{
      categories: Array<{ id: string; name: string; description: string | null; product_count: number }>;
    }>('/stock/categories');
  }

  async getCategoriesTree() {
    return this.request<{
      categories: Array<{
        id: string;
        name: string;
        code: string;
        color: string | null;
        description: string | null;
        default_stock_unit: string;
        product_count: number;
      }>;
    }>('/stock/categories/tree');
  }

  async getCategoriesAllWithCounts() {
    return this.request<{
      categories: Array<{
        id: string;
        name: string;
        code: string;
        color: string | null;
        description: string | null;
        default_stock_unit: string;
        display_order: number;
        product_count: number;
      }>;
    }>('/stock/categories/all-with-counts');
  }

  async getAccountCategories(includeId?: string) {
    const params = includeId ? `?includeId=${includeId}` : '';
    return this.request<{
      categories: Array<{
        id: string;
        name: string;
        code: string;
        color: string | null;
        description: string | null;
        default_stock_unit: string;
        default_alert_threshold: number;
      }>;
    }>(`/stock/categories/for-account${params}`);
  }

  async getAccountPreferences() {
    return this.request<{
      preferences: {
        hideEmptyCategories: boolean;
        selectedCategoryIds: string[] | null;
        useConsumers: boolean;
        flatDisplayMode: boolean;
      };
    }>('/stock/preferences');
  }

  async updateAccountPreferences(data: {
    hideEmptyCategories?: boolean;
    selectedCategoryIds?: string[] | null;
    useConsumers?: boolean;
    flatDisplayMode?: boolean;
  }) {
    return this.request<{
      preferences: {
        hideEmptyCategories: boolean;
        selectedCategoryIds: string[] | null;
        useConsumers: boolean;
        flatDisplayMode: boolean;
      };
    }>('/stock/preferences', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getUserUiSetting<T = unknown>(key: string): Promise<T | null> {
    const res = await this.request<{ value: T | null }>(`/user/ui-settings/${key}`);
    return res.success ? (res.data?.value ?? null) : null;
  }

  async setUserUiSetting<T = unknown>(key: string, value: T): Promise<void> {
    await this.request(`/user/ui-settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  async createCategory(data: { name: string; description?: string }) {
    return this.request<{ category: { id: string } }>('/stock/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSuppliers(options?: { includeInactive?: boolean }) {
    const params = options?.includeInactive ? '?includeInactive=true' : '';
    return this.request<{
      suppliers: Array<{
        id: string;
        account_id: string;
        supplier_registry_id: string;
        client_code: string | null;
        contact_name: string | null;
        phone: string | null;
        email: string | null;
        order_mode: 'email' | 'telephone' | 'en_ligne' | null;
        order_email: string | null;
        order_phone: string | null;
        free_shipping_minimum: number | null;
        payment_terms: string | null;
        notes: string | null;
        is_active: boolean;
        custom_logo_url: string | null;
        registry_name: string;
        registry_website: string | null;
        registry_logo_url: string | null;
        registry_product_search_url: string | null;
      }>;
    }>(`/stock/suppliers${params}`);
  }

  async getSupplier(id: string) {
    return this.request<{
      supplier: {
        id: string;
        account_id: string;
        supplier_registry_id: string;
        client_code: string | null;
        contact_name: string | null;
        phone: string | null;
        email: string | null;
        order_mode: 'email' | 'telephone' | 'en_ligne' | null;
        order_email: string | null;
        order_phone: string | null;
        free_shipping_minimum: number | null;
        payment_terms: string | null;
        notes: string | null;
        is_active: boolean;
        registry_name: string;
        registry_website: string | null;
        registry_logo_url: string | null;
        registry_product_search_url: string | null;
      };
    }>(`/stock/suppliers/${id}`);
  }

  async createSupplier(data: {
    supplierRegistryId?: string;
    customName?: string;
    customWebsite?: string;
    clientCode?: string;
    contactName?: string;
    phone?: string;
    email?: string;
    orderMode?: 'email' | 'telephone' | 'en_ligne';
    orderEmail?: string;
    orderPhone?: string;
    freeShippingMinimum?: number;
    paymentTerms?: string;
    notes?: string;
  }) {
    return this.request<{ supplier: { id: string } }>('/stock/suppliers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSupplier(id: string, data: {
    clientCode?: string;
    contactName?: string;
    phone?: string;
    email?: string;
    orderMode?: 'email' | 'telephone' | 'en_ligne';
    orderEmail?: string;
    orderPhone?: string;
    freeShippingMinimum?: number;
    paymentTerms?: string;
    notes?: string;
    isActive?: boolean;
  }) {
    return this.request<{ supplier: { id: string } }>(`/stock/suppliers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSupplier(id: string) {
    return this.request<{ message: string }>(`/stock/suppliers/${id}`, {
      method: 'DELETE',
    });
  }

  async getSupplierStats(id: string) {
    return this.request<{
      stats: {
        order_count: number;
        total_spent: number;
        last_order_date: string | null;
        avg_order_value: number;
        avg_delivery_days: number | null;
        complete_reception_rate: number;
        active_product_count: number;
        last_delivery_date: string | null;
        last_invoice_date: string | null;
        spending_trend_pct: number | null;
        sparklines: {
          weekly_value: number[];
          weekly_count: number[];
          weekly_avg_order: number[];
          weekly_delivery: number[];
          weekly_reception: number[];
        } | null;
        benchmarks: {
          avg_delivery_days: number | null;
          avg_order_value: number | null;
          complete_reception_rate: number | null;
        } | null;
      };
    }>(`/stock/suppliers/${id}/stats`);
  }

  async getSupplierEvolution(id: string, months: 3 | 6 | 12 = 12) {
    return this.request<{
      period: string;
      data: Array<{
        date: string;
        orderValue: number;
        orderCount: number;
        lineCount: number;
      }>;
      previousYearData: Array<{
        date: string;
        orderValue: number;
        orderCount: number;
        lineCount: number;
      }>;
    }>(`/stock/suppliers/${id}/evolution?months=${months}`);
  }

  async getSupplierTopProducts(id: string) {
    return this.request<{
      products: Array<{
        variant_id: string;
        product_id: string;
        product_name: string;
        variant_label: string | null;
        total_quantity: number;
        total_value: number;
        order_count: number;
        current_stock: number;
        min_price: number | null;
        max_price: number | null;
        avg_price: number | null;
        last_price: number | null;
      }>;
    }>(`/stock/suppliers/${id}/top-products`);
  }

  async getSupplierInactiveProducts(id: string, days = 90) {
    return this.request<{
      products: Array<{
        variant_id: string;
        product_id: string;
        product_name: string;
        variant_label: string | null;
        last_order_date: string;
        total_orders: number;
        last_price: number | null;
        days_inactive: number;
      }>;
    }>(`/stock/suppliers/${id}/inactive-products?days=${days}`);
  }

  async getStockMovements(params?: {
    productId?: string;
    variantId?: string;
    lotId?: string;
    type?: string;
    page?: number;
    limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.productId) searchParams.set('productId', params.productId);
    if (params?.variantId) searchParams.set('variantId', params.variantId);
    if (params?.lotId) searchParams.set('lotId', params.lotId);
    if (params?.type) searchParams.set('type', params.type);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    const query = searchParams.toString();
    return this.request<{
      movements: Array<{
        id: string;
        lot_id: string;
        movement_type: string;
        quantity: number;
        reason: string | null;
        performed_by: string | null;
        user_name: string | null;
        lot_number: string | null;
        product_name: string | null;
        variant_label: string | null;
        created_at: string;
      }>;
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/stock/movements${query ? `?${query}` : ''}`);
  }

  async getLowStockAlerts() {
    return this.request<{
      alerts: Array<{
        variant_id: string;
        product_name: string;
        variant_label: string | null;
        stock_unit: string;
        alert_threshold: number;
        current_stock: number;
      }>;
    }>('/stock/movements/alerts/low-stock');
  }

  async getExpiringLots(days: number = 30) {
    return this.request<{
      lots: Array<{
        id: string;
        lot_number: string | null;
        quantity_remaining: number;
        expiry_date: string;
        days_until_expiry: number;
        product_name: string | null;
        variant_label: string | null;
      }>;
    }>(`/stock/movements/alerts/expiring?days=${days}`);
  }

  async expireLots(lotId?: string, reason?: string) {
    return this.request<{
      expired: Array<{
        lotId: string;
        lotNumber: string | null;
        expiredQuantity: number;
      }>;
      count?: number;
    }>('/stock/expire', {
      method: 'POST',
      body: JSON.stringify({ lotId, reason }),
    });
  }

  // ==================== DASHBOARD ====================

  async getStockDashboard() {
    return this.request<{
      totalProducts: number;
      totalVariants: number;
      totalActiveLots: number;
      stockValue: number;
      ruptureCount: number;
      lowStockCount: number;
      expiringCounts: { expired: number; within30: number; within90: number };
      ruptures: Array<{
        variant_id: string;
        product_id: string;
        product_name: string;
        variant_label: string | null;
        stock_unit: string;
        alert_threshold: number;
      }>;
      lowStock: Array<{
        variant_id: string;
        product_id: string;
        product_name: string;
        variant_label: string | null;
        stock_unit: string;
        current_stock: number;
        alert_threshold: number;
      }>;
      expiringLots: Array<{
        id: string;
        lot_number: string | null;
        expiry_date: string;
        days_until_expiry: number;
        quantity_remaining: number;
        product_name: string;
        variant_label: string | null;
      }>;
      pendingOrdersCount: number;
      topSuppliers: Array<{
        supplier_id: string;
        supplier_name: string;
        order_count: number;
      }>;
    }>('/stock/dashboard');
  }

  async getStockEvolution(months: 3 | 6 | 12 = 12) {
    return this.request<{
      period: string;
      data: Array<{
        date: string;
        stockValue: number;
        movementVolume: number;
        totalStockVolume: number;
      }>;
    }>(`/stock/dashboard/evolution?months=${months}`);
  }

  async getStockLastModified() {
    return this.request<{ lastModifiedAt: string | null }>('/stock/dashboard/last-modified');
  }

  async getDashboardByCategory() {
    return this.request<{
      categories: Array<{
        id: string;
        name: string;
        total_value: number;
        product_count: number;
      }>;
    }>('/stock/dashboard/by-category');
  }

  async getExpirations(level?: string) {
    const query = level ? `?level=${level}` : '';
    return this.request<{
      summary: {
        expired: { count: number; value: number };
        within30: { count: number; value: number };
        within90: { count: number; value: number };
        ok: { count: number };
      };
      lots: Array<{
        id: string;
        lot_number: string | null;
        expiry_date: string;
        days_remaining: number;
        quantity_remaining: number;
        unit_price: number | null;
        value: number;
        product_name: string;
        product_id: string;
        variant_id: string;
        variant_label: string | null;
        supplier_name: string | null;
        level: 'expired' | 'critical' | 'warning' | 'ok';
        prediction: {
          weeklyConsumption: number;
          daysNeeded: number | null;
          status: 'ok' | 'risk' | 'unknown';
        } | null;
      }>;
    }>(`/stock/dashboard/expirations${query}`);
  }

  // ==================== EXPORT COMPTABLE ====================

  async getExportComptable(params: {
    date: string;
    method: 'pump' | 'fifo' | 'last_price';
    groupBy?: 'category' | 'none';
    includeLotDetail?: boolean;
    includeZeroStock?: boolean;
  }) {
    const sp = new URLSearchParams();
    sp.set('date', params.date);
    sp.set('method', params.method);
    if (params.groupBy) sp.set('groupBy', params.groupBy);
    if (params.includeLotDetail) sp.set('includeLotDetail', 'true');
    if (params.includeZeroStock) sp.set('includeZeroStock', 'true');
    return this.request<{
      date: string;
      method: string;
      groupBy: string;
      totalQuantity: number;
      totalValue: number;
      productCount: number;
      variantCount: number;
      avgUnitValue: number;
      categories?: Array<{
        category: string;
        category_color: string | null;
        totalQuantity: number;
        totalValue: number;
        lines: Array<{
          variant_label: string | null;
          stock_unit: string;
          product_name: string;
          category_name: string | null;
          quantity: number;
          unit_price: number;
          value: number;
          lots: Array<{
            lot_number: string | null;
            reception_date: string | null;
            expiry_date: string | null;
            quantity: number;
            unit_price: number;
            value: number;
          }>;
        }>;
      }>;
      lines?: Array<{
        variant_label: string | null;
        stock_unit: string;
        product_name: string;
        category_name: string | null;
        quantity: number;
        unit_price: number;
        value: number;
      }>;
    }>(`/stock/dashboard/export?${sp.toString()}`);
  }

  // ==================== PREDICTIONS ====================

  async getPredictions(horizon?: number) {
    const query = horizon ? `?horizon=${horizon}` : '';
    return this.request<{
      horizon: number;
      summary: {
        ruptureImminent: number;
        lowStock: number;
        ok: number;
        dormant: number;
      };
      ruptureImminent: PredictionItem[];
      lowStock: PredictionItem[];
      ok: PredictionItem[];
      dormant: PredictionItem[];
      thresholdSuggestions: PredictionItem[];
    }>(`/stock/dashboard/predictions${query}`);
  }

  // ==================== SCANNER ====================

  async scannerLookup(barcode: string) {
    return this.request<{
      parsed: {
        type: 'gs1' | 'simple';
        gtin?: string;
        lotNumber?: string;
        expiryDate?: string;
        serialNumber?: string;
        quantity?: number;
        raw: string;
      };
      match: {
        product_id: string;
        product_name: string;
        is_unique: boolean;
        variant_id: string;
        variant_label: string | null;
        barcode: string;
        units_per_package: number;
        stock_unit: string;
        manufacturer: string | null;
        category_name: string | null;
      } | null;
      lot: {
        id: string;
        lot_number: string;
        quantity_remaining: number;
        expiry_date: string | null;
        status: string;
      } | null;
      community: {
        id: string;
        name: string;
        manufacturer: string | null;
        suggested_category: string | null;
        stock_unit: string | null;
        packaging: string | null;
        cabinet_count: number;
        reliability: number;
      } | null;
    }>('/stock/scanner/lookup', {
      method: 'POST',
      body: JSON.stringify({ barcode }),
    });
  }

  async scannerParse(barcode: string) {
    return this.request<{
      // GS1 fields
      gtin?: string;
      lotNumber?: string;
      serialNumber?: string;
      expiryDate?: string;
      productionDate?: string;
      additionalId?: string;
      customerPartNo?: string;
      manufacturerRef?: string;
      // HIBC fields
      upn?: string;
      labelerCode?: string;
      productCode?: string;
      // Common
      symbology?: string;
      quantity?: number;
      raw: string;
    }>('/stock/scanner/parse', {
      method: 'POST',
      body: JSON.stringify({ barcode }),
    });
  }

  async scannerGetPending() {
    return this.request<{
      scans: Array<{
        id: string;
        barcode: string;
        code_type: string;
        extracted_data: Record<string, any>;
        identification_source: string | null;
        suggested_name: string | null;
        suggested_manufacturer: string | null;
        context: string;
        status: string;
        created_at: string;
      }>;
    }>('/stock/scanner/pending');
  }

  async scannerSavePending(data: {
    barcode: string;
    codeType: string;
    extractedData: Record<string, any>;
    context: 'reception' | 'inventory' | 'manual';
    identificationSource?: string;
    suggestedName?: string;
    suggestedManufacturer?: string;
    orderId?: string;
  }) {
    return this.request<{ scan: { id: string; created_at: string } }>('/stock/scanner/pending', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async scannerCreateFromScan(data: {
    name: string;
    barcode: string;
    isUnique: boolean;
    categoryId?: string;
    manufacturer?: string;
    stockUnit?: string;
    alertThreshold?: number;
    supplierId?: string;
    supplierRef?: string;
    catalogPrice?: number;
    lotNumber?: string;
    expiryDate?: string;
    manufacturingDate?: string;
    quantity?: number;
    purchasePrice?: number;
    scanId?: string;
  }) {
    return this.request<{
      productId: string;
      variantId: string;
      lotId?: string;
    }>('/stock/scanner/create-from-scan', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async scannerAssociate(data: {
    barcode: string;
    variantId: string;
    supplierId?: string;
    supplierRef?: string;
    catalogPrice?: number;
    scanId?: string;
  }) {
    return this.request<{ variantId: string }>('/stock/scanner/associate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async scannerIgnore(scanId: string) {
    return this.request<{ message: string }>(`/stock/scanner/pending/${scanId}/ignore`, {
      method: 'PATCH',
    });
  }

  async scannerSearchProducts(q: string) {
    return this.request<{
      results: Array<{
        product_id: string;
        product_name: string;
        manufacturer: string | null;
        is_unique: boolean;
        variant_id: string;
        variant_label: string | null;
        stock_unit: string;
        barcode: string | null;
      }>;
    }>(`/stock/scanner/search-products?q=${encodeURIComponent(q)}`);
  }

  // ==================== PURCHASE ORDERS ====================

  async getOrders(params?: {
    status?: string;
    supplierId?: string;
    page?: number;
    limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.supplierId) searchParams.set('supplierId', params.supplierId);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    const query = searchParams.toString();
    return this.request<{
      orders: Array<{
        id: string;
        order_number: string;
        supplier_name: string;
        supplier_logo: string | null;
        order_date: string;
        expected_date: string | null;
        received_date: string | null;
        status: string;
        subtotal: number | null;
        tva_amount: number | null;
        total: number | null;
        line_count: string;
        linked_invoices_count: number;
      }>;
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/stock/orders${query ? `?${query}` : ''}`);
  }

  async getOrder(id: string) {
    return this.request<{
      order: {
        id: string;
        order_number: string;
        supplier_id: string;
        supplier_name: string;
        supplier_logo: string | null;
        order_date: string;
        expected_date: string | null;
        received_date: string | null;
        status: string;
        subtotal: number | null;
        tva_amount: number | null;
        total: number | null;
        notes: string | null;
        created_by_name: string | null;
        lines: Array<{
          id: string;
          variant_id: string;
          quantity_ordered: number;
          quantity_received: number;
          order_unit_price: number | null;
          invoice_unit_price: number | null;
          status: string;
          lot_id: string | null;
          barcode: string | null;
          variant_label: string | null;
          product_name: string | null;
        }>;
      };
    }>(`/stock/orders/${id}`);
  }

  async createOrder(data: {
    supplierId: string;
    expectedDeliveryDate?: string;
    shippingCost?: number;
    notes?: string;
    lines: Array<{
      variantId: string;
      quantityOrdered: number;
      quantityUnits?: number;
      orderUnitPrice?: number;
    }>;
  }) {
    return this.request<{
      order: { id: string; orderNumber: string };
    }>('/stock/orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async markOrderSent(orderId: string) {
    return this.request<{ message: string }>(`/stock/orders/${orderId}/send`, {
      method: 'PUT',
    });
  }

  async markOrderReceived(orderId: string, actualDeliveryDate?: string) {
    return this.request<{ message: string }>(`/stock/orders/${orderId}/receive`, {
      method: 'PUT',
      body: JSON.stringify({ actualDeliveryDate }),
    });
  }

  async updateOrderStatus(orderId: string, status: string, actualDeliveryDate?: string) {
    return this.request<{ message: string }>(`/stock/orders/${orderId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, actualDeliveryDate }),
    });
  }

  async receiveOrderLine(
    orderId: string,
    lineId: string,
    data: {
      quantityReceived: number;
      invoiceUnitPrice?: number;
      lotNumber?: string;
      expiryDate?: string;
    }
  ) {
    return this.request<{
      reception: { lotId: string; quantityReceived: number };
    }>(`/stock/orders/${orderId}/lines/${lineId}/receive`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getOrderSupplierLinks(orderId: string) {
    return this.request<{
      links: Array<{
        variantId: string;
        productName: string;
        variantLabel: string | null;
        supplierRef: string | null;
        productUrl: string | null;
      }>;
    }>(`/stock/orders/${orderId}/supplier-links`);
  }

  async getOrderEmailBody(orderId: string) {
    return this.request<{
      subject: string;
      body: string;
    }>(`/stock/orders/${orderId}/email-body`);
  }

  // ==================== RECEPTION ====================

  async receiveStock(data: {
    variantId: string;
    quantity: number;
    lotNumber?: string;
    manufacturingDate?: string;
    expiryDate?: string;
    supplierId?: string;
    purchasePrice?: number;
    receptionDate?: string;
    invoiceRef?: string;
    scannedBarcode?: string;
    notes?: string;
    orderId?: string;
    orderLineId?: string;
  }) {
    return this.request<{
      reception: {
        lotId: string;
        movementId: string;
        quantity: number;
        unitPrice: number | null;
        orderLineUpdated: boolean;
        orderStatusUpdated: string | null;
        newStock: number | null;
        barcode: string | null;
        productName: string | null;
        variantLabel: string | null;
      };
    }>('/stock/receive', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async cancelReception(movementId: string) {
    return this.request<{
      movementId: string;
      lotId: string;
      removedQuantity: number;
    }>(`/stock/movements/${movementId}/cancel-reception`, {
      method: 'POST',
    });
  }

  // ==================== RECEPTION UNIDENTIFIED ====================

  async getReceptionUnidentified() {
    return this.request<{
      scans: Array<{
        id: string;
        barcode: string;
        extracted_data: Record<string, any>;
        identification_source: string | null;
        created_at: string;
      }>;
    }>('/stock/scanner/pending?context=reception');
  }

  async deleteReceptionUnidentified(scanId: string) {
    return this.request<void>(`/stock/scanner/pending/${scanId}/ignore`, { method: 'PATCH' });
  }

  // ==================== RECEPTION WAITING ====================

  async getReceptionWaiting() {
    return this.request<{
      items: Array<{
        id: string;
        variant_id: string;
        product_name: string;
        variant_label: string | null;
        suggested_qty: number;
        suggested_price: number | null;
        order_id: string | null;
        order_line_id: string | null;
        created_at: string;
      }>;
    }>('/stock/reception-waiting');
  }

  async addReceptionWaiting(data: {
    variantId: string;
    productName: string;
    variantLabel: string | null;
    suggestedQty: number;
    suggestedPrice?: number;
    orderId?: string;
    orderLineId?: string;
  }) {
    return this.request<{ item: { id: string; created_at: string } }>('/stock/reception-waiting', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeReceptionWaiting(id: string) {
    return this.request<void>(`/stock/reception-waiting/${id}`, { method: 'DELETE' });
  }

  async receiveStockBatch(items: Array<{
    variantId: string;
    quantity: number;
    lotNumber?: string;
    manufacturingDate?: string;
    expiryDate?: string;
    supplierId?: string;
    purchasePrice?: number;
    receptionDate?: string;
    invoiceRef?: string;
    scannedBarcode?: string;
    notes?: string;
    orderId?: string;
    orderLineId?: string;
  }>) {
    return this.request<{
      results: Array<{
        lotId: string;
        quantity: number;
        unitPrice: number | null;
        orderLineUpdated: boolean;
        orderStatusUpdated: string | null;
      }>;
      totalLots: number;
    }>('/stock/receive/batch', {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  // ==================== CONSUMPTION ====================

  async consumePreview(variantId: string, quantity: number, lotId?: string) {
    const params = new URLSearchParams({ variant_id: variantId, quantity: String(quantity) });
    if (lotId) params.set('lot_id', lotId);
    return this.request<{
      preview: {
        variantId: string;
        variantLabel: string | null;
        productName: string;
        stockUnit: string;
        requested: number;
        available: number;
        sufficient: boolean;
        alertThreshold: number;
        stockAfter: number;
        willTriggerAlert: boolean;
        lots: Array<{
          lotId: string;
          lotNumber: string | null;
          expiryDate: string | null;
          quantityBefore: number;
          quantityTaken: number;
          quantityAfter: number;
        }>;
      };
    }>(`/stock/consume/preview?${params.toString()}`);
  }

  async checkFEFOViolation(variantId: string, lotId: string) {
    return this.request<{
      fefo_violation: boolean;
      older_lots: Array<{
        lot_id: string;
        lot_number: string | null;
        expiry_date: string | null;
        quantity_remaining: number;
        storage_location: string | null;
      }>;
      scanned_lot: {
        lot_id: string;
        lot_number: string | null;
        expiry_date: string | null;
        quantity_remaining: number;
      } | null;
    }>('/stock/movements/check-fefo', {
      method: 'POST',
      body: JSON.stringify({ variantId, lotId }),
    });
  }

  async consumeStock(data: {
    variantId: string;
    quantity: number;
    reason?: string;
    lotId?: string;
    fefoOverride?: boolean;
  }) {
    return this.request<{
      consumption: {
        totalConsumed: number;
        lots: Array<{
          lotId: string;
          lotNumber: string | null;
          expiryDate: string | null;
          quantityBefore: number;
          quantityTaken: number;
          quantityAfter: number;
        }>;
        stockAfter: number;
        alertTriggered: boolean;
        alertThreshold: number;
      };
    }>('/stock/consume', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== SORTIE RAPIDE ====================

  async sortieBarcode(rawBarcode: string, reason: string, force?: boolean, consumerId?: string | null) {
    return this.request<{
      status: 'success' | 'out_of_stock' | 'unidentified' | 'fefo_warning' | 'lot_not_found';
      movementId?: string;
      product?: { id: string; name: string; variantId: string; variantLabel: string | null };
      lot?: { number: string | null; expiryDate: string | null };
      stockRemaining?: number;
      fefoWarning?: {
        olderLot: { id: string; number: string | null; expiryDate: string | null; stock: number };
        currentLot: { number: string | null; expiryDate: string | null };
      };
      scannedLotNumber?: string;
      lotExhausted?: boolean;
      fallbackLot?: { id: string; number: string | null; expiryDate: string | null; stock: number };
      parsedBarcode?: {
        gtin?: string;
        lotNumber?: string;
        productionDate?: string | null;
        expiryDate?: string | null;
        serialNumber?: string;
        manufacturerRef?: string;
      };
      rawBarcode?: string;
      scanId?: string;
    }>('/stock/movements/sortie', {
      method: 'POST',
      body: JSON.stringify({ rawBarcode, reason, ...(force ? { force: true } : {}), ...(consumerId ? { consumerId } : {}) }),
    });
  }

  async cancelMovement(movementId: string) {
    return this.request<{
      movementId: string;
      lotId: string;
      restoredQuantity: number;
    }>(`/stock/movements/${movementId}/cancel`, {
      method: 'POST',
    });
  }

  async getSortieUnidentified() {
    return this.request<{
      scans: Array<{ id: string; barcode: string; created_at: string }>;
    }>('/stock/movements/sortie/unidentified');
  }

  async deleteUnidentifiedScan(scanId: string) {
    return this.request<{ deleted: boolean }>(
      `/stock/movements/sortie/unidentified/${scanId}`,
      { method: 'DELETE' }
    );
  }

  async associateSortieUnidentified(
    scanId: string,
    data: { variantId: string; reason: string; saveBarcode?: boolean }
  ) {
    return this.request<{
      status: 'success' | 'out_of_stock';
      movementId?: string;
      product?: { id: string; name: string; variantId: string; variantLabel: string | null };
      lot?: { number: string | null; expiryDate: string | null };
      stockRemaining?: number;
      fefoWarning?: {
        olderLot: { id: string; number: string | null; expiryDate: string | null; stock: number };
        currentLot: { number: string | null; expiryDate: string | null };
      };
      parsedBarcode?: {
        gtin?: string;
        lotNumber?: string;
        productionDate?: string | null;
        expiryDate?: string | null;
        serialNumber?: string;
        manufacturerRef?: string;
      };
    }>(`/stock/movements/sortie/unidentified/${scanId}/associate`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async consumeFromLot(lotId: string, reason: string) {
    return this.request<{
      consumption: { lotId: string; consumed: number; remaining: number; movementId: string };
    }>('/stock/movements/consume', {
      method: 'POST',
      body: JSON.stringify({ lotId, quantity: 1, reason }),
    });
  }

  // ==================== SUPPLIER REGISTRY ====================

  async getSupplierRegistry() {
    return this.request<{
      registry: Array<{
        id: string;
        name: string;
        logo_url: string | null;
        website: string | null;
        is_public: boolean;
        already_added: boolean;
        deactivated_supplier_id: string | null;
      }>;
    }>('/stock/supplier-registry');
  }

  // ==================== MANUFACTURER REGISTRY ====================

  async getManufacturerRegistry() {
    return this.request<{
      registry: {
        public: Array<{
          id: string;
          name: string;
          website: string | null;
          country: string | null;
          is_public: boolean;
          logo_url: string | null;
        }>;
        custom: Array<{
          id: string;
          name: string;
          website: string | null;
          notes: string | null;
        }>;
      };
    }>('/stock/manufacturer-registry');
  }

  async addCustomManufacturer(data: { name: string; website?: string; country?: string; notes?: string }) {
    return this.request<{
      manufacturer: {
        id: string;
        name: string;
        website: string | null;
        country: string | null;
        notes: string | null;
      };
    }>('/stock/manufacturers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== SUGGESTIONS ====================

  async getSuggestedAttributes(categoryId: string, manufacturer?: string) {
    const params = new URLSearchParams({ category_id: categoryId });
    if (manufacturer) params.set('manufacturer', manufacturer);
    return this.request<{
      attributes: Record<string, string[]>;
    }>(`/stock/suggestions/attributes?${params}`);
  }

  async getSuggestionHabits(categoryId: string) {
    return this.request<{
      habits: Array<{
        attribute_type_id: string;
        attribute_name: string;
        values: string[];
      }>;
    }>(`/stock/suggestions/habits?category_id=${categoryId}`);
  }

  async recordSuggestionHabits(habits: Array<{ attributeTypeId: string; values: string[] }>) {
    return this.request<{ recorded: number }>('/stock/suggestions/habits', {
      method: 'POST',
      body: JSON.stringify({ habits }),
    });
  }

  // ==================== BARCODE LOOKUP (new schema) ====================

  async barcodeLookup(barcode: string) {
    return this.request<{
      match: {
        product_id: string;
        product_name: string;
        is_unique: boolean;
        variant_id: string;
        variant_label: string | null;
        barcode: string;
        units_per_package: number;
        is_archived: boolean;
      } | null;
    }>('/stock/products/barcode-lookup', {
      method: 'POST',
      body: JSON.stringify({ barcode }),
    });
  }

  // ==================== LEGACY LOT METHODS (Scanner/Reception/Sortie pages) ====================

  async lookupBarcode(barcode: string) {
    return this.request<{
      parsed: {
        type: 'gs1' | 'simple';
        gtin?: string;
        lotNumber?: string;
        expiryDate?: string;
        barcode?: string;
      };
      product: {
        id: string;
        name: string;
        reference: string;
        currentStock: number;
        unit: string;
      } | null;
      lot: {
        id: string;
        lotNumber: string;
        currentQuantity: number;
        expiryDate: string | null;
      } | null;
      suggestedExpiryDate?: string;
      suggestedLotNumber?: string;
    }>('/stock/barcode/lookup', {
      method: 'POST',
      body: JSON.stringify({ barcode }),
    });
  }

  async getProductLots(productId: string, includeEmpty: boolean = false) {
    return this.request<{
      lots: Array<{
        id: string;
        lot_number: string;
        serial_number: string | null;
        initial_quantity: number;
        current_quantity: number;
        expiry_date: string | null;
        manufacturing_date: string | null;
        received_at: string;
        unit_cost: number | null;
        status: string;
        supplier_name: string | null;
        expiry_status: 'ok' | 'expiring_soon' | 'expired' | null;
      }>;
    }>(`/stock/products/${productId}/lots?includeEmpty=${includeEmpty}`);
  }

  async receiveLot(data: {
    productId: string;
    lotNumber: string;
    quantity: number;
    expiryDate?: string;
    manufacturingDate?: string;
    unitCost?: number;
    supplierId?: string;
    serialNumber?: string;
    invoiceNumber?: string;
    notes?: string;
  }) {
    return this.request<{
      reception: {
        lotId: string;
        productId: string;
        lotNumber: string;
        quantity: number;
        newLotQuantity: number;
        newProductStock: number;
      };
    }>('/stock/lots/receive', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deductFromLot(lotId: string, data: {
    quantity: number;
    reason?: string;
    notes?: string;
  }) {
    return this.request<{
      deduction: {
        lotId: string;
        productId: string;
        lotNumber: string;
        quantity: number;
        newLotQuantity: number;
        newProductStock: number;
      };
    }>(`/stock/lots/${lotId}/deduct`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deductFEFO(productId: string, data: {
    quantity: number;
    reason?: string;
    notes?: string;
  }) {
    return this.request<{
      deduction: {
        productId: string;
        totalDeducted: number;
        deductions: Array<{
          lotId: string;
          lotNumber: string;
          quantity: number;
        }>;
      };
    }>(`/stock/products/${productId}/deduct-fefo`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== PRODUCT ATTRIBUTES & VARIANTS ====================

  async getAttributes() {
    return this.request<{
      attributes: Array<{
        id: string;
        account_id: string;
        name: string;
        type: 'size' | 'color' | 'concentration' | 'volume' | 'custom';
        display_order: number;
        is_active: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>('/stock/attributes');
  }

  async createAttribute(data: {
    name: string;
    type: 'size' | 'color' | 'concentration' | 'volume' | 'custom';
  }) {
    return this.request<{
      attribute: {
        id: string;
        name: string;
        type: string;
      };
    }>('/stock/attributes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAttributeTerms(attributeId: string) {
    return this.request<{
      terms: Array<{
        id: string;
        attribute_id: string;
        value: string;
        display_order: number;
        created_at: string;
      }>;
    }>(`/stock/attributes/${attributeId}/terms`);
  }

  async createAttributeTerm(attributeId: string, data: {
    value: string;
    displayOrder?: number;
  }) {
    return this.request<{
      term: {
        id: string;
        value: string;
      };
    }>(`/stock/attributes/${attributeId}/terms`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAttributeTerm(termId: string) {
    return this.request<{
      message: string;
    }>(`/stock/attributes/terms/${termId}`, {
      method: 'DELETE',
    });
  }

  async addProductVariant(productId: string, data: {
    label?: string;
    stockUnit?: string;
    alertThreshold?: number;
    destockUnitaire?: boolean;
    unitesParBoite?: number;
    barcodeUnitaire?: string;
    storageLocationId?: string | null;
    barcode?: string;
    manufacturerRef?: string;
    unitsPerPackage?: number;
    suppliers?: Array<{
      supplierId?: string;
      supplierRef?: string;
      catalogPrice?: number;
    }>;
  }) {
    return this.request<{ variant: { id: string } }>(`/stock/products/${productId}/variants`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVariant(variantId: string, data: {
    label?: string;
    stockUnit?: string;
    alertThreshold?: number;
    optimalStock?: number | null;
    destockUnitaire?: boolean;
    unitesParBoite?: number;
    barcodeUnitaire?: string;
    storageLocationId?: string | null;
    isActive?: boolean;
    barcode?: string;
    manufacturerRef?: string;
  }) {
    return this.request<{ variant: { id: string } }>(`/stock/variants/${variantId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async replaceVariantSuppliers(variantId: string, suppliers: Array<{
    supplierId: string;
    supplierRef?: string;
    catalogPrice?: number;
  }>) {
    return this.request<{ supplierIds: string[] }>(`/stock/variants/${variantId}/suppliers`, {
      method: 'PUT',
      body: JSON.stringify({ suppliers }),
    });
  }

  async addVariantSupplier(variantId: string, data: {
    supplierId: string;
    supplierRef?: string;
    catalogPrice?: number;
  }) {
    return this.request<{ supplier: { id: string } }>(`/stock/variants/${variantId}/suppliers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVariant(variantId: string) {
    return this.request<{
      variant: {
        id: string;
        product_id: string;
        label: string;
        stock_unit: string;
        alert_threshold: number;
        is_active: boolean;
        current_stock: number;
        status: 'ok' | 'low' | 'out';
        barcode: string | null;
        manufacturer_ref: string | null;
        units_per_package: number;
        catalog_price: number | null;
        attributes: string[];
        product: {
          id: string;
          name: string;
          manufacturer: string | null;
          is_unique: boolean;
          category_id: string | null;
        };
        suppliers: Array<{
          id: string;
          variant_id: string;
          supplier_id: string;
          supplier_ref: string | null;
          catalog_price: number | null;
          is_active: boolean;
          is_principal: boolean;
          supplier_name: string | null;
        }>;
        lots: Array<{
          id: string;
          variant_id: string;
          lot_number: string | null;
          expiry_date: string | null;
          quantity_received: number;
          quantity_remaining: number;
          purchase_price: number | null;
          unit_price: number | null;
          reception_date: string;
          status: string;
        }>;
      };
    }>(`/stock/variants/${variantId}`);
  }

  async getProductVariants(productId: string) {
    return this.request<{
      variants: Array<{
        id: string;
        name: string;
        reference: string;
        barcode: string | null;
        current_stock: number;
        min_stock: number;
        max_stock: number | null;
        unit_price: number;
        tva_rate: number;
        location: string | null;
        attributes: Array<{
          attribute_id: string;
          attribute_name: string;
          attribute_type: string;
          term_id: string;
          term_value: string;
        }>;
      }>;
    }>(`/stock/products/${productId}/variants`);
  }

  async createProductVariant(productId: string, data: {
    attributeValues: Array<{ attributeId: string; termId: string }>;
    name?: string;
    reference?: string;
    barcode?: string;
    currentStock?: number;
    minStock?: number;
    maxStock?: number;
    unitPrice?: number;
    location?: string;
  }) {
    return this.request<{
      variantId: string;
    }>(`/stock/products/${productId}/variants`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteVariant(variantId: string) {
    return this.request<{
      message: string;
    }>(`/stock/variants/${variantId}`, {
      method: 'DELETE',
    });
  }

  async restoreVariant(variantId: string) {
    return this.request(`/stock/variants/${variantId}/restore`, {
      method: 'PUT',
    });
  }

  async getProductStockSummary(productId: string) {
    return this.request<{
      summary: {
        variant_count: number;
        total_stock: number;
        min_variant_stock: number;
        max_variant_stock: number;
        low_stock_count: number;
      };
    }>(`/stock/products/${productId}/stock-summary`);
  }

  async updateVariantAttributes(variantId: string, data: {
    attributeValues: Array<{ attributeId: string; termId: string }>;
  }) {
    return this.request<{
      message: string;
    }>(`/stock/variants/${variantId}/attributes`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getVariantSuppliers(variantId: string) {
    return this.request<{
      suppliers: Array<{
        variantSupplierId: string;
        supplierId: string;
        supplierName: string;
        supplierRef: string | null;
        catalogPrice: number | null;
        isPrincipal: boolean;
        lastPrice: number | null;
        avg6months: number | null;
        franco: number | null;
        email: string | null;
        phone: string | null;
      }>;
    }>(`/stock/variants/${variantId}/suppliers`);
  }

  async setPrincipalSupplier(variantId: string, supplierId: string) {
    return this.request<{ message: string }>(
      `/stock/variants/${variantId}/suppliers/${supplierId}/principal`,
      {
        method: 'PATCH',
      }
    );
  }

  async getSuggestedPrice(variantId: string) {
    return this.request<{ price: number | null; source: 'last_paid' | 'catalog' | null }>(
      `/stock/variants/${variantId}/suggested-price`
    );
  }

  // ==================== IMPLANTS MODULE ====================

  // Patients
  async getImplantsPatients(params?: { search?: string; limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return this.request<{
      items: Array<{
        id: string;
        firstName: string;
        lastName: string;
        birthDate: string | null;
        patientNumber: string | null;
        phone: string | null;
        email: string | null;
      }>;
      total: number;
    }>(`/redactia/patients${query ? `?${query}` : ''}`);
  }

  async createImplantsPatient(data: {
    firstName: string;
    lastName: string;
    birthDate?: string;
    patientNumber?: string;
    phone?: string;
    email?: string;
    address?: string;
    socialSecurityNumber?: string;
    notes?: string;
  }) {
    return this.request<{
      id: string;
      firstName: string;
      lastName: string;
    }>('/redactia/patients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Preop Checklists
  async getPreopChecklists(params?: {
    patientId?: string;
    status?: 'draft' | 'validated' | 'completed';
    fromDate?: string;
    toDate?: string;
    limit?: number;
    offset?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.patientId) searchParams.set('patientId', params.patientId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.fromDate) searchParams.set('fromDate', params.fromDate);
    if (params?.toDate) searchParams.set('toDate', params.toDate);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return this.request<{
      items: Array<{
        id: string;
        patientId: string;
        patientName: string;
        patientNumber: string | null;
        plannedDate: string;
        status: 'draft' | 'validated' | 'completed';
        preparedByName: string | null;
        validatedByName: string | null;
        createdAt: string;
      }>;
      total: number;
    }>(`/redactia/checklists${query ? `?${query}` : ''}`);
  }

  async getPreopChecklist(id: string) {
    return this.request<{
      id: string;
      patientId: string;
      patientName: string;
      patientBirthDate: string | null;
      patientNumber: string | null;
      plannedDate: string;
      // Administrative
      devisSigned: boolean;
      consentSigned: boolean;
      prescriptionDone: boolean;
      prepostAdviceGiven: boolean;
      // Analysis
      medicalQuestionnaireReviewed: boolean;
      cbctAnalysisDone: boolean;
      patientInformed: boolean;
      // Planned implants
      plannedImplants: Array<{
        position: string;
        type: string;
        diameter: string;
        length: string;
      }>;
      // Biomaterials
      boneSubstitute: string | null;
      boneSubstituteQty: number | null;
      membrane: string | null;
      membraneQty: number | null;
      // Equipment
      surgicalCassette: string | null;
      implantKit: string | null;
      sinusLiftCassette: boolean;
      piezoKit: string | null;
      versahCassette: boolean;
      pinsKit: boolean;
      prfCassette: boolean;
      boneScraper: boolean;
      // Consumables
      anestheticType: string | null;
      anestheticQty: number | null;
      bladeNumber: string | null;
      bladeQty: number | null;
      sutureType: string | null;
      sutureQty: number | null;
      salineType: string | null;
      salineQty: number | null;
      // Misc
      surgicalGuide: boolean;
      extractionPlanned: boolean;
      extractionTeeth: string | null;
      provisionalCrown: boolean;
      provisionalProsthesis: boolean;
      // Meta
      status: 'draft' | 'validated' | 'completed';
      preparedBy: string | null;
      preparedByName: string | null;
      validatedBy: string | null;
      validatedByName: string | null;
      validatedAt: string | null;
      pdfPath: string | null;
      createdAt: string;
      updatedAt: string;
    }>(`/redactia/checklists/${id}`);
  }

  async createPreopChecklist(data: {
    patientId: string;
    plannedDate: string;
    devisSigned?: boolean;
    consentSigned?: boolean;
    prescriptionDone?: boolean;
    prepostAdviceGiven?: boolean;
    medicalQuestionnaireReviewed?: boolean;
    cbctAnalysisDone?: boolean;
    patientInformed?: boolean;
    plannedImplants?: Array<{ position: string; type: string; diameter: string; length: string }>;
    boneSubstitute?: string;
    boneSubstituteQty?: number;
    membrane?: string;
    membraneQty?: number;
    surgicalCassette?: string;
    implantKit?: string;
    sinusLiftCassette?: boolean;
    piezoKit?: string;
    versahCassette?: boolean;
    pinsKit?: boolean;
    prfCassette?: boolean;
    boneScraper?: boolean;
    anestheticType?: string;
    anestheticQty?: number;
    bladeNumber?: string;
    bladeQty?: number;
    sutureType?: string;
    sutureQty?: number;
    salineType?: string;
    salineQty?: number;
    surgicalGuide?: boolean;
    extractionPlanned?: boolean;
    extractionTeeth?: string;
    provisionalCrown?: boolean;
    provisionalProsthesis?: boolean;
  }) {
    return this.request<{ id: string }>('/redactia/checklists', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePreopChecklist(id: string, data: Partial<{
    patientId: string;
    plannedDate: string;
    devisSigned: boolean;
    consentSigned: boolean;
    prescriptionDone: boolean;
    prepostAdviceGiven: boolean;
    medicalQuestionnaireReviewed: boolean;
    cbctAnalysisDone: boolean;
    patientInformed: boolean;
    plannedImplants: Array<{ position: string; type: string; diameter: string; length: string }>;
    boneSubstitute: string;
    boneSubstituteQty: number;
    membrane: string;
    membraneQty: number;
    surgicalCassette: string;
    implantKit: string;
    sinusLiftCassette: boolean;
    piezoKit: string;
    versahCassette: boolean;
    pinsKit: boolean;
    prfCassette: boolean;
    boneScraper: boolean;
    anestheticType: string;
    anestheticQty: number;
    bladeNumber: string;
    bladeQty: number;
    sutureType: string;
    sutureQty: number;
    salineType: string;
    salineQty: number;
    surgicalGuide: boolean;
    extractionPlanned: boolean;
    extractionTeeth: string;
    provisionalCrown: boolean;
    provisionalProsthesis: boolean;
  }>) {
    return this.request<{ id: string }>(`/redactia/checklists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePreopChecklist(id: string) {
    return this.request(`/redactia/checklists/${id}`, { method: 'DELETE' });
  }

  async validatePreopChecklist(id: string) {
    return this.request<{ id: string; status: string }>(`/redactia/checklists/${id}/validate`, {
      method: 'POST',
    });
  }

  async completePreopChecklist(id: string) {
    return this.request<{ id: string; status: string }>(`/redactia/checklists/${id}/complete`, {
      method: 'POST',
    });
  }

  getPreopChecklistPdfUrl(id: string) {
    const token = this.getAccessToken();
    return `${API_URL}/redactia/checklists/${id}/pdf?token=${token}`;
  }

  // ─── RedactIA ─────────────────────────────────────────────

  async getRedactiaDocumentTypes() {
    return this.request<Array<{
      key: string;
      label: string;
      icon: string;
      schema: Record<string, unknown>;
      sortOrder: number;
    }>>('/redactia/document-types');
  }

  // --- Document Templates ---

  async getRedactiaDocumentTemplates(typeKey?: string) {
    const params = typeKey ? `?typeKey=${typeKey}` : '';
    return this.request<Array<{
      id: string;
      documentTypeKey: string;
      documentTypeLabel?: string;
      name: string;
      isDefault: boolean;
      fieldDefaults: Record<string, Record<string, unknown>>;
      createdAt: string;
      updatedAt: string;
    }>>(`/redactia/document-templates${params}`);
  }

  async getRedactiaDocumentTemplate(id: string) {
    return this.request<{
      id: string;
      documentTypeKey: string;
      documentTypeLabel?: string;
      name: string;
      isDefault: boolean;
      fieldDefaults: Record<string, Record<string, unknown>>;
      createdAt: string;
      updatedAt: string;
    }>(`/redactia/document-templates/${id}`);
  }

  async createRedactiaDocumentTemplate(data: { name: string; documentTypeKey: string; isDefault?: boolean; fieldDefaults?: Record<string, unknown> }) {
    return this.request<{ id: string }>('/redactia/document-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRedactiaDocumentTemplate(id: string, data: { name?: string; isDefault?: boolean; fieldDefaults?: Record<string, unknown> }) {
    return this.request(`/redactia/document-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRedactiaDocumentTemplate(id: string) {
    return this.request(`/redactia/document-templates/${id}`, { method: 'DELETE' });
  }

  async setRedactiaDocumentTemplateDefault(id: string) {
    return this.request(`/redactia/document-templates/${id}/default`, { method: 'PUT' });
  }

  async duplicateRedactiaDocumentTemplate(id: string) {
    return this.request<{ id: string }>(`/redactia/document-templates/${id}/duplicate`, { method: 'POST' });
  }

  async getRedactiaDocumentTemplateDefault(typeKey: string) {
    return this.request<{
      id: string;
      documentTypeKey: string;
      name: string;
      isDefault: boolean;
      fieldDefaults: Record<string, Record<string, unknown>>;
    } | null>(`/redactia/document-templates/default/${typeKey}`);
  }

  async getRedactiaLatestChecklist(patientId: string) {
    return this.request<unknown>(`/redactia/checklists/latest?patientId=${patientId}`);
  }

  async getRedactiaDocuments(filters?: {
    patientId?: string;
    typeKey?: string;
    status?: string;
    fromDate?: string;
    toDate?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.patientId) params.set('patientId', filters.patientId);
    if (filters?.typeKey) params.set('typeKey', filters.typeKey);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.fromDate) params.set('fromDate', filters.fromDate);
    if (filters?.toDate) params.set('toDate', filters.toDate);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));
    const query = params.toString();
    return this.request<{ items: RiDocument[]; total: number }>(
      `/redactia/documents${query ? `?${query}` : ''}`
    );
  }

  async getRedactiaDocument(id: string) {
    return this.request<RiDocument>(`/redactia/documents/${id}`);
  }

  async createRedactiaDocument(data: {
    patientId: string;
    documentTypeKey: string;
    templateId?: string;
    content?: Record<string, unknown>;
  }) {
    return this.request<RiDocument>('/redactia/documents', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async updateRedactiaDocument(id: string, data: {
    content?: Record<string, unknown>;
    templateId?: string;
  }) {
    return this.request<RiDocument>(`/redactia/documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async generateRedactiaCompteRendu(documentTypeKey: string, elements: Array<{ sectionKey: string; sectionLabel: string; values: Record<string, unknown> }>, documentTypeLabel?: string) {
    return this.request<{ text: string }>('/redactia/documents/generate-text', {
      method: 'POST',
      body: JSON.stringify({ documentTypeKey, documentTypeLabel, elements }),
    });
  }

  async finalizeRedactiaDocument(id: string) {
    return this.request<RiDocument>(`/redactia/documents/${id}/finalize`, {
      method: 'POST',
    });
  }

  async deleteRedactiaDocument(id: string) {
    return this.request(`/redactia/documents/${id}`, { method: 'DELETE' });
  }

  async getRedactiaDocumentVersions(id: string) {
    return this.request<RiDocumentVersion[]>(`/redactia/documents/${id}/versions`);
  }

  async saveRedactiaDocumentVersion(id: string) {
    return this.request(`/redactia/documents/${id}/versions/save`, { method: 'POST' });
  }

  async restoreRedactiaDocumentVersion(documentId: string, versionId: string) {
    return this.request<RiDocument>(
      `/redactia/documents/${documentId}/versions/restore/${versionId}`,
      { method: 'POST' }
    );
  }

  async getRedactiaDashboardStats() {
    return this.request<{
      docsThisWeek: number;
      docsThisMonth: number;
      draftCount: number;
      checklistsToday: number;
      docsByType: Array<{ key: string; label: string; count: number }>;
    }>('/redactia/dashboard/stats');
  }

  async getRedactiaRecentDocuments(limit = 8) {
    return this.request<{ items: RiDocument[] }>(
      `/redactia/dashboard/recent-documents?limit=${limit}`
    );
  }

  async getRedactiaUpcomingChecklists(days = 7) {
    return this.request<{ items: Array<{
      id: string;
      patient_name: string;
      planned_date: string;
      status: string;
      template_name?: string;
    }> }>(`/redactia/dashboard/upcoming-checklists?days=${days}`);
  }

  async getRedactiaStockAlerts(days = 7) {
    return this.request<{ alerts: Array<{
      produitId: string;
      produitNom: string;
      needed: number;
      available: number;
      deficit: number;
      checklists: Array<{ id: string; patientName: string; plannedDate: string }>;
    }> }>(`/redactia/stock-alerts?days=${days}`);
  }

  async searchRedactiaPatients(q: string, limit = 20) {
    return this.request<Array<{
      id: string;
      firstName: string;
      lastName: string;
      birthDate: string | null;
      patientNumber: string | null;
      fullName: string;
    }>>(`/redactia/patients/search?q=${encodeURIComponent(q)}&limit=${limit}`);
  }

  async previewRedactiaDocumentStock(id: string) {
    return this.request<{
      items: Array<{
        variantId: string;
        qty: number;
        productName?: string;
        preview: {
          variantLabel: string;
          productName: string;
          stockUnit: string;
          requested: number;
          available: number;
          sufficient: boolean;
          stockAfter: number;
          willTriggerAlert: boolean;
        } | null;
      }>;
    }>(`/redactia/documents/${id}/consume-stock/preview`);
  }

  async consumeRedactiaDocumentStock(id: string) {
    return this.request<{
      consumed: Array<{ variantId: string; productName?: string; consumed: number; error?: string }>;
    }>(`/redactia/documents/${id}/consume-stock`, { method: 'POST' });
  }

  async getRedactiaImplantStats(period?: string) {
    return this.request<{
      totalImplants: number;
      byMonth: Array<{ month: string; count: number }>;
      byBrand: Array<{ brand: string; count: number }>;
      byPosition: Array<{ position: string; count: number }>;
      avgTorque: number | null;
    }>(`/redactia/stats/implants${period ? `?period=${period}` : ''}`);
  }

  async dictateRedactia(
    audioBlob: Blob,
    documentTypeKey: string
  ): Promise<{ transcription: string; fields: Record<string, unknown> }> {
    const formData = new FormData();
    const ext = audioBlob.type.includes('webm') ? 'webm' : audioBlob.type.includes('mp4') ? 'mp4' : 'wav';
    formData.append('audio', audioBlob, `dictation.${ext}`);
    formData.append('documentTypeKey', documentTypeKey);

    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${API_URL}/redactia/dictate`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Erreur de dictée');
    }
    return data.data;
  }

  async dictateRedactiaField(
    audioBlob: Blob,
    fieldKey: string,
    fieldLabel: string,
    fieldType: string,
    options?: string[]
  ): Promise<{ transcription: string; value: unknown }> {
    const formData = new FormData();
    const ext = audioBlob.type.includes('webm') ? 'webm' : 'wav';
    formData.append('audio', audioBlob, `field.${ext}`);
    formData.append('fieldKey', fieldKey);
    formData.append('fieldLabel', fieldLabel);
    formData.append('fieldType', fieldType);
    if (options && options.length > 0) formData.append('options', JSON.stringify(options));

    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${API_URL}/redactia/dictate/field`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'Erreur de dictée');
    return data.data;
  }

  // ─── RedactIA PDF Export ─────────────────────────────────────

  async downloadDocumentPdf(documentId: string): Promise<void> {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${API_URL}/redactia/documents/${documentId}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Erreur génération PDF');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    a.download = match ? decodeURIComponent(match[1]) : 'document.pdf';
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── RedactIA Dictation Stream ──────────────────────────────

  async createDictationSession(documentTypeKey: string): Promise<{ sessionId: string }> {
    const data = await this.request<{ sessionId: string }>('/redactia/dictation/sessions', {
      method: 'POST',
      body: JSON.stringify({ documentTypeKey }),
    });
    return data as unknown as { sessionId: string };
  }

  async uploadDictationChunk(sessionId: string, audioBlob: Blob): Promise<{ partialTranscript: string }> {
    const formData = new FormData();
    const ext = audioBlob.type.includes('webm') ? 'webm' : audioBlob.type.includes('mp4') ? 'mp4' : 'wav';
    formData.append('audio', audioBlob, `chunk.${ext}`);

    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${API_URL}/redactia/dictation/sessions/${sessionId}/chunk`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'Erreur chunk');
    return data.data;
  }

  async pauseDictationSession(sessionId: string): Promise<void> {
    await this.request(`/redactia/dictation/sessions/${sessionId}/pause`, { method: 'POST' });
  }

  async resumeDictationSession(sessionId: string): Promise<void> {
    await this.request(`/redactia/dictation/sessions/${sessionId}/resume`, { method: 'POST' });
  }

  async finalizeDictationSession(sessionId: string): Promise<{ transcription: string; fields: Record<string, unknown> }> {
    const data = await this.request<{ transcription: string; fields: Record<string, unknown> }>(
      `/redactia/dictation/sessions/${sessionId}/finalize`,
      { method: 'POST' }
    );
    return data as unknown as { transcription: string; fields: Record<string, unknown> };
  }

  async deleteDictationSession(sessionId: string): Promise<void> {
    await this.request(`/redactia/dictation/sessions/${sessionId}`, { method: 'DELETE' });
  }

  getDictationStreamUrl(sessionId: string): string {
    return `${API_URL}/redactia/dictation/sessions/${sessionId}/stream`;
  }

  // ─── RedactIA Snippets ──────────────────────────────────────

  async getRedactiaSnippets(typeKey?: string): Promise<Record<string, string[]>> {
    const params = typeKey ? `?typeKey=${encodeURIComponent(typeKey)}` : '';
    const result = await this.request<Record<string, string[]>>(`/redactia/snippets${params}`);
    return result.data ?? {};
  }

  async getAllRedactiaSnippets(typeKey?: string): Promise<Array<{
    id: string;
    fieldKey: string;
    fieldLabel: string | null;
    documentTypeKey: string | null;
    snippets: string[];
    isAiGenerated: boolean;
  }>> {
    const params = typeKey ? `?typeKey=${encodeURIComponent(typeKey)}` : '';
    const result = await this.request<Array<{
      id: string;
      fieldKey: string;
      fieldLabel: string | null;
      documentTypeKey: string | null;
      snippets: string[];
      isAiGenerated: boolean;
    }>>(`/redactia/snippets/all${params}`);
    return result.data ?? [];
  }

  async getRedactiaSnippetOnboardingStatus(): Promise<{
    status: 'pending' | 'completed' | 'skipped';
    questions: Array<{ id: string; question: string; placeholder: string }>;
  }> {
    const result = await this.request<{
      status: 'pending' | 'completed' | 'skipped';
      questions: Array<{ id: string; question: string; placeholder: string }>;
    }>('/redactia/snippets/onboarding-status');
    return result.data!;
  }

  async skipRedactiaSnippetOnboarding(): Promise<void> {
    await this.request('/redactia/snippets/onboarding-skip', { method: 'POST' });
  }

  async generateRedactiaSnippets(
    responses: Array<{ questionId: string; answer: string }>
  ): Promise<Array<{ id: string; fieldKey: string; fieldLabel: string | null; snippets: string[] }>> {
    const result = await this.request<Array<{ id: string; fieldKey: string; fieldLabel: string | null; snippets: string[] }>>(
      '/redactia/snippets/generate',
      { method: 'POST', body: JSON.stringify({ responses }) }
    );
    return result.data ?? [];
  }

  async addRedactiaSnippet(data: {
    fieldKey: string;
    fieldLabel?: string;
    documentTypeKey?: string;
    sectionKey?: string;
    text: string;
  }): Promise<{ id: string; fieldKey: string; fieldLabel: string | null; snippets: string[]; isAiGenerated: boolean; documentTypeKey: string | null }> {
    const result = await this.request<{ id: string; fieldKey: string; fieldLabel: string | null; snippets: string[]; isAiGenerated: boolean; documentTypeKey: string | null }>(
      '/redactia/snippets',
      { method: 'POST', body: JSON.stringify(data) }
    );
    return result.data!;
  }

  async updateRedactiaSnippet(id: string, snippets: string[]): Promise<void> {
    await this.request(`/redactia/snippets/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ snippets }),
    });
  }

  async deleteRedactiaSnippet(id: string): Promise<void> {
    await this.request(`/redactia/snippets/${id}`, { method: 'DELETE' });
  }

  // ─── RedactIA Checklist Templates ──────────────────────────

  async getGlobalChecklistTemplates() {
    return this.request<RiChecklistTemplate[]>('/redactia/templates/global');
  }

  async cloneGlobalChecklistTemplate(globalId: string) {
    return this.request<RiChecklistTemplate>(`/redactia/templates/clone/${globalId}`, { method: 'POST' });
  }

  async getRedactiaTemplates() {
    return this.request<RiChecklistTemplate[]>('/redactia/templates');
  }

  async getRedactiaTemplate(id: string) {
    return this.request<RiChecklistTemplate>(`/redactia/templates/${id}`);
  }

  async createRedactiaTemplate(data: { name: string; description?: string; isDefault?: boolean }) {
    return this.request<RiChecklistTemplate>('/redactia/templates', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async updateRedactiaTemplate(id: string, data: { name?: string; description?: string; isDefault?: boolean }) {
    return this.request<RiChecklistTemplate>(`/redactia/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async deleteRedactiaTemplate(id: string) {
    return this.request<void>(`/redactia/templates/${id}`, { method: 'DELETE' });
  }

  async addRedactiaTemplateCategory(templateId: string, data: { name: string; color?: string }) {
    return this.request<RiChecklistCategory>(`/redactia/templates/${templateId}/categories`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async updateRedactiaTemplateCategory(templateId: string, categoryId: string, data: { name?: string; color?: string }) {
    return this.request<RiChecklistCategory>(`/redactia/templates/${templateId}/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async deleteRedactiaTemplateCategory(templateId: string, categoryId: string) {
    return this.request<void>(`/redactia/templates/${templateId}/categories/${categoryId}`, { method: 'DELETE' });
  }

  async reorderRedactiaTemplateCategories(templateId: string, orderedIds: string[]) {
    return this.request<void>(`/redactia/templates/${templateId}/categories/reorder`, {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async addRedactiaTemplateItem(templateId: string, data: {
    categoryId: string; name: string; itemType: 'checkbox' | 'quantity' | 'select';
    productId?: string; defaultQty?: number; unit?: string; selectOptions?: string[]; isRequired?: boolean;
  }) {
    return this.request<RiChecklistItem>(`/redactia/templates/${templateId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async updateRedactiaTemplateItem(templateId: string, itemId: string, data: {
    name?: string; itemType?: 'checkbox' | 'quantity' | 'select';
    productId?: string | null; defaultQty?: number | null; unit?: string;
    selectOptions?: string[]; isRequired?: boolean;
  }) {
    return this.request<RiChecklistItem>(`/redactia/templates/${templateId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async deleteRedactiaTemplateItem(templateId: string, itemId: string) {
    return this.request<void>(`/redactia/templates/${templateId}/items/${itemId}`, { method: 'DELETE' });
  }

  async reorderRedactiaTemplateItems(templateId: string, categoryId: string, orderedIds: string[]) {
    return this.request<void>(`/redactia/templates/${templateId}/categories/${categoryId}/items/reorder`, {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // ─── RedactIA Preop Checklists ─────────────────────────────

  async getRedactiaChecklists(filters?: {
    patientId?: string; status?: string; fromDate?: string; toDate?: string;
    limit?: number; offset?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.patientId) params.set('patientId', filters.patientId);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.fromDate) params.set('fromDate', filters.fromDate);
    if (filters?.toDate) params.set('toDate', filters.toDate);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));
    const query = params.toString();
    return this.request<{ items: RiPreopChecklist[]; total: number }>(
      `/redactia/checklists${query ? `?${query}` : ''}`
    );
  }

  async getRedactiaChecklist(id: string) {
    return this.request<RiPreopChecklist>(`/redactia/checklists/${id}`);
  }

  async createRedactiaChecklist(data: { patientId: string; templateId?: string; plannedDate: string; notes?: string }) {
    return this.request<RiPreopChecklist>('/redactia/checklists', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async updateRedactiaChecklist(id: string, data: {
    plannedDate?: string; notes?: string; plannedImplants?: unknown[];
    items?: Array<{ templateItemId: string; value: Record<string, unknown> }>;
  }) {
    return this.request<RiPreopChecklist>(`/redactia/checklists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async validateRedactiaChecklist(id: string) {
    return this.request<RiPreopChecklist>(`/redactia/checklists/${id}/validate`, { method: 'POST' });
  }

  async completeRedactiaChecklist(id: string) {
    return this.request<RiPreopChecklist>(`/redactia/checklists/${id}/complete`, { method: 'POST' });
  }

  async deleteRedactiaChecklist(id: string) {
    return this.request<void>(`/redactia/checklists/${id}`, { method: 'DELETE' });
  }

  async getRedactiaStockAvailability(productIds: string[]) {
    return this.request<Array<{
      productId: string; productName: string;
      available: number; reserved: number; net: number;
    }>>(`/redactia/stock-availability?productIds=${productIds.join(',')}`);
  }

  // ─── Product Images ────────────────────────────────────────

  async uploadProductImage(file: File): Promise<ApiResponse<{ imageUrl: string; thumbnailUrl: string }>> {
    const formData = new FormData();
    formData.append('image', file);

    return this.request('/stock/products/upload-image', {
      method: 'POST',
      body: formData,
    }, 60_000);
  }

  async uploadProductImageFromUrl(imageUrl: string): Promise<ApiResponse<{ imageUrl: string; thumbnailUrl: string }>> {
    return this.request('/stock/products/upload-image-from-url', {
      method: 'POST',
      body: JSON.stringify({ imageUrl }),
      headers: {
        'Content-Type': 'application/json',
      },
    }, 60_000);
  }

  // ─── Invoice OCR ─────────────────────────────────────────────

  async parseInvoice(file: File): Promise<ApiResponse<{
    invoice: {
      id: string;
      lines: Array<{
        id: string;
        supplierRef: string | null;
        productName: string;
        quantity: number;
        unitPrice: number | null;
        totalPrice: number | null;
        matchedProductId: string | null;
        matchedProductName: string | null;
        matchedVariantLabel: string | null;
        matchedVariantId: string | null;
        isReceived: boolean;
        receivedLotId: string | null;
        isIgnored: boolean;
      }>;
      supplierName: string | null;
      invoiceNumber: string | null;
      invoiceDate: string | null;
      totalHT: number | null;
      totalTTC: number | null;
      parseMethod: 'pdf-text' | 'ocr' | 'claude-text' | 'claude-vision';
      cached: boolean;
    };
  }>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request('/stock/invoice/parse', {
      method: 'POST',
      body: formData,
    }, 120_000); // OCR + AI parsing can take up to 2 minutes
  }

  async getRecentInvoices(limit = 10) {
    return this.request<{
      invoices: Array<{
        id: string;
        supplierName: string | null;
        invoiceNumber: string | null;
        invoiceDate: string | null;
        totalHT: number | null;
        totalTTC: number | null;
        lineCount: number;
        matchedCount: number;
        receivedCount: number;
        ignoredCount: number;
        createdAt: string;
      }>;
    }>(`/stock/invoice/recent?limit=${limit}`);
  }

  async getInvoice(invoiceId: string) {
    return this.request<{
      invoice: {
        id: string;
        supplierName: string | null;
        invoiceNumber: string | null;
        invoiceDate: string | null;
        totalHT: number | null;
        totalTTC: number | null;
        parseMethod: string;
        lines: Array<{
          id: string;
          supplierRef: string | null;
          productName: string;
          quantity: number;
          unitPrice: number | null;
          totalPrice: number | null;
          matchedProductId: string | null;
          matchedProductName: string | null;
          matchedVariantLabel: string | null;
          matchedVariantId: string | null;
          isReceived: boolean;
          receivedLotId: string | null;
          isIgnored: boolean;
        }>;
      };
    }>(`/stock/invoice/${invoiceId}`);
  }

  async markInvoiceLineReceived(lineId: string, lotId: string) {
    return this.request<{ message: string }>(`/stock/invoice/lines/${lineId}/received`, {
      method: 'PATCH',
      body: JSON.stringify({ lotId }),
    });
  }

  async toggleLineIgnored(lineId: string, ignored: boolean) {
    return this.request<{ message: string }>(`/stock/invoice/lines/${lineId}/ignore`, {
      method: 'PATCH',
      body: JSON.stringify({ ignored }),
    });
  }

  async rematchInvoice(invoiceId: string) {
    return this.request<{ newMatches: number }>(`/stock/invoice/${invoiceId}/rematch`, {
      method: 'POST',
    });
  }

  async updateInvoiceSupplier(invoiceId: string, supplierId: string) {
    return this.request<{ supplierName: string; supplierLogoUrl: string | null }>(
      `/stock/invoice/${invoiceId}/supplier`,
      {
        method: 'PATCH',
        body: JSON.stringify({ supplierId }),
      }
    );
  }

  async deleteInvoice(invoiceId: string) {
    return this.request<{ message: string }>(`/stock/invoice/${invoiceId}`, {
      method: 'DELETE',
    });
  }

  async getInvoices(params?: {
    page?: number;
    limit?: number;
    status?: 'all' | 'non_traitee' | 'partielle' | 'complete';
    supplierId?: string;
    search?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status && params.status !== 'all') searchParams.set('status', params.status);
    if (params?.supplierId) searchParams.set('supplierId', params.supplierId);
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    return this.request<{
      invoices: Array<{
        id: string;
        supplierId: string | null;
        supplierName: string | null;
        supplierLogoUrl: string | null;
        invoiceNumber: string | null;
        invoiceDate: string | null;
        totalHT: number | null;
        totalTTC: number | null;
        lineCount: number;
        matchedCount: number;
        receivedCount: number;
        ignoredCount: number;
        linkedOrdersCount: number;
        createdAt: string;
      }>;
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/stock/invoice/list${query ? `?${query}` : ''}`);
  }

  async getSupplierInvoices(supplierId: string) {
    return this.request<{
      invoices: Array<{
        id: string;
        supplierId: string | null;
        supplierName: string | null;
        supplierLogoUrl: string | null;
        invoiceNumber: string | null;
        invoiceDate: string | null;
        totalHT: number | null;
        totalTTC: number | null;
        lineCount: number;
        matchedCount: number;
        receivedCount: number;
        ignoredCount: number;
        createdAt: string;
      }>;
    }>(`/stock/invoice/supplier/${supplierId}`);
  }

  // ─── Catalog ───────────────────────────────────────────────

  async searchCatalog(query: string, categoryId?: string, limit = 20): Promise<ApiResponse<{
    products: Array<{
      id: string;
      name: string;
      manufacturer: string | null;
      manufacturer_ref: string | null;
      category_name: string | null;
      barcode: string | null;
      usage_count: number;
    }>;
  }>> {
    const params = new URLSearchParams({ q: query, limit: limit.toString() });
    if (categoryId) params.append('categoryId', categoryId);

    return this.request(`/stock/catalog/search?${params.toString()}`);
  }

  async recordCatalogUsage(catalogProductId: string): Promise<ApiResponse<any>> {
    return this.request('/stock/catalog/usage', {
      method: 'POST',
      body: JSON.stringify({ catalogProductId }),
    });
  }

  // ─── Storage Locations ─────────────────────────────────────

  async getStorageLocations() {
    return this.request<{
      locations: Array<{
        id: string;
        account_id: string;
        name: string;
        description: string | null;
        is_default: boolean;
        is_active: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>('/stock/storage-locations');
  }

  async getDefaultStorageLocation() {
    return this.request<{
      location: {
        id: string;
        account_id: string;
        name: string;
        description: string | null;
        is_default: boolean;
        is_active: boolean;
        created_at: string;
        updated_at: string;
      } | null;
    }>('/stock/storage-locations/default');
  }

  async createStorageLocation(data: {
    name: string;
    description?: string;
    isDefault?: boolean;
  }) {
    return this.request<{
      location: {
        id: string;
        account_id: string;
        name: string;
        description: string | null;
        is_default: boolean;
        is_active: boolean;
        created_at: string;
        updated_at: string;
      };
    }>('/stock/storage-locations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStorageLocation(
    id: string,
    data: {
      name?: string;
      description?: string;
      isDefault?: boolean;
    }
  ) {
    return this.request<{
      location: {
        id: string;
        account_id: string;
        name: string;
        description: string | null;
        is_default: boolean;
        is_active: boolean;
        created_at: string;
        updated_at: string;
      };
    }>(`/stock/storage-locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async setDefaultStorageLocation(id: string) {
    return this.request<{
      location: {
        id: string;
        account_id: string;
        name: string;
        description: string | null;
        is_default: boolean;
        is_active: boolean;
        created_at: string;
        updated_at: string;
      };
    }>(`/stock/storage-locations/${id}/set-default`, {
      method: 'PUT',
    });
  }

  async deleteStorageLocation(id: string) {
    return this.request(`/stock/storage-locations/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkAssignStorageLocation(productIds: string[], storageLocationId: string | null) {
    return this.request<{ updatedCount: number }>('/stock/products/bulk-storage-location', {
      method: 'PUT',
      body: JSON.stringify({ productIds, storageLocationId }),
    });
  }

  // ==================== Image Search ====================

  async searchProductImages(params: { query: string; limit?: number }) {
    return this.request<{
      images: Array<{
        url: string;
        thumbnailUrl: string;
        title: string;
        contextLink: string;
        width: number;
        height: number;
      }>;
      provider: 'serpapi' | 'brave';
      quotaUsed: number;
      quotaLimit: number;
      accountUsed: number;
      accountLimit: number;
    }>('/stock/image-search/search', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async getImageSearchQuota() {
    return this.request<{
      used: number;
      limit: number;
      account: { used: number; limit: number };
      providers: {
        serpapi: { used: number; limit: number; configured: boolean };
        brave: { used: number; limit: number; configured: boolean };
      };
      activeProvider: 'serpapi' | 'brave' | null;
    }>('/stock/image-search/quota');
  }

  // ─── Order-Invoice Associations ────────────────────────────────

  async linkInvoiceToOrders(invoiceId: string, orderIds: string[], notes?: string) {
    return this.request(`/stock/invoices/${invoiceId}/link-orders`, {
      method: 'POST',
      body: JSON.stringify({ orderIds, notes }),
    });
  }

  async unlinkInvoiceFromOrder(invoiceId: string, orderId: string) {
    return this.request(`/stock/invoices/${invoiceId}/unlink-order/${orderId}`, {
      method: 'DELETE',
    });
  }

  async getOrdersForInvoice(invoiceId: string) {
    return this.request<{ orders: any[] }>(`/stock/invoices/${invoiceId}/orders`);
  }

  async getSuggestedOrdersForInvoice(invoiceId: string) {
    return this.request<{ suggestions: any[] }>(`/stock/invoices/${invoiceId}/suggested-orders`);
  }

  async getInvoicesForOrder(orderId: string) {
    return this.request<{ invoices: any[] }>(`/stock/invoices/orders/${orderId}/invoices`);
  }

  // ─── Invoice-Order Line Matching ────────────────────────────────

  async getInvoiceOrderAutoMatches(invoiceId: string, orderId: string) {
    return this.request<{
      matches: Array<{
        invoiceLineId: string;
        orderLineId: string;
        productName: string;
        variantLabel: string | null;
        invoiceQty: number;
        invoicePrice: number | null;
        invoiceTotal: number | null;
        orderQty: number;
        orderPrice: number | null;
        orderTotal: number | null;
        matchType: 'auto';
        priceDifference: number | null;
        priceDifferencePercent: number | null;
      }>;
      unmatchedInvoiceLines: Array<{
        id: string;
        productName: string;
        variantLabel: string | null;
        quantity: number;
        unitPrice: number | null;
        totalPrice: number | null;
        isRecognized: boolean;
        matchedVariantId: string | null;
      }>;
      unmatchedOrderLines: Array<{
        id: string;
        productName: string;
        variantLabel: string | null;
        variantId: string;
        quantity: number;
        orderedPrice: number | null;
        totalPrice: number | null;
      }>;
    }>(`/stock/invoices/${invoiceId}/orders/${orderId}/auto-match`);
  }

  async saveInvoiceOrderMatches(
    invoiceId: string,
    orderId: string,
    payload: {
      matches: Array<{
        invoiceLineId: string;
        orderLineId: string;
        quantity: number;
        matchType: 'auto' | 'manual';
      }>;
      bonusLineIds: string[];
    }
  ) {
    return this.request(`/stock/invoices/${invoiceId}/orders/${orderId}/save-matches`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async dissociateInvoiceOrderMatch(invoiceLineId: string, orderLineId: string) {
    return this.request(`/stock/invoice-lines/${invoiceLineId}/order-lines/${orderLineId}/match`, {
      method: 'DELETE',
    });
  }

  async getSuggestedProductsForInvoiceLine(lineId: string, searchTerm: string) {
    return this.request<{
      suggestions: Array<{
        variantId: string;
        productName: string;
        variantLabel: string | null;
        matchScore: number;
      }>;
    }>(`/stock/invoice-lines/${lineId}/suggested-products?searchTerm=${encodeURIComponent(searchTerm)}`);
  }

  // ── Smart Order ─────────────────────────────────────────────

  async getSmartOrderSettings() {
    return this.request<{ settings: SmartOrderSettings }>('/stock/smart-order/settings');
  }

  async updateSmartOrderSettings(data: Partial<SmartOrderSettings>) {
    return this.request<{ settings: SmartOrderSettings }>('/stock/smart-order/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getSmartOrderSuggestions(limit = 10) {
    return this.request<{ suggestions: SmartOrderSuggestion[] }>(`/stock/smart-order/suggestions?limit=${limit}`);
  }

  async getSmartOrderSuggestion(id: string) {
    return this.request<{ suggestion: SmartOrderSuggestion }>(`/stock/smart-order/suggestions/${id}`);
  }

  async generateSmartOrder() {
    return this.request<{ suggestion: SmartOrderSuggestion }>('/stock/smart-order/generate', { method: 'POST' });
  }

  async updateSmartOrderLine(
    suggestionId: string,
    lineId: string,
    data: { decision: 'accepted' | 'modified' | 'rejected'; adjustedQuantity?: number; rejectionReason?: string }
  ) {
    return this.request<{ line: SmartOrderLine }>(`/stock/smart-order/suggestions/${suggestionId}/lines/${lineId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async validateSmartOrder(suggestionId: string) {
    return this.request<{ ordersCreated: number }>(`/stock/smart-order/suggestions/${suggestionId}/validate`, {
      method: 'POST',
    });
  }

  async dismissSmartOrder(suggestionId: string) {
    return this.request<{ success: boolean }>(`/stock/smart-order/suggestions/${suggestionId}/dismiss`, {
      method: 'POST',
    });
  }

  async getSmartOrderPreferences() {
    return this.request<{ preferences: SmartOrderPreference[] }>('/stock/smart-order/preferences');
  }

  async deleteSmartOrderPreference(id: string) {
    return this.request<{ success: boolean }>(`/stock/smart-order/preferences/${id}`, { method: 'DELETE' });
  }

  // ── Anomalies ────────────────────────────────────────────────

  async getAnomalies(params?: {
    status?: AnomalyStatus;
    severity?: AnomalySeverity;
    type?: AnomalyType;
    limit?: number;
    offset?: number;
  }) {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.severity) q.set('severity', params.severity);
    if (params?.type) q.set('type', params.type);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString() ? `?${q.toString()}` : '';
    return this.request<{ anomalies: StockAnomaly[]; total: number }>(`/stock/anomalies${qs}`);
  }

  async getAnomalyStats() {
    return this.request<{ stats: AnomalyStats }>('/stock/anomalies/stats');
  }

  async getAnomaly(id: string) {
    return this.request<{ anomaly: StockAnomaly }>(`/stock/anomalies/${id}`);
  }

  async updateAnomalyStatus(id: string, data: {
    status: AnomalyStatus;
    resolutionComment?: string;
    resolutionAction?: string;
  }) {
    return this.request<{ anomaly: StockAnomaly }>(`/stock/anomalies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async triggerAnomalyDetection() {
    return this.request<{ detected: { spike: number; drop: number; expiration: number } }>('/stock/anomalies/detect', {
      method: 'POST',
    });
  }

  async getAnomalyRules() {
    return this.request<{ rules: Array<{ account_id: string; anomaly_type: string; config: Record<string, unknown>; is_active: boolean; false_positive_count: number; total_detections: number }> }>('/stock/anomalies/rules');
  }

  // ── Chat ──────────────────────────────────────────────────────

  async createChatSession() {
    return this.request<{ session: ChatSession; welcome: ChatMessage }>('/stock/chat/sessions', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async listChatSessions() {
    return this.request<{ sessions: ChatSession[] }>('/stock/chat/sessions');
  }

  async getChatHistory(sessionId: string) {
    return this.request<{ messages: ChatMessage[] }>(`/stock/chat/sessions/${sessionId}/messages`);
  }

  async sendChatMessage(sessionId: string, content: string, language?: string) {
    return this.request<{ userMessage: ChatMessage; assistantMessage: ChatMessage }>(
      `/stock/chat/sessions/${sessionId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ content, language }),
      }
    );
  }

  async deleteChatSession(sessionId: string) {
    return this.request<Record<string, never>>(`/stock/chat/sessions/${sessionId}`, { method: 'DELETE' });
  }

  async sendVoiceMessage(sessionId: string, audioBlob: Blob, language?: string) {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    if (language) formData.append('language', language);
    return this.request<{
      transcription: { text: string; language: string; duration_seconds: number };
      userMessage: ChatMessage;
      assistantMessage: ChatMessage;
    }>(`/stock/chat/sessions/${sessionId}/voice`, {
      method: 'POST',
      body: formData,
    });
  }

  // ── Auto-Tag ───────────────────────────────────────────────────

  async getAutoTagStatus() {
    return this.request<AutoTagStatus>('/stock/auto-tag/status');
  }

  async getUntaggedProducts() {
    return this.request<{ products: UntaggedProduct[] }>('/stock/auto-tag/untagged');
  }

  async getGenericTypes() {
    return this.request<{ types: GenericTypeDefinition[] }>('/stock/auto-tag/generic-types');
  }

  async searchByConcept(concept: string) {
    return this.request<ConceptSearchResult>(`/stock/auto-tag/search/${encodeURIComponent(concept)}`);
  }

  async backfillAutoTag(force = false) {
    return this.request<BackfillResult>(`/stock/auto-tag/backfill?force=${force}`, { method: 'POST' });
  }

  async triggerProductTag(productId: string) {
    return this.request<{ tags: ProductTags }>(`/stock/auto-tag/products/${productId}/tag`, { method: 'POST' });
  }

  async updateProductTags(productId: string, corrections: { generic_type?: string; tags?: string[]; brand?: string | null }) {
    return this.request<Record<string, never>>(`/stock/auto-tag/products/${productId}/tags`, {
      method: 'PUT',
      body: JSON.stringify(corrections),
    });
  }

  // ── Cart ───────────────────────────────────────────────────────

  async getCart(supplierId?: string) {
    const qs = supplierId ? `?supplier_id=${supplierId}` : '';
    return this.request<CartViewResult>(`/stock/cart${qs}`);
  }

  async getCartSummary() {
    return this.request<CartSummaryResult>('/stock/cart/summary');
  }

  async addToCart(item: CartItemInput) {
    return this.request<CartAddResult>('/stock/cart/items', {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateCartItem(itemId: string, quantity: number) {
    return this.request<Record<string, unknown>>(`/stock/cart/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    });
  }

  async removeCartItem(itemId: string) {
    return this.request<Record<string, unknown>>(`/stock/cart/items/${itemId}`, { method: 'DELETE' });
  }

  async clearCart(supplierId?: string) {
    const qs = supplierId ? `?supplier_id=${supplierId}` : '';
    return this.request<Record<string, unknown>>(`/stock/cart${qs}`, { method: 'DELETE' });
  }

  async validateCart(body: { supplier_id?: string; supplier_search?: string; validate_all?: boolean }) {
    return this.request<CartValidateResult>('/stock/cart/validate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ── Journal d'activité ─────────────────────────────────────────

  async getJournal(params?: {
    page?: number;
    limit?: number;
    eventType?: string;
    from?: string;
    to?: string;
    userId?: string;
  }) {
    const qs = new URLSearchParams();
    if (params?.page)      qs.set('page',      String(params.page));
    if (params?.limit)     qs.set('limit',     String(params.limit));
    if (params?.eventType) qs.set('eventType', params.eventType);
    if (params?.from)      qs.set('from',      params.from);
    if (params?.to)        qs.set('to',        params.to);
    if (params?.userId)    qs.set('userId',    params.userId);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return this.request<{ entries: JournalEntry[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/stock/journal${query}`);
  }

  async getJournalUsers() {
    return this.request<{ users: { id: string; name: string }[] }>('/stock/journal/users');
  }

  // ==================== CSV IMPORT ====================

  async getImportAccounts() {
    return this.request<{ accounts: Array<{ id: string; name: string; slug: string }> }>('/admin/import/accounts');
  }

  async importSupplierRegistry(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<{ total: number; created: number; updated: number; unchanged: number; errors: string[] }>(
      '/admin/import/supplier-registry', { method: 'POST', body: formData }, 60_000
    );
  }

  async importManufacturerRegistry(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<{ total: number; created: number; updated: number; unchanged: number; errors: string[] }>(
      '/admin/import/manufacturer-registry', { method: 'POST', body: formData }, 60_000
    );
  }

  async importSuppliers(accountId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('accountId', accountId);
    return this.request<{ total: number; linked: number; created: number; updated: number; errors: string[] }>(
      '/admin/import/suppliers', { method: 'POST', body: formData }, 60_000
    );
  }

  async purgeAccountData(accountId: string) {
    return this.request<{
      products: number; variants: number; lots: number; movements: number; suppliers: number;
      orders: number; orderLines: number; anomalies: number; cartItems: number;
    }>(`/admin/import/purge/${accountId}`, { method: 'DELETE' }, 60_000);
  }

  async importProducts(accountId: string, file: File, lotsFile?: File) {
    const formData = new FormData();
    formData.append('file', file);
    if (lotsFile) formData.append('lotsFile', lotsFile);
    formData.append('accountId', accountId);
    return this.request<{
      total: number; productsCreated: number; productsUpdated: number;
      variantsCreated: number; variantsUpdated: number; lotsCreated: number;
      unmappedCategories: string[]; unmappedSuppliers: string[]; errors: string[];
    }>('/admin/import/products', { method: 'POST', body: formData }, 120_000);
  }

  async adminAutoTag(accountId: string, force = false) {
    return this.request<{
      status: string; total: number; processed: number; errors: number;
      by_type: Record<string, number>; duration_seconds: number;
    }>('/admin/import/auto-tag', {
      method: 'POST',
      body: JSON.stringify({ accountId, force }),
    }, 300_000);
  }

  async adminAutoTagStatus(accountId: string) {
    return this.request<AutoTagStatus>(`/admin/import/auto-tag/status/${accountId}`);
  }

  async adminUntaggedProducts(accountId: string) {
    return this.request<{ products: UntaggedProduct[] }>(`/admin/import/auto-tag/untagged/${accountId}`);
  }

  async adminGenericTypes(accountId: string) {
    return this.request<{ types: GenericTypeDefinition[] }>(`/admin/import/auto-tag/generic-types/${accountId}`);
  }

  async adminTagProduct(accountId: string, productId: string) {
    return this.request<{ tags: { generic_type: string; tags: string[]; brand: string | null } }>(`/admin/import/auto-tag/products/${accountId}/${productId}/tag`, { method: 'POST' });
  }

  async exportAccountBackup(accountId: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120_000);
    try {
      const response = await fetch(`${API_URL}/admin/backup/export/${accountId}`, {
        headers,
        signal: controller.signal,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: { message: 'Erreur export' } }));
        throw new Error(err?.error?.message || 'Erreur export backup');
      }
      return response.blob();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async validateAccountBackup(accountId: string, file: File) {
    const formData = new FormData();
    formData.append('backupFile', file);
    return this.request<{
      version: string; accountId: string; exportedAt: string; tables: string[];
      accountMismatch: boolean;
    }>(`/admin/backup/validate/${accountId}`, { method: 'POST', body: formData });
  }

  async importAccountBackup(accountId: string, file: File) {
    const formData = new FormData();
    formData.append('backupFile', file);
    return this.request<{
      tables: Record<string, { inserted: number }>;
      totalRows: number;
      backupInfo: { version: string; accountId: string; exportedAt: string; tables: string[] };
      remapped?: boolean;
    }>(`/admin/backup/import/${accountId}`, { method: 'POST', body: formData }, 120_000);
  }

  // ── Auto Backup ───────────────────────────────────────────────

  async listAutoBackups() {
    return this.request<Array<{
      accountId: string;
      accountName: string;
      files: { filename: string; size: number; createdAt: string }[];
    }>>('/admin/backup/auto/list');
  }

  async downloadAutoBackup(accountId: string, filename: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
    const response = await fetch(`${API_URL}/admin/backup/auto/download/${accountId}/${encodeURIComponent(filename)}`, { headers });
    if (!response.ok) throw new Error('Erreur téléchargement backup automatique');
    return response.blob();
  }

  async runAutoBackupNow() {
    return this.request<{ message: string }>('/admin/backup/auto/run', { method: 'POST' });
  }

  // ── Admin Accounts ────────────────────────────────────────────

  async getAdminAccounts() {
    return this.request<{ accounts: Array<{
      id: string; name: string; slug: string; email: string | null;
      phone: string | null; city: string | null; plan: string;
      enabled_modules: string[]; created_at: string; users_count: number;
    }> }>('/admin/accounts');
  }

  async getAdminAccount(id: string) {
    return this.request<{ account: {
      id: string; name: string; slug: string; email: string | null;
      phone: string | null; address: string | null; city: string | null;
      postal_code: string | null; country: string; siret: string | null;
      tva_number: string | null; plan: string; enabled_modules: string[];
      max_users: number; sync_enabled: boolean; created_at: string; updated_at: string;
    } }>(`/admin/accounts/${id}`);
  }

  async getAdminAccountStats(id: string) {
    return this.request<{ stats: {
      stock: { products: number; variants: number; suppliers: number; lots: number; movements: number; orders: number };
      implants: { patients: number; checklists: number; surgical_reports: number };
    } }>(`/admin/accounts/${id}/stats`);
  }

  async getAdminAccountUsers(id: string) {
    return this.request<{ users: Array<{
      id: string; first_name: string | null; last_name: string | null;
      email: string; role: string; is_active: boolean;
      joined_at: string; last_login_at: string | null; created_at: string;
    }> }>(`/admin/accounts/${id}/users`);
  }

  async updateAdminAccount(id: string, data: { plan?: string; enabled_modules?: string[]; max_users?: number }) {
    return this.request<{ account: { id: string; name: string; slug: string; plan: string; enabled_modules: string[]; max_users: number; updated_at: string } }>(`/admin/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async generateTestData(accountId: string, options: { modules?: string[]; purge_first?: boolean }) {
    return this.request<{
      stock: { products: number; lots: number; movements: number; orders: number; suppliers: number };
      equipe: { employes: number; pointages: number; conges: number; formations: number; entretiens: number; bulletins: number };
      implants: { patients: number; checklists: number; reports: number };
      zilloteam: { conversations: number; messages: number };
      repondeur: { config: number; messages: number; generated: number };
    }>(`/admin/test-data/${accountId}`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async deleteAdminAccount(id: string, confirmName: string) {
    return this.request<{ message: string; backupFile: string; deletedRows: number }>(`/admin/accounts/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmName }),
    });
  }

  // ── API Usage ──────────────────────────────────────────────────

  async getApiUsageSummary(from?: string, to?: string) {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return this.request<Array<{
      account_id: string; account_name: string; service: string;
      calls: number; input_tokens: number; output_tokens: number; cost_usd: string;
    }>>(`/admin/api-usage/summary?${p.toString()}`);
  }

  async getApiUsageAccount(id: string, from?: string, to?: string) {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return this.request<Array<{
      service: string; feature: string; model: string | null;
      calls: number; input_tokens: number; output_tokens: number;
      cost_usd: string; cached_calls: number;
    }>>(`/admin/api-usage/account/${id}?${p.toString()}`);
  }

  async getZilloDocsAiStats(from?: string, to?: string) {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return this.request<{
      byFeature: Array<{
        feature: string; model: string | null;
        calls: number; input_tokens: number; output_tokens: number; cost_usd: string;
      }>;
      byAccount: Array<{
        account_id: string; account_name: string; feature: string;
        calls: number; input_tokens: number; output_tokens: number; cost_usd: string;
      }>;
      dailyTrend: Array<{
        day: string; feature: string; calls: number; cost_usd: string;
      }>;
      recentVision: Array<{
        id: string; account_id: string; account_name: string;
        feature: string; model: string; input_tokens: number; output_tokens: number;
        cost_usd: string; created_at: string;
      }>;
    }>(`/admin/api-usage/zillodocs-stats?${p.toString()}`);
  }

  async getZilloDocsAiDocuments(from?: string, to?: string, method?: string) {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (method) p.set('method', method);
    return this.request<Array<{
      document_id: string; titre: string | null; fichier_nom: string | null;
      type_document: string | null; fichier_mime: string | null;
      nb_pages: number; confiance_ai: number | null;
      account_id: string; account_name: string;
      method: string | null; model: string | null;
      input_tokens: number | null; output_tokens: number | null;
      classified_at: string;
    }>>(`/admin/api-usage/zillodocs-documents?${p.toString()}`);
  }

  // ── Registries ────────────────────────────────────────────────

  async listManufacturerRegistry() {
    return this.request<ManufacturerRegistryEntry[]>('/admin/registries/manufacturers');
  }
  async createManufacturerRegistry(data: { name: string; website?: string; country?: string }) {
    return this.request<ManufacturerRegistryEntry>('/admin/registries/manufacturers', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateManufacturerRegistry(id: string, data: { name: string; website?: string; country?: string }) {
    return this.request<ManufacturerRegistryEntry>(`/admin/registries/manufacturers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async toggleManufacturerRegistry(id: string) {
    return this.request<ManufacturerRegistryEntry>(`/admin/registries/manufacturers/${id}/toggle`, { method: 'PATCH' });
  }
  async deleteManufacturerRegistry(id: string) {
    return this.request<{ id: string }>(`/admin/registries/manufacturers/${id}`, { method: 'DELETE' });
  }
  async mergeManufacturerRegistry(keepId: string, removeId: string) {
    return this.request<{ kept: string; removed: string; productsUpdated: number }>(
      '/admin/registries/manufacturers/merge',
      { method: 'POST', body: JSON.stringify({ keepId, removeId }) }
    );
  }
  async listCabinetManufacturers() {
    return this.request<ManufacturerCabinetEntry[]>('/admin/registries/manufacturers/cabinet');
  }
  async promoteManufacturerToGlobal(cabinetId: string, targetRegistryId?: string) {
    return this.request<{ registryId: string; name: string; productsUpdated: number }>(
      `/admin/registries/manufacturers/promote/${cabinetId}`,
      { method: 'POST', body: JSON.stringify(targetRegistryId ? { targetRegistryId } : {}) }
    );
  }

  async listSupplierRegistry() {
    return this.request<SupplierRegistryEntry[]>('/admin/registries/suppliers');
  }
  async createSupplierRegistry(data: { name: string; website?: string; country?: string; product_search_url?: string }) {
    return this.request<SupplierRegistryEntry>('/admin/registries/suppliers', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateSupplierRegistry(id: string, data: { name: string; website?: string; country?: string; product_search_url?: string }) {
    return this.request<SupplierRegistryEntry>(`/admin/registries/suppliers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async toggleSupplierRegistry(id: string) {
    return this.request<SupplierRegistryEntry>(`/admin/registries/suppliers/${id}/toggle`, { method: 'PATCH' });
  }
  async deleteSupplierRegistry(id: string) {
    return this.request<{ id: string }>(`/admin/registries/suppliers/${id}`, { method: 'DELETE' });
  }
  async makeSupplierPublic(id: string) {
    return this.request<SupplierRegistryEntry>(`/admin/registries/suppliers/${id}/make-public`, { method: 'PATCH' });
  }
  async mergeSupplierRegistry(keepId: string, removeId: string) {
    return this.request<{ kept: string; removed: string }>(
      '/admin/registries/suppliers/merge',
      { method: 'POST', body: JSON.stringify({ keepId, removeId }) }
    );
  }

  async adminListCategories() {
    return this.request<CategoryEntry[]>('/admin/registries/categories');
  }
  async adminCreateCategory(data: { name: string; code: string; color?: string; description?: string; default_stock_unit?: string; default_alert_threshold?: number; display_order?: number }) {
    return this.request<CategoryEntry>('/admin/registries/categories', { method: 'POST', body: JSON.stringify(data) });
  }
  async adminUpdateCategory(id: string, data: { name: string; color?: string; description?: string; default_stock_unit?: string; default_alert_threshold?: number; display_order?: number }) {
    return this.request<CategoryEntry>(`/admin/registries/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async adminToggleCategory(id: string) {
    return this.request<CategoryEntry>(`/admin/registries/categories/${id}/toggle`, { method: 'PATCH' });
  }

  async adminListProductCatalog(filters?: { search?: string; category_id?: string }) {
    const params = new URLSearchParams();
    if (filters?.search) params.set('search', filters.search);
    if (filters?.category_id) params.set('category_id', filters.category_id);
    const qs = params.toString();
    return this.request<CatalogEntry[]>(`/admin/registries/catalog${qs ? `?${qs}` : ''}`);
  }
  async adminCreateProductCatalog(data: { name: string; manufacturer?: string; manufacturer_ref?: string; barcode?: string; category_id?: string; description?: string }) {
    return this.request<CatalogEntry>('/admin/registries/catalog', { method: 'POST', body: JSON.stringify(data) });
  }
  async adminUpdateProductCatalog(id: string, data: { name: string; manufacturer?: string; manufacturer_ref?: string; barcode?: string; category_id?: string; description?: string }) {
    return this.request<CatalogEntry>(`/admin/registries/catalog/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async adminToggleProductCatalog(id: string) {
    return this.request<CatalogEntry>(`/admin/registries/catalog/${id}/toggle`, { method: 'PATCH' });
  }

  async adminListCatalogVariants(catalogId: string) {
    return this.request<CatalogVariantEntry[]>(`/admin/registries/catalog/${catalogId}/variants`);
  }
  async adminCreateCatalogVariant(catalogId: string, data: { label: string; barcode?: string; manufacturer_ref?: string; display_order?: number }) {
    return this.request<CatalogVariantEntry>(`/admin/registries/catalog/${catalogId}/variants`, { method: 'POST', body: JSON.stringify(data) });
  }
  async adminUpdateCatalogVariant(catalogId: string, variantId: string, data: { label: string; barcode?: string; manufacturer_ref?: string; display_order?: number }) {
    return this.request<CatalogVariantEntry>(`/admin/registries/catalog/${catalogId}/variants/${variantId}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async adminDeleteCatalogVariant(catalogId: string, variantId: string) {
    return this.request<void>(`/admin/registries/catalog/${catalogId}/variants/${variantId}`, { method: 'DELETE' });
  }
  async adminToggleCatalogVariant(catalogId: string, variantId: string) {
    return this.request<CatalogVariantEntry>(`/admin/registries/catalog/${catalogId}/variants/${variantId}/toggle`, { method: 'PATCH' });
  }

  async uploadManufacturerLogo(id: string, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    return this.request<ManufacturerRegistryEntry>(`/admin/registries/manufacturers/${id}/upload-logo`, { method: 'POST', body: formData });
  }
  async deleteManufacturerLogo(id: string) {
    return this.request<ManufacturerRegistryEntry>(`/admin/registries/manufacturers/${id}/logo`, { method: 'DELETE' });
  }
  async uploadSupplierLogo(id: string, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    return this.request<SupplierRegistryEntry>(`/admin/registries/suppliers/${id}/upload-logo`, { method: 'POST', body: formData });
  }
  async deleteSupplierLogo(id: string) {
    return this.request<SupplierRegistryEntry>(`/admin/registries/suppliers/${id}/logo`, { method: 'DELETE' });
  }
  async fetchManufacturerLogo(id: string, website?: string) {
    return this.request<ManufacturerRegistryEntry>(`/admin/registries/manufacturers/${id}/fetch-logo`, { method: 'POST', body: JSON.stringify({ website }) });
  }
  async fetchSupplierLogo(id: string, website?: string) {
    return this.request<SupplierRegistryEntry>(`/admin/registries/suppliers/${id}/fetch-logo`, { method: 'POST', body: JSON.stringify({ website }) });
  }
  async uploadCatalogImage(id: string, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    return this.request<CatalogEntry>(`/admin/registries/catalog/${id}/upload-image`, { method: 'POST', body: formData });
  }
  async deleteCatalogImage(id: string) {
    return this.request<CatalogEntry>(`/admin/registries/catalog/${id}/image`, { method: 'DELETE' });
  }

  // ─── Consumers ──────────────────────────────────────────────

  async getConsumers() {
    return this.request<{
      consumers: Array<{
        id: string;
        account_id: string;
        name: string;
        description: string | null;
        type: 'person' | 'service' | 'room';
        color: string;
        qr_token: string;
        display_order: number;
        is_default: boolean;
        is_active: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>('/stock/consumers');
  }

  async createConsumer(data: {
    name: string;
    description?: string;
    type?: 'person' | 'service' | 'room';
    color?: string;
    isDefault?: boolean;
    displayOrder?: number;
  }) {
    return this.request<{
      consumer: {
        id: string; account_id: string; name: string; description: string | null;
        type: 'person' | 'service' | 'room'; color: string; qr_token: string;
        display_order: number; is_default: boolean; is_active: boolean;
        created_at: string; updated_at: string;
      };
    }>('/stock/consumers', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateConsumer(id: string, data: {
    name?: string;
    description?: string;
    type?: 'person' | 'service' | 'room';
    color?: string;
    isDefault?: boolean;
    displayOrder?: number;
  }) {
    return this.request<{
      consumer: {
        id: string; account_id: string; name: string; description: string | null;
        type: 'person' | 'service' | 'room'; color: string; qr_token: string;
        display_order: number; is_default: boolean; is_active: boolean;
        created_at: string; updated_at: string;
      };
    }>(`/stock/consumers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async setDefaultConsumer(id: string) {
    return this.request<{
      consumer: {
        id: string; account_id: string; name: string; description: string | null;
        type: 'person' | 'service' | 'room'; color: string; qr_token: string;
        display_order: number; is_default: boolean; is_active: boolean;
        created_at: string; updated_at: string;
      };
    }>(`/stock/consumers/${id}/default`, { method: 'PATCH' });
  }

  async toggleConsumer(id: string) {
    return this.request<{
      consumer: {
        id: string; account_id: string; name: string; description: string | null;
        type: 'person' | 'service' | 'room'; color: string; qr_token: string;
        display_order: number; is_default: boolean; is_active: boolean;
        created_at: string; updated_at: string;
      };
    }>(`/stock/consumers/${id}/toggle`, { method: 'PATCH' });
  }

  async refreshConsumerQr(id: string) {
    return this.request<{
      consumer: {
        id: string; account_id: string; name: string; description: string | null;
        type: 'person' | 'service' | 'room'; color: string; qr_token: string;
        display_order: number; is_default: boolean; is_active: boolean;
        created_at: string; updated_at: string;
      };
    }>(`/stock/consumers/${id}/refresh-qr`, { method: 'POST' });
  }

  async getConsumerStats(period?: '7d' | '30d' | '90d') {
    const p = period || '30d';
    return this.request<{
      stats: Array<{
        consumer_id: string | null;
        consumer_name: string | null;
        consumer_color: string | null;
        exit_count: number;
        total_value: number;
      }>;
    }>(`/stock/consumers/stats?period=${p}`);
  }

  async getConsumerById(id: string) {
    return this.request<{
      consumer: {
        id: string; account_id: string; name: string; description: string | null;
        type: 'person' | 'service' | 'room'; color: string; qr_token: string;
        display_order: number; is_default: boolean; is_active: boolean;
        created_at: string; updated_at: string;
      };
    }>(`/stock/consumers/${id}`);
  }

  async getConsumerTopProducts(id: string, period?: '7d' | '30d' | '90d' | 'all') {
    const p = period || '30d';
    return this.request<{
      products: Array<{
        variant_id: string; product_id: string; product_name: string;
        variant_label: string | null; stock_unit: string;
        total_qty: number; total_value: number; exit_count: number;
      }>;
    }>(`/stock/consumers/${id}/top-products?period=${p}`);
  }

  async getConsumerEvolution(id: string) {
    return this.request<{
      evolution: Array<{ month: string; exit_count: number; total_qty: number; total_value: number }>;
    }>(`/stock/consumers/${id}/evolution`);
  }

  async getConsumerMovements(id: string, options?: { page?: number; limit?: number; period?: '7d' | '30d' | '90d' | 'all' }) {
    const params = new URLSearchParams();
    if (options?.page) params.set('page', String(options.page));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.period) params.set('period', options.period);
    return this.request<{
      movements: Array<{
        id: string; quantity: number; reason: string | null; created_at: string;
        lot_number: string | null; unit_price: number | null; total_value: number;
        product_id: string; product_name: string;
        variant_id: string; variant_label: string | null; stock_unit: string;
      }>;
      totalValue: number;
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/stock/consumers/${id}/movements?${params.toString()}`);
  }

  // ==================== ZILLOTEAM MODULE ====================

  // Conversations
  async getConversations(archived: boolean = false, partnerIds?: string[], patientIds?: string[]) {
    const params = new URLSearchParams();
    if (archived) params.set('archived', 'true');
    if (partnerIds?.length) params.set('partners', partnerIds.join(','));
    if (patientIds?.length) params.set('patients', patientIds.join(','));
    const qs = params.toString();
    return this.request<{
      conversations: Array<{
        id: string;
        accountId: string;
        patientId: string;
        title?: string;
        description?: string;
        isArchived: boolean;
        lastMessageAt?: string;
        createdAt: string;
        updatedAt: string;
        patient?: { id: string; prefix?: string; firstName: string; lastName: string };
        participants?: Array<{ id: string; prefix?: string; firstName: string; lastName: string; specialty?: string; email: string }>;
        lastMessage?: { id: string; content: string; messageType: string; createdAt: string; sender: { firstName: string; lastName: string } };
        unreadCount?: number;
      }>;
      total: number;
    }>(`/zilloteam/conversations${qs ? '?' + qs : ''}`);
  }

  async getConversationById(id: string) {
    return this.request<{
      id: string;
      accountId: string;
      patientId: string;
      title?: string;
      description?: string;
      isArchived: boolean;
      lastMessageAt?: string;
      createdAt: string;
      updatedAt: string;
    }>(`/zilloteam/conversations/${id}`);
  }

  async createConversation(data: {
    patientId?: string;
    title?: string;
    description?: string;
    participantIds?: string[];
  }) {
    return this.request<{
      id: string;
      accountId: string;
      patientId: string;
      title?: string;
      description?: string;
      isArchived: boolean;
      lastMessageAt?: string;
      createdAt: string;
      updatedAt: string;
    }>('/zilloteam/conversations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateConversation(id: string, data: {
    title?: string;
    description?: string;
    isArchived?: boolean;
  }) {
    return this.request<{
      id: string;
      accountId: string;
      patientId: string;
      title?: string;
      description?: string;
      isArchived: boolean;
      lastMessageAt?: string;
      createdAt: string;
      updatedAt: string;
    }>(`/zilloteam/conversations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteConversation(id: string) {
    return this.request(`/zilloteam/conversations/${id}`, {
      method: 'DELETE',
    });
  }


  async preserveConversationAttachments(conversationId: string) {
    return this.request<{ preserved: number }>(
      `/zilloteam/conversations/${conversationId}/preserve-attachments`,
      { method: 'POST' },
    );
  }

  async getPatientMediaFiles(patientId: string, category?: string, limit?: number, offset?: number) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    const query = params.toString();
    return this.request<{
      files: Array<{
        id: string;
        patientId: string;
        sourceConversationId: string | null;
        sourceAttachmentId: string | null;
        fileName: string;
        fileUrl: string;
        fileSize: number | null;
        fileType: string | null;
        category: string | null;
        preservedBy: string;
        preservedAt: string;
        uploader?: { id: string; firstName: string; lastName: string } | null;
      }>;
      total: number;
    }>(`/zilloteam/patients/${patientId}/media-files${query ? `?${query}` : ''}`);
  }

  async uploadPatientMediaFile(patientId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<{ fileName: string; fileUrl: string; category: string }>(
      `/zilloteam/patients/${patientId}/media-files/upload`,
      { method: 'POST', body: formData },
    );
  }

  async addConversationParticipant(conversationId: string, userId: string, role?: 'owner' | 'member') {
    return this.request(`/zilloteam/conversations/${conversationId}/participants`, {
      method: 'POST',
      body: JSON.stringify({ userId, role }),
    });
  }

  async removeConversationParticipant(conversationId: string, userId: string) {
    return this.request(`/zilloteam/conversations/${conversationId}/participants/${userId}`, {
      method: 'DELETE',
    });
  }

  // Messages
  async getMessages(conversationId: string, limit?: number, offset?: number) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    const query = params.toString();

    return this.request<{
      messages: Array<{
        id: string;
        conversationId: string;
        senderId: string;
        content: string;
        messageType: 'text' | 'file' | 'system';
        isEdited: boolean;
        isDeleted: boolean;
        createdAt: string;
        updatedAt: string;
        sender: { id: string; firstName: string; lastName: string; email: string };
        attachments: Array<any>;
        readBy: Array<{ userId: string; readAt: string }>;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/zilloteam/conversations/${conversationId}/messages${query ? `?${query}` : ''}`);
  }

  async searchMessages(query: string, limit: number = 20) {
    const params = new URLSearchParams({ q: query, limit: limit.toString() });
    return this.request<{
      results: Array<{
        id: string;
        content: string;
        createdAt: string;
        conversationId: string;
        conversationTitle: string | null;
        sender: { id: string; firstName: string; lastName: string };
        patient: { id: string; firstName: string; lastName: string } | null;
      }>;
      total: number;
    }>(`/zilloteam/messages/search?${params}`);
  }

  async createMessage(data: {
    conversationId: string;
    content: string;
    messageType?: 'text' | 'file' | 'system';
    replyToId?: string;
  }) {
    return this.request<{
      id: string;
      conversationId: string;
      senderId: string;
      content: string;
      messageType: string;
      isEdited: boolean;
      isDeleted: boolean;
      replyToId?: string;
      replyTo?: any;
      createdAt: string;
      updatedAt: string;
      sender: { id: string; firstName: string; lastName: string; email: string };
      attachments: Array<any>;
      readBy: Array<{ userId: string; readAt: string }>;
    }>('/zilloteam/messages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMessage(id: string, data: { content: string }) {
    return this.request<{
      id: string;
      conversationId: string;
      content: string;
      isEdited: boolean;
      updatedAt: string;
    }>(`/zilloteam/messages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMessage(id: string) {
    return this.request(`/zilloteam/messages/${id}`, {
      method: 'DELETE',
    });
  }

  async markMessageAsRead(id: string) {
    return this.request(`/zilloteam/messages/${id}/read`, {
      method: 'POST',
    });
  }

  async markConversationAsRead(conversationId: string) {
    return this.request(`/zilloteam/conversations/${conversationId}/mark-read`, {
      method: 'POST',
    });
  }

  // Patients (ZilloTeam)
  async getZilloTeamPatients(search?: string, limit?: number, offset?: number, sortBy?: 'name' | 'recent') {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    if (sortBy) params.append('sortBy', sortBy);
    const query = params.toString();

    return this.request<{
      patients: Array<{
        id: string;
        accountId: string;
        firstName: string;
        lastName: string;
        sex?: 'M' | 'F';
        socialSecurityNumber?: string;
        dateOfBirth?: string;
        email?: string;
        phone?: string;
        address?: string;
        postalCode?: string;
        city?: string;
        notes?: string;
        createdAt: string;
        updatedAt: string;
        activeConversationsCount?: number;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/zilloteam/patients${query ? `?${query}` : ''}`);
  }

  async searchZilloTeamPatients(query: string, limit?: number) {
    const params = new URLSearchParams({ q: query });
    if (limit) params.append('limit', limit.toString());

    return this.request<Array<{
      id: string;
      firstName: string;
      lastName: string;
      socialSecurityNumber?: string;
      email?: string;
      phone?: string;
    }>>(`/zilloteam/patients/search?${params}`);
  }

  async getZilloTeamPatient(id: string) {
    return this.request<{
      id: string;
      accountId: string;
      firstName: string;
      lastName: string;
      sex?: 'M' | 'F';
      socialSecurityNumber?: string;
      dateOfBirth?: string;
      email?: string;
      phone?: string;
      address?: string;
      postalCode?: string;
      city?: string;
      notes?: string;
      createdAt: string;
      updatedAt: string;
      conversations?: Array<{
        id: string;
        title?: string;
        lastMessageAt?: string;
        isArchived: boolean;
      }>;
    }>(`/zilloteam/patients/${id}`);
  }

  async createZilloTeamPatient(data: {
    prefix?: string;
    firstName: string;
    lastName: string;
    sex?: 'M' | 'F';
    socialSecurityNumber?: string;
    dateOfBirth?: string;
    email?: string;
    phone?: string;
    address?: string;
    postalCode?: string;
    city?: string;
    notes?: string;
  }) {
    return this.request<{
      id: string;
      firstName: string;
      lastName: string;
    }>('/zilloteam/patients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateZilloTeamPatient(id: string, data: Partial<{
    firstName: string;
    lastName: string;
    sex: 'M' | 'F';
    socialSecurityNumber: string;
    dateOfBirth: string;
    email: string;
    phone: string;
    address: string;
    postalCode: string;
    city: string;
    notes: string;
  }>) {
    return this.request<{
      id: string;
      firstName: string;
      lastName: string;
    }>(`/zilloteam/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteZilloTeamPatient(id: string) {
    return this.request(`/zilloteam/patients/${id}`, {
      method: 'DELETE',
    });
  }

  // Patient Attachments (all conversations)
  async getPatientAttachments(patientId: string, category?: string, limit?: number, offset?: number) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    const query = params.toString();

    return this.request<{
      attachments: Array<{
        id: string;
        messageId: string;
        fileName: string;
        fileUrl: string;
        fileSize: number;
        fileType: string;
        mimeType?: string;
        category: 'photo' | 'radiograph' | 'stl' | 'pdf' | 'other';
        uploadedById: string;
        uploadedAt: string;
        uploadedBy?: { id: string; firstName: string; lastName: string };
        messageCreatedAt?: string;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/zilloteam/patients/${patientId}/attachments${query ? `?${query}` : ''}`);
  }

  async getPatientHistory(patientId: string) {
    return this.request<Array<{
      id: string;
      patientId: string;
      changedBy: string;
      changedByName: string;
      fieldName: string;
      oldValue: string | null;
      newValue: string | null;
      changedAt: string;
    }>>(`/zilloteam/patients/${patientId}/history`);
  }

  async upsertPatientCabinetRef(patientId: string, dossierNumber: string) {
    return this.request<void>(`/zilloteam/patients/${patientId}/cabinet-ref`, {
      method: 'PUT',
      body: JSON.stringify({ dossierNumber }),
    });
  }

  // Attachments
  async getConversationAttachments(conversationId: string, category?: string, limit?: number, offset?: number) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    const query = params.toString();

    return this.request<{
      attachments: Array<{
        id: string;
        messageId: string;
        fileName: string;
        fileUrl: string;
        fileSize: number;
        mimeType: string;
        category: 'photo' | 'radiograph' | 'stl' | 'pdf' | 'other';
        uploadedById: string;
        uploadedAt: string;
        uploadedBy?: { id: string; firstName: string; lastName: string };
        messageCreatedAt?: string;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/zilloteam/conversations/${conversationId}/attachments${query ? `?${query}` : ''}`);
  }

  async getAttachmentStats(conversationId: string) {
    return this.request<{
      totalCount: number;
      totalSize: number;
      byCategory: Record<string, { count: number; size: number }>;
    }>(`/zilloteam/conversations/${conversationId}/attachments/stats`);
  }

  async createAttachment(data: {
    messageId: string;
    fileName: string;
    fileUrl: string;
    fileSize: number;
    mimeType: string;
    category: 'photo' | 'radiograph' | 'stl' | 'pdf' | 'other';
  }) {
    return this.request<{
      id: string;
      messageId: string;
      fileName: string;
      fileUrl: string;
      fileSize: number;
      mimeType: string;
      category: string;
      uploadedById: string;
      uploadedAt: string;
    }>('/zilloteam/attachments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadFile(file: File, messageId: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('messageId', messageId);

    const headers: HeadersInit = {};
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_URL}/zilloteam/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    return response.json() as Promise<ApiResponse<{
      id: string;
      messageId: string;
      fileName: string;
      fileUrl: string;
      fileSize: number;
      fileType: string;
      category: string;
      uploadedAt: string;
    }>>;
  }

  async deleteAttachment(id: string) {
    return this.request(`/zilloteam/attachments/${id}`, {
      method: 'DELETE',
    });
  }


  // ── Correspondence (Courrier IA) ─────────────────────────────────────────────

  async generateCorrespondenceText(data: { patientName: string; recipientName: string; subject: string; context: string }) {
    return this.request<{ text: string }>('/zilloteam/correspondence/generate-text', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateCorrespondencePdf(data: { recipientId: string; patientId: string; subject: string; body: string; imageUrls: string[] }) {
    return this.request<{ url: string; filename: string }>('/zilloteam/correspondence/generate-pdf', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ── Export & Backup ─────────────────────────────────────────────────────────

  async exportConversation(conversationId: string, format: 'pdf' | 'html' | 'pdf_with_files') {
    const response = await fetch(`${API_URL}/zilloteam/conversations/${conversationId}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
      },
      body: JSON.stringify({ format }),
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] || (format === 'pdf' ? 'conversation.pdf' : 'conversation.zip');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async generateBackup(options: { scope: string; scopeId?: string; includeJson: boolean; includeHtml: boolean; includeFiles: boolean }) {
    return this.request<{ jobId: string }>('/zilloteam/backup/generate', {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async getBackupStatus(jobId: string) {
    return this.request<any>(`/zilloteam/backup/status/${jobId}`);
  }

  async getBackupHistory() {
    return this.request<{ backups: any[] }>('/zilloteam/backup/history');
  }

  async downloadBackup(jobId: string) {
    const response = await fetch(`${API_URL}/zilloteam/backup/download/${jobId}`, {
      headers: this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {},
    });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] || 'backup.zip';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async deleteBackup(jobId: string) {
    return this.request(`/zilloteam/backup/${jobId}`, { method: 'DELETE' });
  }

  async restorePreview(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/zilloteam/backup/restore/preview`, {
      method: 'POST',
      headers: this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {},
      body: formData,
    });
    const json = await response.json();
    return json as ApiResponse<{
      tempPath: string;
      metadata: any;
      conversations: Array<{
        id: string;
        title: string;
        patientName: string | null;
        messageCount: number;
        attachmentCount: number;
        exists: boolean;
      }>;
    }>;
  }

  async restoreExecute(tempPath: string, selectedConversationIds: string[]) {
    return this.request<{ restored: number; skipped: number }>('/zilloteam/backup/restore/execute', {
      method: 'POST',
      body: JSON.stringify({ tempPath, selectedConversationIds }),
    });
  }

  // ── Membres ──────────────────────────────────────────────────────────────────

  async getMembres(params?: { search?: string; role?: string; limit?: number; offset?: number; sortBy?: string }) {
    const q = new URLSearchParams();
    if (params?.search) q.set('search', params.search);
    if (params?.role) q.set('role', params.role);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    if (params?.sortBy) q.set('sortBy', params.sortBy);
    const qs = q.toString();
    return this.request<any>(`/membres${qs ? `?${qs}` : ''}`);
  }

  async getMembre(id: string) {
    return this.request<any>(`/membres/${id}`);
  }

  async createMembre(data: Record<string, any>) {
    return this.request<any>('/membres', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateMembre(id: string, data: Record<string, any>) {
    return this.request<any>(`/membres/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteMembre(id: string) {
    return this.request<any>(`/membres/${id}`, { method: 'DELETE' });
  }

  async uploadMembreAvatar(id: string, file: File) {
    const formData = new FormData();
    formData.append('avatar', file);
    return this.request<any>(`/membres/${id}/avatar`, { method: 'POST', body: formData });
  }

  // ==================== PARTNERSHIPS (ZilloTeam Partenaires) ====================

  async searchPlatformUsers(params: { q: string; limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams();
    searchParams.set('q', params.q);
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.offset) searchParams.set('offset', params.offset.toString());
    return this.request<{
      users: Array<{
        id: string; email: string; firstName: string | null; lastName: string | null;
        displayName: string | null; officeName: string | null; avatarUrl: string | null;
        city: string | null; partnershipStatus: string | null;
      }>; total: number;
    }>(`/partnerships/search-users?${searchParams.toString()}`);
  }

  async getPartnerships(params?: { status?: string; search?: string; limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return this.request<{
      partnerships: Array<{
        id: string; status: string; direction: 'sent' | 'received'; message: string | null;
        createdAt: string; respondedAt: string | null;
        partner: {
          id: string; email: string; firstName: string | null; lastName: string | null;
          displayName: string | null; officeName: string | null; avatarUrl: string | null; city: string | null;
        };
      }>;
    }>(`/partnerships${query ? `?${query}` : ''}`);
  }

  async getPartnershipsPendingCount() {
    return this.request<{ count: number }>('/partnerships/pending-count');
  }

  async sendPartnershipInvitation(data: { addresseeId: string; message?: string }) {
    return this.request('/partnerships/invite', { method: 'POST', body: JSON.stringify(data) });
  }

  async respondToPartnership(id: string, action: 'accept' | 'decline') {
    return this.request(`/partnerships/${id}/respond`, { method: 'POST', body: JSON.stringify({ action }) });
  }

  async cancelPartnership(id: string) {
    return this.request(`/partnerships/${id}/cancel`, { method: 'POST' });
  }

  async removePartnership(id: string) {
    return this.request(`/partnerships/${id}`, { method: 'DELETE' });
  }

  async createInviteePartnership(data: { email: string; prefix: string; firstName?: string; lastName?: string; officeName?: string; message?: string }) {
    return this.request('/partnerships/create-invitee', { method: 'POST', body: JSON.stringify(data) });
  }

  async verifyPartnerInviteToken(token: string) {
    return this.request<{
      partnershipId: string;
      addresseeEmail: string;
      requesterName: string;
      requesterOffice: string | null;
    }>(`/partnerships/verify-invite-token?token=${encodeURIComponent(token)}`);
  }

  async completePartnerInvite(token: string, password: string) {
    return this.request<{
      user: any;
      account: any;
      tokens: { accessToken: string; refreshToken: string; expiresIn: number };
    }>('/partnerships/complete-invite', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async completePartnerInviteSocial(token: string, provider: 'google' | 'apple', idToken: string) {
    return this.request<{
      user: any;
      account: any;
      tokens: { accessToken: string; refreshToken: string; expiresIn: number };
    }>('/partnerships/complete-invite-social', {
      method: 'POST',
      body: JSON.stringify({ token, provider, idToken }),
    });
  }

  async googleLogin(credential: string) {
    return this.request<{
      user: any;
      account: any;
      tokens: { accessToken: string; refreshToken: string; expiresIn: number };
    }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
  }

  async appleLogin(idToken: string, user?: { name?: { firstName?: string; lastName?: string } }) {
    return this.request<{
      user: any;
      account: any;
      tokens: { accessToken: string; refreshToken: string; expiresIn: number };
    }>('/auth/apple', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken, user }),
    });
  }

  async getSocialConfig() {
    return this.request<{
      google: boolean;
      apple: boolean;
      googleClientId: string | null;
    }>('/auth/social-config');
  }

  // ==================== Ingest Token ====================

  async getIngestToken() {
    return this.request<{ token: string }>('/zilloteam/ingest-token');
  }

  // ==================== Partner Preferences ====================

  async getPartnerPreferences() {
    return this.request<Record<string, { nickname?: string | null; avatarColor?: string | null; customAvatarUrl?: string | null }>>('/zilloteam/partner-preferences');
  }

  async upsertPartnerPreference(partnerUserId: string, data: { nickname?: string | null; avatarColor?: string | null; customAvatarUrl?: string | null }) {
    return this.request<{ nickname?: string | null; avatarColor?: string | null; customAvatarUrl?: string | null }>(
      `/zilloteam/partner-preferences/${partnerUserId}`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
  }

  // ==================== Patient Link (Logos deep link) ====================

  async getPatientLink(params: Record<string, string>) {
    const qs = new URLSearchParams(params).toString();
    return this.request<
      | { action: 'found'; patientId: string; conversationCount: number }
      | {
          action: 'not_found';
          patientData: {
            firstName: string;
            lastName: string;
            prefix: string | null;
            sex: string | null;
            dateOfBirth: string | null;
            phone: string | null;
            socialSecurityNumber: string | null;
            address: string | null;
            postalCode: string | null;
            city: string | null;
            dossierNumber: string | null;
          };
        }
    >(`/zilloteam/patient-link?${qs}`);
  }

  async uploadPartnerAvatar(partnerUserId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<{ avatarUrl: string }>(
      `/zilloteam/partner-preferences/${partnerUserId}/avatar`,
      { method: 'POST', body: formData },
    );
  }

  // ── Équipe ─────────────────────────────────────────────────────────────────

  getMyEmployeProfile() {
    return this.request<{ employe: EquipeEmploye }>('/equipe/employes/me');
  }

  getEmployes(filters?: { statut?: string; search?: string }) {
    const params = new URLSearchParams();
    if (filters?.statut) params.set('statut', filters.statut);
    if (filters?.search) params.set('search', filters.search);
    const qs = params.toString();
    return this.request<{ employes: EquipeEmploye[] }>(`/equipe/employes${qs ? `?${qs}` : ''}`);
  }

  getEmploye(id: string) {
    return this.request<{ employe: EquipeEmploye }>(`/equipe/employes/${id}`);
  }

  createEmploye(data: Partial<EquipeEmploye>) {
    return this.request<{ employe: EquipeEmploye }>('/equipe/employes', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  updateEmploye(id: string, data: Partial<EquipeEmploye>) {
    return this.request<{ employe: EquipeEmploye }>(`/equipe/employes/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  archiveEmploye(id: string) {
    return this.request<null>(`/equipe/employes/${id}/archive`, { method: 'POST' });
  }

  getDocuments(employeId: string) {
    return this.request<{ documents: EquipeDocument[] }>(`/equipe/documents/${employeId}`);
  }

  createDocument(employeId: string, data: Partial<EquipeDocument>) {
    return this.request<{ document: EquipeDocument }>(`/equipe/documents/${employeId}`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  deleteDocument(employeId: string, documentId: string) {
    return this.request<null>(`/equipe/documents/${employeId}/${documentId}`, { method: 'DELETE' });
  }

  getPointageConfig() {
    return this.request<any>('/equipe/pointage/config');
  }

  updatePointageConfig(data: { pause_deduction_auto?: boolean; pause_duree_minutes?: number; pause_pointee?: boolean }) {
    return this.request<any>('/equipe/pointage/config', { method: 'PATCH', body: JSON.stringify(data) });
  }

  getPointageQrToken() {
    return this.request<{ token: string; expiresInSeconds: number; rotationSeconds: number }>('/equipe/pointage/qr-token');
  }

  getPointageHistory(filters?: { employeId?: string; dateFrom?: string; dateTo?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (filters?.employeId) params.set('employeId', filters.employeId);
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    if (filters?.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return this.request<PointageEvent[]>(`/equipe/pointage${qs ? `?${qs}` : ''}`);
  }

  submitManualPointage(data: { employeId: string; type: string; horodatage: string; motif: string }) {
    return this.request<PointageEvent>('/equipe/pointage/manual', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  // Admin pointage
  getAdminPointages(filters?: { employeId?: string; dateFrom?: string; dateTo?: string; methode?: string; validation?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (filters?.employeId) params.set('employeId', filters.employeId);
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    if (filters?.methode) params.set('methode', filters.methode);
    if (filters?.validation) params.set('validation', filters.validation);
    if (filters?.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return this.request<any[]>(`/equipe/pointage/admin${qs ? `?${qs}` : ''}`);
  }

  corrigerPointage(id: string, data: { horodatage: string; motif: string }) {
    return this.request<any>(`/equipe/pointage/${id}/corriger`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deletePointage(id: string) {
    return this.request<void>(`/equipe/pointage/${id}`, { method: 'DELETE' });
  }

  validerPointage(id: string) {
    return this.request<any>(`/equipe/pointage/${id}/valider`, { method: 'PATCH' });
  }

  contesterPointage(id: string) {
    return this.request<any>(`/equipe/pointage/${id}/contester`, { method: 'PATCH' });
  }

  getCompteurHeuresMensuel(mois: string) {
    return this.request<any[]>(`/equipe/compteur-heures?mois=${mois}`);
  }

  getCompteurHeuresEmploye(employeId: string, mois: string) {
    return this.request<any[]>(`/equipe/compteur-heures/${employeId}?mois=${mois}`);
  }

  getPointageAnomalies() {
    return this.request<any[]>('/equipe/compteur-heures/anomalies');
  }

  validerPeriodeHeures(employeId: string, data: { dateDebut: string; dateFin: string; commentaire?: string }) {
    return this.request<null>(`/equipe/compteur-heures/${employeId}/valider`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  getRegistre(filters?: { employeId?: string; typeEvenement?: string; dateFrom?: string; dateTo?: string }) {
    const params = new URLSearchParams();
    if (filters?.employeId) params.set('employeId', filters.employeId);
    if (filters?.typeEvenement) params.set('typeEvenement', filters.typeEvenement);
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    const qs = params.toString();
    return this.request<any[]>(`/equipe/registre${qs ? `?${qs}` : ''}`);
  }

  getAlertesEquipe(filters?: { statut?: string }) {
    const params = new URLSearchParams();
    if (filters?.statut) params.set('statut', filters.statut);
    const qs = params.toString();
    return this.request<EquipeAlerte[]>(`/equipe/alertes${qs ? `?${qs}` : ''}`);
  }

  getAlertesEquipeActives() {
    return this.request<EquipeAlerte[]>('/equipe/alertes/active');
  }

  acquitterAlerte(id: string) {
    return this.request<EquipeAlerte>(`/equipe/alertes/${id}/acquitter`, { method: 'POST' });
  }

  genererAlertes() {
    return this.request<{ message: string }>('/equipe/alertes/generer', { method: 'POST' });
  }

  getRoles() {
    return this.request<EquipeRole[]>('/equipe/roles');
  }

  createRole(data: { nom: string; description?: string; permissions?: Array<{ module: string; ressource: string; action: string }> }) {
    return this.request<EquipeRole>('/equipe/roles', { method: 'POST', body: JSON.stringify(data) });
  }

  updateRole(id: string, data: Partial<EquipeRole>) {
    return this.request<EquipeRole>(`/equipe/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteRole(id: string) {
    return this.request<null>(`/equipe/roles/${id}`, { method: 'DELETE' });
  }

  // ── Équipe Phase 2 — Congés ────────────────────────────────────────────────

  getConges(filters?: { statut?: string; employe_id?: string; dateFrom?: string; dateTo?: string }) {
    const params = new URLSearchParams();
    if (filters?.statut) params.set('statut', filters.statut);
    if (filters?.employe_id) params.set('employe_id', filters.employe_id);
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    const qs = params.toString();
    return this.request<any[]>(`/equipe/conges${qs ? `?${qs}` : ''}`);
  }

  getCongesCompteurs(employe_id?: string, annee?: number) {
    const params = new URLSearchParams();
    if (employe_id) params.set('employe_id', employe_id);
    if (annee) params.set('annee', String(annee));
    const qs = params.toString();
    return this.request<any[]>(`/equipe/conges/compteurs${qs ? `?${qs}` : ''}`);
  }

  createConge(data: { employe_id: string; type: string; date_debut: string; date_fin: string; nb_jours: number; motif?: string }) {
    return this.request<any>('/equipe/conges', { method: 'POST', body: JSON.stringify(data) });
  }

  approuverConge(id: string) {
    return this.request<any>(`/equipe/conges/${id}/approuver`, { method: 'PATCH' });
  }

  refuserConge(id: string, commentaire?: string) {
    return this.request<any>(`/equipe/conges/${id}/refuser`, { method: 'PATCH', body: JSON.stringify({ commentaire }) });
  }

  annulerConge(id: string) {
    return this.request<any>(`/equipe/conges/${id}/annuler`, { method: 'PATCH' });
  }

  initCongesCompteurs(employe_id: string, annee?: number) {
    return this.request<any>('/equipe/conges/compteurs/init', { method: 'POST', body: JSON.stringify({ employe_id, annee }) });
  }

  // ── Équipe Phase 5 — Fermetures cabinet ───────────────────────────────────

  getFermetures(annee?: number) {
    const qs = annee ? `?annee=${annee}` : '';
    return this.request<any>(`/equipe/fermetures${qs}`);
  }

  createFermeture(data: { date_debut: string; date_fin: string; motif?: string }) {
    return this.request<any>('/equipe/fermetures', { method: 'POST', body: JSON.stringify(data) });
  }

  deleteFermeture(id: string) {
    return this.request<any>(`/equipe/fermetures/${id}`, { method: 'DELETE' });
  }

  // ── Équipe Phase 5 — Rémunération libérale ────────────────────────────────

  getRemunerations(employeId: string, annee: number) {
    return this.request<any>(`/equipe/remuneration/${employeId}?annee=${annee}`);
  }

  createRemuneration(data: any) {
    return this.request<any>('/equipe/remuneration', { method: 'POST', body: JSON.stringify(data) });
  }

  updateRemuneration(id: string, data: any) {
    return this.request<any>(`/equipe/remuneration/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteRemuneration(id: string) {
    return this.request<any>(`/equipe/remuneration/${id}`, { method: 'DELETE' });
  }

  // ── Équipe — Sondages ───────────────────────────────────────────────────────
  getSondages() {
    return this.request<any[]>('/equipe/sondages');
  }
  getSondage(id: string) {
    return this.request<any>(`/equipe/sondages/${id}`);
  }
  createSondage(data: any) {
    return this.request<any>('/equipe/sondages', { method: 'POST', body: JSON.stringify(data) });
  }
  updateSondage(id: string, data: any) {
    return this.request<any>(`/equipe/sondages/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  publierSondage(id: string) {
    return this.request<any>(`/equipe/sondages/${id}/publier`, { method: 'PATCH' });
  }
  cloturerSondage(id: string) {
    return this.request<any>(`/equipe/sondages/${id}/cloturer`, { method: 'PATCH' });
  }
  deleteSondage(id: string) {
    return this.request<any>(`/equipe/sondages/${id}`, { method: 'DELETE' });
  }
  repondreSondage(id: string, reponses: Record<string, any>) {
    return this.request<any>(`/equipe/sondages/${id}/repondre`, { method: 'POST', body: JSON.stringify({ reponses }) });
  }
  getResultatsSondage(id: string) {
    return this.request<any>(`/equipe/sondages/${id}/resultats`);
  }

  // ── Équipe Phase 2 — Primes ────────────────────────────────────────────────

  getPrimes(filters?: { mois?: string; employe_id?: string; statut?: string }) {
    const params = new URLSearchParams();
    if (filters?.mois) params.set('mois', filters.mois);
    if (filters?.employe_id) params.set('employe_id', filters.employe_id);
    if (filters?.statut) params.set('statut', filters.statut);
    const qs = params.toString();
    return this.request<any[]>(`/equipe/primes${qs ? `?${qs}` : ''}`);
  }

  getPrimesConfig() {
    return this.request<any[]>('/equipe/primes/config');
  }

  createPrimeConfig(data: { nom: string; description?: string; montant_defaut?: number; est_recurrente?: boolean; mois_application?: number }) {
    return this.request<any>('/equipe/primes/config', { method: 'POST', body: JSON.stringify(data) });
  }

  updatePrimeConfig(id: string, data: any) {
    return this.request<any>(`/equipe/primes/config/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deletePrimeConfig(id: string) {
    return this.request<null>(`/equipe/primes/config/${id}`, { method: 'DELETE' });
  }

  createPrime(data: { employe_id: string; nom: string; montant: number; mois: string; config_id?: string; commentaire?: string }) {
    return this.request<any>('/equipe/primes', { method: 'POST', body: JSON.stringify(data) });
  }

  validerPrime(id: string) {
    return this.request<any>(`/equipe/primes/${id}/valider`, { method: 'PATCH' });
  }

  marquerPrimePaye(id: string) {
    return this.request<any>(`/equipe/primes/${id}/payer`, { method: 'PATCH' });
  }

  deletePrime(id: string) {
    return this.request<null>(`/equipe/primes/${id}`, { method: 'DELETE' });
  }

  genererPrimesMois(mois: string) {
    return this.request<{ created: number }>('/equipe/primes/generer', { method: 'POST', body: JSON.stringify({ mois }) });
  }

  // ── Équipe Phase 2 — Planning (template hebdomadaire) ───────────────────────

  getPlanningTemplate() {
    return this.request<any[]>('/equipe/planning');
  }

  getPlanningJourTemplate(jourSemaine: number) {
    return this.request<any[]>(`/equipe/planning/jour?jourSemaine=${jourSemaine}`);
  }

  createPlanningSlot(data: { employe_id: string; jour_semaine: number; heure_debut: string; heure_fin: string; type?: string; note?: string; salle_id?: string | null }) {
    return this.request<any>('/equipe/planning', { method: 'POST', body: JSON.stringify(data) });
  }

  updatePlanningSlot(id: string, data: any) {
    return this.request<any>(`/equipe/planning/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deletePlanningSlot(id: string) {
    return this.request<null>(`/equipe/planning/${id}`, { method: 'DELETE' });
  }

  // ── Équipe — Salles ────────────────────────────────────────────────────────

  getSalles() {
    return this.request<any[]>('/equipe/salles');
  }

  createSalle(data: { nom: string; couleur?: string; ordre?: number }) {
    return this.request<any>('/equipe/salles', { method: 'POST', body: JSON.stringify(data) });
  }

  updateSalle(id: string, data: { nom?: string; couleur?: string; est_active?: boolean; ordre?: number }) {
    return this.request<any>(`/equipe/salles/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteSalle(id: string) {
    return this.request<null>(`/equipe/salles/${id}`, { method: 'DELETE' });
  }

  // ── Équipe — Planning Types ───────────────────────────────────────────────

  getPlanningTypes() {
    return this.request<{ types: any[]; isDefault: boolean }>('/equipe/planning-types');
  }

  initializePlanningTypes() {
    return this.request<{ types: any[]; isDefault: boolean }>('/equipe/planning-types/initialize', { method: 'POST' });
  }

  createPlanningType(data: { label: string; code?: string; couleur?: string; ordre?: number }) {
    return this.request<any>('/equipe/planning-types', { method: 'POST', body: JSON.stringify(data) });
  }

  updatePlanningType(id: string, data: { label?: string; couleur?: string; est_actif?: boolean; ordre?: number }) {
    return this.request<any>(`/equipe/planning-types/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deletePlanningType(id: string) {
    return this.request<null>(`/equipe/planning-types/${id}`, { method: 'DELETE' });
  }

  // ── Équipe Phase 2 — Météo ─────────────────────────────────────────────────

  getMeteoToday() {
    return this.request<any[]>('/equipe/meteo/today');
  }

  getMeteoStats(period?: '7d' | '30d') {
    return this.request<any>(`/equipe/meteo/stats${period ? `?period=${period}` : ''}`);
  }

  submitMeteo(data: { employe_id: string; score: number; commentaire?: string }) {
    return this.request<any>('/equipe/meteo', { method: 'POST', body: JSON.stringify(data) });
  }

  // ── Équipe Phase 2 — Suggestions ──────────────────────────────────────────

  getSuggestions(filters?: { statut?: string; categorie?: string }) {
    const params = new URLSearchParams();
    if (filters?.statut) params.set('statut', filters.statut);
    if (filters?.categorie) params.set('categorie', filters.categorie);
    const qs = params.toString();
    return this.request<any[]>(`/equipe/suggestions${qs ? `?${qs}` : ''}`);
  }

  createSuggestion(data: { titre: string; contenu: string; categorie?: string; est_anonyme?: boolean; employe_id?: string }) {
    return this.request<any>('/equipe/suggestions', { method: 'POST', body: JSON.stringify(data) });
  }

  traiterSuggestion(id: string, data: { statut: string; reponse?: string }) {
    return this.request<any>(`/equipe/suggestions/${id}/traiter`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  // ── Équipe Phase 2 — Export Comptable ─────────────────────────────────────

  getEquipeExport(mois: string) {
    return this.request<any[]>(`/equipe/export?mois=${mois}`);
  }

  // ── Équipe Phase 2 — Mutuelle & Conformité ────────────────────────────────

  getMutuelleData() {
    return this.request<any[]>('/equipe/mutuelle');
  }

  updateMutuelle(employeId: string, data: { mutuelle_adherent?: boolean; mutuelle_date_adhesion?: string; mutuelle_regime?: string; mutuelle_num_adherent?: string; mutuelle_notes?: string }) {
    return this.request<any>(`/equipe/mutuelle/${employeId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  // ── Équipe Phase 3 — Bulletins de salaire ────────────────────────────────

  getBulletins(mois: string) {
    return this.request<any[]>(`/equipe/bulletins?mois=${mois}`);
  }

  genererBulletins(mois: string) {
    return this.request<{ generated: number; bulletins: any[] }>('/equipe/bulletins/generer', { method: 'POST', body: JSON.stringify({ mois }) });
  }

  getBulletinsForEmploye(employeId: string) {
    return this.request<any[]>(`/equipe/bulletins/employe/${employeId}`);
  }

  getBulletinData(id: string) {
    return this.request<any>(`/equipe/bulletins/${id}/data`);
  }

  getPaieConfig() {
    return this.request<any>('/equipe/bulletins/config');
  }

  updatePaieConfig(data: Record<string, any>) {
    return this.request<any>('/equipe/bulletins/config', { method: 'PATCH', body: JSON.stringify(data) });
  }

  // ── Équipe Phase 3 — Entretiens annuels ──────────────────────────────────

  getEntretiens(filters?: { employe_id?: string; annee?: number; type?: string }) {
    const params = new URLSearchParams();
    if (filters?.employe_id) params.set('employe_id', filters.employe_id);
    if (filters?.annee) params.set('annee', String(filters.annee));
    if (filters?.type) params.set('type', filters.type);
    const qs = params.toString();
    return this.request<any[]>(`/equipe/entretiens${qs ? '?' + qs : ''}`);
  }

  createEntretien(data: Record<string, any>) {
    return this.request<any>('/equipe/entretiens', { method: 'POST', body: JSON.stringify(data) });
  }

  updateEntretien(id: string, data: Record<string, any>) {
    return this.request<any>(`/equipe/entretiens/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  signerEntretien(id: string, role: 'manager' | 'employe') {
    return this.request<any>(`/equipe/entretiens/${id}/signer`, { method: 'PATCH', body: JSON.stringify({ role }) });
  }

  // Entretiens templates
  getEntretiensTemplates() {
    return this.request<any[]>('/equipe/entretiens/templates');
  }

  createEntretienTemplate(data: { nom: string; description?: string; grille?: any[]; est_defaut?: boolean }) {
    return this.request<any>('/equipe/entretiens/templates', { method: 'POST', body: JSON.stringify(data) });
  }

  updateEntretienTemplate(id: string, data: { nom?: string; description?: string; grille?: any[]; est_defaut?: boolean }) {
    return this.request<any>(`/equipe/entretiens/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteEntretienTemplate(id: string) {
    return this.request<void>(`/equipe/entretiens/templates/${id}`, { method: 'DELETE' });
  }

  // Horaires (per-employee schedules)
  getEmployeHoraires() {
    return this.request<any[]>('/equipe/horaires');
  }

  getEmployeHoraire(employeId: string) {
    return this.request<any>(`/equipe/horaires/${employeId}`);
  }

  saveEmployeHoraire(employeId: string, data: {
    pattern: Record<string, { travaille: boolean; debut?: string; fin?: string; pause_debut?: string; pause_fin?: string }>;
    date_debut: string;
  }) {
    return this.request<any>(`/equipe/horaires/${employeId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  getEmployeHistorique(employeId: string) {
    return this.request<any[]>(`/equipe/horaires/${employeId}/historique`);
  }

  deleteHoraireVersion(employeId: string, horaireId: string) {
    return this.request<void>(`/equipe/horaires/${employeId}/version/${horaireId}`, { method: 'DELETE' });
  }

  // Annonces d'embauche
  getAnnonces() {
    return this.request<any[]>('/equipe/annonces');
  }

  createAnnonce(data: { titre: string; poste?: string; type_contrat?: string; contenu: string; statut?: string }) {
    return this.request<any>('/equipe/annonces', { method: 'POST', body: JSON.stringify(data) });
  }

  updateAnnonce(id: string, data: Record<string, any>) {
    return this.request<any>(`/equipe/annonces/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteAnnonce(id: string) {
    return this.request<void>(`/equipe/annonces/${id}`, { method: 'DELETE' });
  }

  genererAnnonceIA(data: { poste: string; type_contrat: string; heures_hebdo?: number; salaire_fourchette?: string; competences?: string[]; avantages?: string[]; description_cabinet?: string }) {
    return this.request<{ contenu: string }>('/equipe/annonces/generer-ia', { method: 'POST', body: JSON.stringify(data) });
  }

  // ── Équipe — Candidatures (Partie U) ─────────────────────────────────────

  getCandidatures(annonceId?: string) {
    const q = annonceId ? `?annonce_id=${annonceId}` : '';
    return this.request<any[]>(`/equipe/candidatures${q}`);
  }

  getCandidature(id: string) {
    return this.request<any>(`/equipe/candidatures/${id}`);
  }

  createCandidature(formData: FormData) {
    return this.request<any>('/equipe/candidatures', { method: 'POST', body: formData });
  }

  updateCandidature(id: string, data: { statut?: string; notes_admin?: string; annonce_id?: string }) {
    return this.request<any>(`/equipe/candidatures/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteCandidature(id: string) {
    return this.request<void>(`/equipe/candidatures/${id}`, { method: 'DELETE' });
  }

  reScoreCandidate(id: string) {
    return this.request<any>(`/equipe/candidatures/${id}/re-scorer`, { method: 'POST' });
  }

  // ── Équipe — Réunions IA (Partie V) ─────────────────────────────────────

  getReunions() {
    return this.request<any[]>('/equipe/reunions');
  }

  getReunion(id: string) {
    return this.request<any>(`/equipe/reunions/${id}`);
  }

  createReunion(data: { titre: string; date_reunion: string; participants?: string[] }) {
    return this.request<any>('/equipe/reunions', { method: 'POST', body: JSON.stringify(data) });
  }

  updateReunion(id: string, data: Record<string, any>) {
    return this.request<any>(`/equipe/reunions/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteReunion(id: string) {
    return this.request<void>(`/equipe/reunions/${id}`, { method: 'DELETE' });
  }

  transcrireReunion(id: string, audioBlob: Blob, fileName: string) {
    const fd = new FormData();
    fd.append('audio', audioBlob, fileName);
    return this.request<any>(`/equipe/reunions/${id}/transcrire`, { method: 'POST', body: fd });
  }

  analyserReunion(id: string) {
    return this.request<any>(`/equipe/reunions/${id}/analyser`, { method: 'POST' });
  }

  creerTachesReunion(id: string, tachesIndices: { index: number; employe_id?: string }[]) {
    return this.request<{ created: number }>(`/equipe/reunions/${id}/creer-taches`, {
      method: 'POST', body: JSON.stringify({ taches_indices: tachesIndices }),
    });
  }

  // ── Équipe — Réunions Dictation Stream ──────────────────────────────────

  async startReunionDictation(reunionId: string): Promise<{ sessionId: string }> {
    const data = await this.request<{ sessionId: string }>('/equipe/reunions/dictation/start', {
      method: 'POST',
      body: JSON.stringify({ reunion_id: reunionId }),
    });
    return data as unknown as { sessionId: string };
  }

  async sendReunionDictationChunk(sessionId: string, audioBlob: Blob): Promise<void> {
    const formData = new FormData();
    const ext = audioBlob.type.includes('webm') ? 'webm' : audioBlob.type.includes('mp4') ? 'mp4' : 'wav';
    formData.append('audio', audioBlob, `chunk.${ext}`);

    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${API_URL}/equipe/reunions/dictation/chunk?sessionId=${sessionId}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!res.ok) throw new Error('Chunk upload failed');
  }

  getReunionDictationStreamUrl(sessionId: string): string {
    return `${API_URL}/equipe/reunions/dictation/stream?sessionId=${sessionId}`;
  }

  async pauseReunionDictation(sessionId: string): Promise<void> {
    await this.request('/equipe/reunions/dictation/pause', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  async resumeReunionDictation(sessionId: string): Promise<void> {
    await this.request('/equipe/reunions/dictation/resume', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  async finalizeReunionDictation(sessionId: string, reunionId: string): Promise<any> {
    const data = await this.request<any>('/equipe/reunions/dictation/finalize', {
      method: 'POST',
      body: JSON.stringify({ sessionId, reunion_id: reunionId }),
    });
    return data;
  }

  async deleteReunionDictation(sessionId: string): Promise<void> {
    await this.request(`/equipe/reunions/dictation?sessionId=${sessionId}`, { method: 'DELETE' });
  }

  // ── Équipe — Juridique IA (Partie W) ─────────────────────────────────────

  getJuridiqueProcedures() {
    return this.request<any[]>('/equipe/juridique/procedures');
  }

  getJuridiqueRisques() {
    return this.request<{ risques: any[]; score: number }>('/equipe/juridique/risques');
  }

  createJuridiqueSession() {
    return this.request<any>('/equipe/juridique/sessions', { method: 'POST' });
  }

  getJuridiqueSessions() {
    return this.request<any[]>('/equipe/juridique/sessions');
  }

  getJuridiqueSessionMessages(sessionId: string) {
    return this.request<any[]>(`/equipe/juridique/sessions/${sessionId}`);
  }

  sendJuridiqueMessage(sessionId: string, content: string) {
    return this.request<any>(`/equipe/juridique/sessions/${sessionId}/messages`, {
      method: 'POST', body: JSON.stringify({ content }),
    }, 120_000);
  }

  deleteJuridiqueSession(sessionId: string) {
    return this.request<void>(`/equipe/juridique/sessions/${sessionId}`, { method: 'DELETE' });
  }

  getDocumentsJuridiques() {
    return this.request<any[]>('/equipe/juridique/documents');
  }

  genererDocumentJuridique(data: { type: string; employe_id?: string; contexte?: Record<string, any> }) {
    return this.request<any>('/equipe/juridique/documents/generer', { method: 'POST', body: JSON.stringify(data) }, 120_000);
  }

  updateDocumentJuridique(id: string, data: { titre?: string; contenu?: string; statut?: string }) {
    return this.request<any>(`/equipe/juridique/documents/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteDocumentJuridique(id: string) {
    return this.request<void>(`/equipe/juridique/documents/${id}`, { method: 'DELETE' });
  }

  getDossiersJuridiques() {
    return this.request<any[]>('/equipe/juridique/dossiers');
  }

  getDossierJuridique(id: string) {
    return this.request<any>(`/equipe/juridique/dossiers/${id}`);
  }

  createDossierJuridique(data: { employe_id?: string; type_procedure: string; titre: string; date_debut: string; date_faits?: string; notes?: string }) {
    return this.request<any>('/equipe/juridique/dossiers', { method: 'POST', body: JSON.stringify(data) });
  }

  updateDossierJuridique(id: string, data: { avancement?: Record<string, any>; statut?: string; notes?: string }) {
    return this.request<any>(`/equipe/juridique/dossiers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteDossierJuridique(id: string) {
    return this.request<void>(`/equipe/juridique/dossiers/${id}`, { method: 'DELETE' });
  }

  // ── Équipe — Heures supplémentaires (Partie P) ────────────────────────────

  getHeuresSupSummary(annee: number) {
    return this.request<any[]>(`/equipe/heures-sup?annee=${annee}`);
  }

  getHeureSup(employeId: string, annee: number) {
    return this.request<any>(`/equipe/heures-sup/${employeId}?annee=${annee}`);
  }

  convertirHeureSup(employeId: string, data: { nbHeures: number; dateMouvement: string; motif?: string }) {
    return this.request<any>(`/equipe/heures-sup/${employeId}/convertir`, { method: 'POST', body: JSON.stringify(data) });
  }

  prendreRecupHS(employeId: string, data: { heures: number; dateMouvement: string; motif?: string }) {
    return this.request<any>(`/equipe/heures-sup/${employeId}/prendre`, { method: 'POST', body: JSON.stringify(data) });
  }

  updateModeCompensationHS(employeId: string, mode: string) {
    return this.request<any>(`/equipe/heures-sup/${employeId}/mode`, { method: 'PATCH', body: JSON.stringify({ mode }) });
  }

  // ── Dock — Aggregated badge counts ──────────────────────────────────────

  getDockBadges() {
    return this.request<{ stock: number; team: number; docs: number; tasks: number; rh: number; redactia: number }>('/dock/badges');
  }

  // ── Équipe Phase 3 — Notifications badges ────────────────────────────────

  getEquipeBadges() {
    return this.request<{ alertes_actives: number; conges_attente: number; primes_brouillon: number; entretiens_planifies: number; absences_en_cours: number; signalements_ouverts: number }>('/equipe/badges');
  }

  // ── Équipe — Signalements ────────────────────────────────────

  createSignalement(data: { pointageId: string; message: string }) {
    return this.request('/equipe/signalements', { method: 'POST', body: JSON.stringify(data) });
  }

  getSignalements(filters?: { status?: string; employeId?: string }) {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.employeId) params.set('employeId', filters.employeId);
    const qs = params.toString();
    return this.request<any[]>(`/equipe/signalements${qs ? `?${qs}` : ''}`);
  }

  getMySignalements() {
    return this.request<any[]>('/equipe/signalements/mine');
  }

  resolveSignalement(id: string, data: { status: string; note?: string }) {
    return this.request(`/equipe/signalements/${id}/resolve`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  // ── Équipe Phase 4 — Absences ────────────────────────────────

  getAbsences(filters?: { employe_id?: string; annee?: number; type?: string; statut?: string }) {
    const params = new URLSearchParams();
    if (filters?.employe_id) params.set('employe_id', filters.employe_id);
    if (filters?.annee) params.set('annee', String(filters.annee));
    if (filters?.type) params.set('type', filters.type);
    if (filters?.statut) params.set('statut', filters.statut);
    return this.request<any[]>(`/equipe/absences?${params}`);
  }

  getAbsencesStats(annee?: number) {
    const params = annee ? `?annee=${annee}` : '';
    return this.request<any>(`/equipe/absences/stats${params}`);
  }

  createAbsence(data: { employe_id: string; type?: string; date_debut: string; date_fin?: string; motif?: string; medecin?: string }) {
    return this.request<any>('/equipe/absences', { method: 'POST', body: JSON.stringify(data) });
  }

  updateAbsence(id: string, data: Record<string, any>) {
    return this.request<any>(`/equipe/absences/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  uploadAbsenceJustificatif(absenceId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<any>(`/equipe/absences/${absenceId}/upload`, {
      method: 'POST',
      body: formData,
    });
  }

  // ── Équipe Phase 4 — Formations ────────────────────────────────

  getFormations(filters?: { type?: string; statut?: string }) {
    const params = new URLSearchParams();
    if (filters?.type) params.set('type', filters.type);
    if (filters?.statut) params.set('statut', filters.statut);
    return this.request<any[]>(`/equipe/formations?${params}`);
  }

  createFormation(data: Record<string, any>) {
    return this.request<any>('/equipe/formations', { method: 'POST', body: JSON.stringify(data) });
  }

  updateFormation(id: string, data: Record<string, any>) {
    return this.request<any>(`/equipe/formations/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteFormation(id: string) {
    return this.request<any>(`/equipe/formations/${id}`, { method: 'DELETE' });
  }

  inscrireEmployeFormation(formationId: string, employeId: string) {
    return this.request<any>(`/equipe/formations/${formationId}/inscrire`, { method: 'POST', body: JSON.stringify({ employe_id: employeId }) });
  }

  getInscriptions(filters?: { employe_id?: string; formation_id?: string }) {
    const params = new URLSearchParams();
    if (filters?.employe_id) params.set('employe_id', filters.employe_id);
    if (filters?.formation_id) params.set('formation_id', filters.formation_id);
    return this.request<any[]>(`/equipe/formations/inscriptions?${params}`);
  }

  updateInscription(id: string, data: Record<string, any>) {
    return this.request<any>(`/equipe/formations/inscriptions/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  getCompetences(filters?: { employe_id?: string }) {
    const params = filters?.employe_id ? `?employe_id=${filters.employe_id}` : '';
    return this.request<any[]>(`/equipe/competences${params}`);
  }

  createCompetence(data: Record<string, any>) {
    return this.request<any>('/equipe/competences', { method: 'POST', body: JSON.stringify(data) });
  }

  updateCompetence(id: string, data: Record<string, any>) {
    return this.request<any>(`/equipe/competences/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteCompetence(id: string) {
    return this.request<any>(`/equipe/competences/${id}`, { method: 'DELETE' });
  }

  // ── Équipe Phase 4 — Objectifs ────────────────────────────────

  getObjectifs(filters?: { employe_id?: string; annee?: number; statut?: string }) {
    const params = new URLSearchParams();
    if (filters?.employe_id) params.set('employe_id', filters.employe_id);
    if (filters?.annee) params.set('annee', String(filters.annee));
    if (filters?.statut) params.set('statut', filters.statut);
    return this.request<any[]>(`/equipe/objectifs?${params}`);
  }

  createObjectif(data: Record<string, any>) {
    return this.request<any>('/equipe/objectifs', { method: 'POST', body: JSON.stringify(data) });
  }

  updateObjectif(id: string, data: Record<string, any>) {
    return this.request<any>(`/equipe/objectifs/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  updateObjectifProgress(id: string, valeurActuelle: number) {
    return this.request<any>(`/equipe/objectifs/${id}/progress`, { method: 'PATCH', body: JSON.stringify({ valeur_actuelle: valeurActuelle }) });
  }

  deleteObjectif(id: string) {
    return this.request<any>(`/equipe/objectifs/${id}`, { method: 'DELETE' });
  }

  // ── Équipe Phase 4 — Reporting ────────────────────────────────

  getReportingResume() {
    return this.request<any>('/equipe/reporting/resume');
  }

  getReportingAbsenteisme(annee?: number) {
    const params = annee ? `?annee=${annee}` : '';
    return this.request<any>(`/equipe/reporting/absenteisme${params}`);
  }

  getReportingMeteoTrend(periode?: '30d' | '60d' | '90d') {
    const params = periode ? `?periode=${periode}` : '';
    return this.request<any>(`/equipe/reporting/meteo-trend${params}`);
  }

  getReportingConformite() {
    return this.request<any>('/equipe/reporting/conformite');
  }

  getReportingTurnover(annee?: number) {
    const params = annee ? `?annee=${annee}` : '';
    return this.request<any>(`/equipe/reporting/turnover${params}`);
  }

  getReportingFormations(annee?: number) {
    const params = annee ? `?annee=${annee}` : '';
    return this.request<any>(`/equipe/reporting/formations${params}`);
  }

  // ── Studio Audio (Répondeur TTS) ──────────────────────────────

  getStudioVoices() {
    return this.request<StudioVoice[]>('/repondeur/studio/voices');
  }

  getStudioTemplates() {
    return this.request<StudioTemplate[]>('/repondeur/studio/templates');
  }

  generateStudioMessage(data: {
    titre: string;
    texte: string;
    type_message: string;
    voice_id: string;
    voice_name: string;
    musique_fond_id?: string | null;
    volume_voix?: number;
    volume_musique?: number;
  }) {
    return this.request<StudioGeneratedMessage>('/repondeur/studio/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getStudioMessages(filters?: { type?: string }) {
    const params = new URLSearchParams();
    if (filters?.type) params.set('type', filters.type);
    const qs = params.toString();
    return this.request<StudioGeneratedMessage[]>(`/repondeur/studio/messages${qs ? `?${qs}` : ''}`);
  }

  getStudioMessage(id: string) {
    return this.request<StudioGeneratedMessage>(`/repondeur/studio/messages/${id}`);
  }

  deleteStudioMessage(id: string) {
    return this.request<{ deleted: boolean }>(`/repondeur/studio/messages/${id}`, { method: 'DELETE' });
  }

  getStudioMusiques() {
    return this.request<StudioBackgroundMusic[]>('/repondeur/studio/musiques');
  }

  generateStudioMusique(data: { prompt: string; nom: string; duration_seconds?: number }) {
    return this.request<StudioBackgroundMusic>('/repondeur/studio/musiques/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getStudioMessageAudioUrl(id: string): string {
    return `${API_URL}/repondeur/studio/messages/${id}/audio`;
  }

  getStudioMessageWavUrl(id: string): string {
    return `${API_URL}/repondeur/studio/messages/${id}/audio/wav`;
  }

  getStudioMusiqueAudioUrl(id: string): string {
    return `${API_URL}/repondeur/studio/musiques/${id}/audio`;
  }

  getStudioVoicePreviewUrl(voiceId: string): string {
    return `${API_URL}/repondeur/studio/voices/${voiceId}/preview`;
  }

  getStudioMusiqueJustificatifUrl(id: string): string {
    return `${API_URL}/repondeur/studio/musiques/${id}/justificatif`;
  }

  uploadStudioMusique(file: File, data: { nom: string; description?: string; categorie?: string; licence_type?: string; licence_source?: string; licence_url?: string; licence_details?: string }) {
    const formData = new FormData();
    formData.append('audio', file);
    Object.entries(data).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });
    return this.request<StudioBackgroundMusic>('/repondeur/studio/musiques', { method: 'POST', body: formData });
  }

  deleteStudioMusique(id: string) {
    return this.request<{ deleted: boolean }>(`/repondeur/studio/musiques/${id}`, { method: 'DELETE' });
  }

  // ==================== PERMISSIONS ====================

  getMembrePermissions(membreId: string) {
    return this.request<{ role: string; permissions: Record<string, any>; overrides: Record<string, any> }>(`/membres/${membreId}/permissions`);
  }

  updateMembrePermissions(membreId: string, permissions: Record<string, any>) {
    return this.request<{ role: string; permissions: Record<string, any>; overrides: Record<string, any> }>(`/membres/${membreId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    });
  }

  // ==================== MULTI-ENTITY ====================

  async switchContext(accountId: string) {
    return this.request<{
      account: { id: string; name: string; slug: string; enabled_modules: string[]; plan: string; entity_type?: string };
      tokens: { accessToken: string; refreshToken: string; expiresIn: number };
    }>('/auth/switch-context', {
      method: 'POST',
      body: JSON.stringify({ accountId }),
    });
  }

  // Entities
  async getEntityDetails() {
    return this.request<{ entityType: string; details: any }>('/entities/details');
  }

  async updateCabinetDetails(data: { rpps?: string; finess?: string; legalStructure?: string }) {
    return this.request<any>('/entities/cabinet', { method: 'PUT', body: JSON.stringify(data) });
  }

  async updateLaboratoryDetails(data: { labRegistrationNumber?: string; specializations?: string[] }) {
    return this.request<any>('/entities/laboratory', { method: 'PUT', body: JSON.stringify(data) });
  }

  // Invitations
  async getInvitations() {
    return this.request<any[]>('/invitations');
  }

  async createInvitation(data: {
    email: string; firstName?: string; lastName?: string;
    role?: string; accessModule?: string; tokenType?: string; personalMessage?: string;
  }) {
    return this.request<any>('/invitations', { method: 'POST', body: JSON.stringify(data) });
  }

  async verifyInvitation(token: string) {
    return this.request<{
      accountName: string; entityType: string; inviterName: string;
      role: string; personalMessage: string | null;
    }>(`/invitations/verify/${token}`);
  }

  async acceptInvitation(token: string) {
    return this.request<any>(`/invitations/accept/${token}`, { method: 'POST' });
  }

  async revokeInvitation(id: string) {
    return this.request<any>(`/invitations/${id}`, { method: 'DELETE' });
  }

  // Collaborations
  async getCollaborations() {
    return this.request<any[]>('/collaborations');
  }

  async initiateCollaboration(partnerId: string, notes?: string) {
    return this.request<any>('/collaborations', {
      method: 'POST',
      body: JSON.stringify({ partnerId, notes }),
    });
  }

  async respondCollaboration(id: string, accept: boolean) {
    return this.request<any>(`/collaborations/${id}/respond`, {
      method: 'POST',
      body: JSON.stringify({ accept }),
    });
  }

  // Work Orders
  async getWorkOrders() {
    return this.request<any[]>('/collaborations/work-orders');
  }

  async getWorkOrder(id: string) {
    return this.request<any>(`/collaborations/work-orders/${id}`);
  }

  async createWorkOrder(data: {
    laboratoryId: string; patientRef?: string; patientInitials?: string;
    workType?: string; toothNumbers?: number[]; shade?: string;
    instructions?: string; deadline?: string; priority?: string;
    agreedPrice?: number; currency?: string;
  }) {
    return this.request<any>('/collaborations/work-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWorkOrderStatus(id: string, status: string, comment?: string) {
    return this.request<any>(`/collaborations/work-orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, comment }),
    });
  }

  async getWorkOrderUpdates(id: string) {
    return this.request<any[]>(`/collaborations/work-orders/${id}/updates`);
  }

  // ==================== ZILLOTASKS MODULE ====================

  async getZtSidebarCounts() {
    return this.request<{ my: number; general: number; documents: number; suivis: number }>('/zillotasks/sidebar-counts');
  }

  async getZtListes() {
    return this.request<any[]>('/zillotasks/listes');
  }

  async createZtListe(data: { nom: string; description?: string; icone?: string; couleur?: string }) {
    return this.request<any>('/zillotasks/listes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateZtListe(id: string, data: { nom?: string; description?: string | null; icone?: string | null; couleur?: string | null }) {
    return this.request<any>(`/zillotasks/listes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteZtListe(id: string) {
    return this.request<any>(`/zillotasks/listes/${id}`, {
      method: 'DELETE',
    });
  }

  async addZtListeAccess(listeId: string, userId: string) {
    return this.request<any>(`/zillotasks/listes/${listeId}/access`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async removeZtListeAccess(listeId: string, userId: string) {
    return this.request<any>(`/zillotasks/listes/${listeId}/access/${userId}`, {
      method: 'DELETE',
    });
  }

  async getZtTaches(params?: { listeId?: string; statut?: string; priorite?: string; hasDocument?: boolean; limit?: number; offset?: number }) {
    const q = new URLSearchParams();
    if (params?.listeId) q.set('listeId', params.listeId);
    if (params?.statut) q.set('statut', params.statut);
    if (params?.priorite) q.set('priorite', params.priorite);
    if (params?.hasDocument) q.set('hasDocument', 'true');
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.request<{ taches: any[]; total: number; hasMore: boolean }>(`/zillotasks/taches${qs ? `?${qs}` : ''}`);
  }

  async getZtMyTaches() {
    return this.request<any[]>('/zillotasks/taches/my');
  }

  async getZtAssignedByMe() {
    return this.request<any[]>('/zillotasks/taches/assigned-by-me');
  }

  async getZtTache(id: string) {
    return this.request<any>(`/zillotasks/taches/${id}`);
  }

  async createZtTache(data: {
    listeId: string;
    titre: string;
    description?: string | null;
    priorite?: 'basse' | 'normale' | 'haute' | 'urgente';
    dateEcheance?: string | null;
    assignees?: string[];
    documentId?: string | null;
  }) {
    return this.request<any>('/zillotasks/taches', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateZtTache(id: string, data: {
    titre?: string;
    description?: string | null;
    priorite?: 'basse' | 'normale' | 'haute' | 'urgente';
    dateEcheance?: string | null;
    listeId?: string;
    position?: number;
  }) {
    return this.request<any>(`/zillotasks/taches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async toggleZtTache(id: string) {
    return this.request<any>(`/zillotasks/taches/${id}/toggle`, {
      method: 'POST',
    });
  }

  async deleteZtTache(id: string) {
    return this.request<any>(`/zillotasks/taches/${id}`, {
      method: 'DELETE',
    });
  }

  async assignZtTache(tacheId: string, userId: string) {
    return this.request<any>(`/zillotasks/taches/${tacheId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async unassignZtTache(tacheId: string, userId: string) {
    return this.request<any>(`/zillotasks/taches/${tacheId}/assign/${userId}`, {
      method: 'DELETE',
    });
  }

  async getZtMessages(tacheId: string, params?: { limit?: number; offset?: number }) {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.request<{ messages: any[]; total: number; hasMore: boolean }>(`/zillotasks/taches/${tacheId}/messages${qs ? `?${qs}` : ''}`);
  }

  async createZtMessage(tacheId: string, contenu: string) {
    return this.request<any>(`/zillotasks/taches/${tacheId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ contenu }),
    });
  }

  async deleteZtMessage(messageId: string) {
    return this.request<any>(`/zillotasks/messages/${messageId}`, {
      method: 'DELETE',
    });
  }

  async getZtNotifications(params?: { limit?: number; offset?: number }) {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.request<any>(`/zillotasks/notifications${qs ? `?${qs}` : ''}`);
  }

  async getZtNotificationsCount() {
    return this.request<{ count: number }>('/zillotasks/notifications/count');
  }

  async markZtNotificationsRead(notificationIds: string[]) {
    return this.request<any>('/zillotasks/notifications/read', {
      method: 'POST',
      body: JSON.stringify({ notificationIds }),
    });
  }

  async markZtAllNotificationsRead() {
    return this.request<any>('/zillotasks/notifications/read-all', {
      method: 'POST',
    });
  }

  // ==================== ZilloDocs ====================

  async getZdTypesDocument() {
    return this.request<{ types: any[] }>('/zillodocs/types');
  }

  async getZdDocuments(params?: {
    typeId?: string; correspondantId?: string; statut?: string;
    tagId?: string; search?: string; dateFrom?: string; dateTo?: string;
    page?: number; limit?: number; sort?: string; priveOnly?: boolean; includePrivate?: boolean;
  }) {
    const sp = new URLSearchParams();
    if (params?.typeId) sp.set('typeId', params.typeId);
    if (params?.correspondantId) sp.set('correspondantId', params.correspondantId);
    if (params?.statut) sp.set('statut', params.statut);
    if (params?.tagId) sp.set('tagId', params.tagId);
    if (params?.search) sp.set('search', params.search);
    if (params?.dateFrom) sp.set('dateFrom', params.dateFrom);
    if (params?.dateTo) sp.set('dateTo', params.dateTo);
    if (params?.page) sp.set('page', params.page.toString());
    if (params?.limit) sp.set('limit', params.limit.toString());
    if (params?.sort) sp.set('sort', params.sort);
    if (params?.priveOnly) sp.set('priveOnly', 'true');
    if (params?.includePrivate) sp.set('includePrivate', 'true');
    const qs = sp.toString();
    return this.request<{ items: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/zillodocs/documents${qs ? `?${qs}` : ''}`
    );
  }

  async searchZdDocuments(q: string, limit = 20, offset = 0) {
    const sp = new URLSearchParams({ q, limit: limit.toString(), offset: offset.toString() });
    return this.request<{ documents: any[]; total: number }>(`/zillodocs/documents/search?${sp}`);
  }

  async askZdQuestion(question: string) {
    return this.request<{
      answer: string;
      documents: any[];
      model: string;
      tokens: { input: number; output: number };
      duration_ms: number;
    }>('/zillodocs/documents/question', {
      method: 'POST',
      body: JSON.stringify({ question }),
    });
  }

  async getZdDocument(id: string) {
    return this.request<{ document: any }>(`/zillodocs/documents/${id}`);
  }

  async uploadZdDocuments(files: File[], prive?: boolean) {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    if (prive) formData.append('prive', 'true');
    return this.request<{ documents: any[]; duplicates?: Array<{ filename: string; existing_id: string; existing_titre: string | null }> }>('/zillodocs/documents/upload', {
      method: 'POST',
      body: formData,
    }, 120_000);
  }

  async updateZdDocument(id: string, data: {
    titre?: string; type_document_id?: string | null;
    correspondant_id?: string | null; date_document?: string | null;
    statut?: string; prive?: boolean;
  }) {
    return this.request<{ document: any }>(`/zillodocs/documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteZdDocument(id: string) {
    return this.request<{ deleted: boolean }>(`/zillodocs/documents/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkDeleteZdDocuments(ids: string[]) {
    return this.request<{ deleted: number }>('/zillodocs/documents/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
  }

  async addZdTag(documentId: string, tagId: string) {
    return this.request<{ added: boolean }>(`/zillodocs/documents/${documentId}/tags/${tagId}`, {
      method: 'POST',
    });
  }

  async removeZdTag(documentId: string, tagId: string) {
    return this.request<{ removed: boolean }>(`/zillodocs/documents/${documentId}/tags/${tagId}`, {
      method: 'DELETE',
    });
  }

  async getZdCorrespondants() {
    return this.request<{ correspondants: any[] }>('/zillodocs/correspondants');
  }

  async createZdCorrespondant(data: { nom: string; type: string; email?: string }) {
    return this.request<{ correspondant: any }>('/zillodocs/correspondants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateZdCorrespondant(id: string, data: { nom?: string; type?: string; email?: string }) {
    return this.request<{ correspondant: any }>(`/zillodocs/correspondants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteZdCorrespondant(id: string) {
    return this.request<{ deleted: boolean }>(`/zillodocs/correspondants/${id}`, {
      method: 'DELETE',
    });
  }

  async getZdTags() {
    return this.request<{ tags: any[] }>('/zillodocs/tags');
  }

  async createZdTag(data: { nom: string; couleur?: string }) {
    return this.request<{ tag: any }>('/zillodocs/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateZdTag(tagId: string, data: { nom?: string; couleur?: string }) {
    return this.request<{ tag: any }>(`/zillodocs/tags/${tagId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteZdTag(tagId: string) {
    return this.request<{ deleted: boolean }>(`/zillodocs/tags/${tagId}`, {
      method: 'DELETE',
    });
  }

  getZdFileUrl(documentId: string) {
    return `${API_URL}/zillodocs/documents/${documentId}/file`;
  }

  getZdThumbnailUrl(documentId: string) {
    return `${API_URL}/zillodocs/documents/${documentId}/thumbnail`;
  }

  getZdPreviewUrl(documentId: string) {
    return `${API_URL}/zillodocs/documents/${documentId}/preview`;
  }

  // --- Variants ---
  async getZdVariants(documentId: string) {
    return this.request<{ variants: Array<{ type: string; filename: string; mime: string; size: number }> }>(
      `/zillodocs/documents/${documentId}/variants`
    );
  }

  getZdVariantUrl(documentId: string, variantType: string) {
    return `${API_URL}/zillodocs/documents/${documentId}/variant/${variantType}`;
  }

  // --- Assignations ---
  async assignZdDocument(documentId: string, assigneA: string, note?: string) {
    return this.request<{ assignation: any }>(`/zillodocs/documents/${documentId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ assigne_a: assigneA, note }),
    });
  }

  async getZdAssignments(documentId: string) {
    return this.request<{ assignations: any[] }>(`/zillodocs/documents/${documentId}/assignments`);
  }

  async getZdMyAssignments(statut?: string) {
    const sp = statut ? `?statut=${statut}` : '';
    return this.request<{ assignations: any[]; pendingCount: number }>(`/zillodocs/my-assignments${sp}`);
  }

  async updateZdAssignment(assignationId: string, statut: string) {
    return this.request<{ assignation: any }>(`/zillodocs/assignments/${assignationId}`, {
      method: 'PUT',
      body: JSON.stringify({ statut }),
    });
  }

  async deleteZdAssignment(assignationId: string) {
    return this.request(`/zillodocs/assignments/${assignationId}`, {
      method: 'DELETE',
    });
  }

  // --- Reclassify ---
  async reclassifyZdDocument(documentId: string) {
    return this.request<{ classification: any; suggestedTags: string[] }>(`/zillodocs/documents/${documentId}/reclassify`, {
      method: 'POST',
    });
  }

  // --- History ---
  async getZdDocumentHistory(documentId: string, limit = 50, offset = 0) {
    const sp = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    return this.request<{ events: any[]; total: number }>(`/zillodocs/documents/${documentId}/history?${sp}`);
  }

  // --- Processing Queue ---
  async getZdProcessingQueue() {
    return this.request<{
      processing: any[];
      pending: any[];
      completed_recent: any[];
      counts: { processing: number; pending: number; completed: number; errored: number };
    }>('/zillodocs/queue');
  }

  // --- PIN (Confidential) ---
  async getZdPinStatus() {
    return this.request<{ hasPin: boolean }>('/zillodocs/pin/status');
  }

  async setZdPin(pin: string) {
    return this.request<{ message: string }>('/zillodocs/pin/set', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
  }

  async verifyZdPin(pin: string) {
    return this.request<{ valid: boolean }>('/zillodocs/pin/verify', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
  }

  async removeZdPin(pin: string) {
    return this.request<{ message: string }>('/zillodocs/pin', {
      method: 'DELETE',
      body: JSON.stringify({ pin }),
    });
  }

  // --- Bulk Import ---
  async uploadZdBulk(files: File[], prive?: boolean, tagIds?: string[]) {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    if (prive) formData.append('prive', 'true');
    if (tagIds && tagIds.length > 0) formData.append('tagIds', JSON.stringify(tagIds));
    return this.request<{ documents: any[]; uploaded: number }>('/zillodocs/bulk/upload', {
      method: 'POST',
      body: formData,
    }, 120_000);
  }

  async getZdBulkEstimate() {
    return this.request<{
      totalFiles: number;
      totalPages: number;
      costOcr: number;
      costClassification: number;
      costTotal: number;
      byType: { mime: string; count: number; pages: number }[];
    }>('/zillodocs/bulk/estimate');
  }

  async getZdBulkStatus() {
    return this.request<{
      totalFiles: number;
      processed: number;
      pending: number;
      errors: number;
      totalPages: number;
      estimatedCost: number;
    }>('/zillodocs/bulk/status');
  }

  async getZdBulkDocuments() {
    return this.request<{ documents: any[] }>('/zillodocs/bulk/documents');
  }

  async launchZdBulkProcess() {
    return this.request<{ launched: number }>('/zillodocs/bulk/process', { method: 'POST' });
  }

  async stopZdBulk() {
    return this.request<{ stopped: number }>('/zillodocs/bulk/stop', { method: 'POST' });
  }

  async cancelZdBulk() {
    return this.request<{ deleted: number }>('/zillodocs/bulk/cancel', { method: 'DELETE' });
  }

  // --- Signatures & Tampons ---
  async getZdSignatures(type?: 'signature' | 'tampon') {
    const qs = type ? `?type=${type}` : '';
    return this.request<{ signatures: any[] }>(`/zillodocs/signatures${qs}`);
  }

  async getZdSignatureImage(id: string) {
    return this.request<{ signature: any }>(`/zillodocs/signatures/${id}/image`);
  }

  async createZdSignature(data: { type: 'signature' | 'tampon'; nom?: string; image_base64?: string; file?: File }) {
    if (data.file) {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('type', data.type);
      if (data.nom) formData.append('nom', data.nom);
      return this.request<{ signature: any }>('/zillodocs/signatures', {
        method: 'POST',
        body: formData,
      });
    }
    return this.request<{ signature: any }>('/zillodocs/signatures', {
      method: 'POST',
      body: JSON.stringify({ type: data.type, nom: data.nom, image_base64: data.image_base64 }),
    });
  }

  async updateZdSignature(id: string, nom: string) {
    return this.request<{ signature: any }>(`/zillodocs/signatures/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ nom }),
    });
  }

  async deleteZdSignature(id: string) {
    return this.request<{ deleted: boolean }>(`/zillodocs/signatures/${id}`, {
      method: 'DELETE',
    });
  }

  // --- Mentions manuscrites ---
  async getZdMentions() {
    return this.request<{ mentions: any[] }>('/zillodocs/mentions');
  }

  async createZdMention(texte: string) {
    return this.request<{ mention: any }>('/zillodocs/mentions', {
      method: 'POST',
      body: JSON.stringify({ texte }),
    });
  }

  async deleteZdMention(id: string) {
    return this.request<{ deleted: boolean }>(`/zillodocs/mentions/${id}`, {
      method: 'DELETE',
    });
  }

  // --- Sign / Overlay ---
  async signZdDocument(documentId: string, overlays: any[]) {
    return this.request<{ document: any; signedDocumentId: string }>(
      `/zillodocs/documents/${documentId}/sign`,
      { method: 'POST', body: JSON.stringify({ overlays }) }
    );
  }

  async rotateZdPage(documentId: string, pageNumber: number) {
    return this.request<{ message: string }>(
      `/zillodocs/documents/${documentId}/rotate-page`,
      { method: 'POST', body: JSON.stringify({ pageNumber }) }
    );
  }

  async deleteZdPage(documentId: string, pageNumber: number) {
    return this.request<{ message: string; nb_pages: number }>(
      `/zillodocs/documents/${documentId}/delete-page`,
      { method: 'POST', body: JSON.stringify({ pageNumber }) }
    );
  }

  // --- Activity Log ---
  async getZdActivity(params?: { limit?: number; offset?: number; action?: string }) {
    const sp = new URLSearchParams();
    if (params?.limit) sp.set('limit', params.limit.toString());
    if (params?.offset) sp.set('offset', params.offset.toString());
    if (params?.action) sp.set('action', params.action);
    const qs = sp.toString();
    return this.request<{ events: any[]; total: number }>(`/zillodocs/activity${qs ? `?${qs}` : ''}`);
  }

  // --- Backup / Restore ---
  async getZdBackupEstimate() {
    return this.request<{
      documentCount: number;
      correspondantCount: number;
      tagCount: number;
      signatureCount: number;
      totalFileSize: number;
      fileCount: number;
    }>('/zillodocs/backup/estimate');
  }

  async exportZdBackup() {
    return this.request<{
      filename: string;
      stats: { version: string; accountId: string; exportedAt: string; tables: Record<string, number>; fileCount: number; totalFileSize: number };
    }>('/zillodocs/backup/export', { method: 'POST' }, 600_000);
  }

  async downloadZdBackup(filename: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 600_000);
    try {
      const response = await fetch(`${API_URL}/zillodocs/backup/download/${encodeURIComponent(filename)}`, {
        headers,
        signal: controller.signal,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: { message: 'Erreur téléchargement backup' } }));
        throw new Error(err?.error?.message || 'Erreur téléchargement backup');
      }
      return response.blob();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async restoreZdBackup(file: File) {
    const formData = new FormData();
    formData.append('backupFile', file);
    return this.request<{
      documentCount: number;
      tableCounts: Record<string, number>;
      errors: string[];
    }>('/zillodocs/backup/restore', { method: 'POST', body: formData }, 600_000);
  }

  async getZdBackupHistory() {
    return this.request<{
      backups: { filename: string; size: number; createdAt: string }[];
    }>('/zillodocs/backup/history');
  }

  async deleteZdBackup(filename: string) {
    return this.request<{ deleted: boolean }>(`/zillodocs/backup/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    });
  }

  // --- Integrity Check ---
  async getZdIntegrityCheck() {
    return this.request<{
      totalDocuments: number;
      checkedAt: string;
      issues: Array<{
        type: string;
        documentId: string;
        titre: string;
        fichierNom: string;
        detail: string;
        repairable: boolean;
        nearDuplicatePair?: {
          docA: { id: string; titre: string; fichierNom: string; createdAt: string; taille: number; nbPages: number | null; ocrLength: number; thumbnailPath: string | null };
          docB: { id: string; titre: string; fichierNom: string; createdAt: string; taille: number; nbPages: number | null; ocrLength: number; thumbnailPath: string | null };
          similarity: number;
          suggestedKeepId: string;
        };
      }>;
      summary: Record<string, { count: number; repairable: boolean; label: string }>;
      recentlyDeleted: Array<{
        id: string;
        titre: string;
        fichierNom: string;
        deletedAt: string;
        deletedDaysAgo: number;
        hasThumbnail: boolean;
      }>;
    }>('/zillodocs/integrity/check', {}, 120_000);
  }

  async restoreZdDocument(documentIds: string[]) {
    return this.request<{
      repaired: number;
      failed: number;
      details: Array<{ documentId: string; success: boolean; error?: string }>;
    }>('/zillodocs/integrity/restore', {
      method: 'POST',
      body: JSON.stringify({ documentIds }),
    });
  }

  async repairZdIntegrity(issueType: string, documentIds?: string[], keepDocumentId?: string) {
    return this.request<{
      repaired: number;
      failed: number;
      details: Array<{ documentId: string; success: boolean; error?: string }>;
    }>('/zillodocs/integrity/repair', {
      method: 'POST',
      body: JSON.stringify({ issueType, documentIds, keepDocumentId }),
    }, 300_000);
  }

  // ── Admin — Constantes Sociales ──────────────────────────────────
  async getConstantesSocialesAll() {
    return this.request<any[]>('/admin/constantes-sociales');
  }

  async getConstantesSocialesByYear(annee: number) {
    return this.request<any>(`/admin/constantes-sociales/${annee}`);
  }

  async createConstantesSociales(data: Record<string, any>) {
    return this.request<any>('/admin/constantes-sociales', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateConstantesSociales(annee: number, data: Record<string, any>) {
    return this.request<any>(`/admin/constantes-sociales/${annee}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteConstantesSociales(annee: number) {
    return this.request<void>(`/admin/constantes-sociales/${annee}`, {
      method: 'DELETE',
    });
  }

  // ── Public — Constantes Sociales ──────────────────────────────────
  async getConstantesSocialesCurrent() {
    return this.request<any>('/equipe/constantes-sociales/current');
  }

  // ── ZilloTV ─────────────────────────────────────────────────────

  async getZilloTvSettings() {
    return this.request<{ settings: any }>('/zillo-tv/settings');
  }

  async updateZilloTvSettings(data: Record<string, any>) {
    return this.request<{ settings: any }>('/zillo-tv/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async uploadZilloTvLogo(file: File) {
    const formData = new FormData();
    formData.append('logo', file);
    return this.request<{ settings: any }>('/zillo-tv/settings/logo', {
      method: 'POST',
      body: formData,
    });
  }

  async getZilloTvPlaylist() {
    return this.request<{ items: any[] }>('/zillo-tv/playlist');
  }

  async updateZilloTvPlaylist(items: { slide_key: string; position: number; is_enabled: boolean; config?: Record<string, unknown> }[]) {
    return this.request<{ items: any[] }>('/zillo-tv/playlist', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  async resetZilloTvPlaylist() {
    return this.request<{ items: any[] }>('/zillo-tv/playlist/reset', {
      method: 'POST',
    });
  }

  async getZilloTvDevices() {
    return this.request<{ devices: any[] }>('/zillo-tv/devices');
  }

  async pairZilloTvDevice(code: string) {
    return this.request<{ device: any }>('/zillo-tv/devices/pair', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async deleteZilloTvDevice(id: string) {
    return this.request<null>(`/zillo-tv/devices/${id}`, {
      method: 'DELETE',
    });
  }

  async renameZilloTvDevice(id: string, name: string) {
    return this.request<{ device: any }>(`/zillo-tv/devices/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async getZilloTvPreviewWeather() {
    return this.request<{ weather: any }>('/zillo-tv/preview/weather');
  }

  async getZilloTvPreviewCinema() {
    return this.request<{ movies: any[] }>('/zillo-tv/preview/cinema');
  }

  async getZilloTvSlides(filters?: { category?: string; subcategory?: string; status?: string }) {
    const params = new URLSearchParams();
    if (filters?.category) params.set('category', filters.category);
    if (filters?.subcategory) params.set('subcategory', filters.subcategory);
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();
    return this.request<{ slides: any[] }>(`/zillo-tv/slides${qs ? `?${qs}` : ''}`);
  }

  async getZilloTvSlideCategories() {
    return this.request<{ categories: any[] }>('/zillo-tv/slides/categories');
  }

  async getZilloTvCategoryConfig() {
    return this.request<{ config: any[] }>('/zillo-tv/category-config');
  }

  async createZilloTvSlide(data: Record<string, any>) {
    return this.request<{ slide: any }>('/zillo-tv/slides', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateZilloTvSlide(id: string, data: Record<string, any>) {
    return this.request<{ slide: any }>(`/zillo-tv/slides/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteZilloTvSlide(id: string) {
    return this.request<null>(`/zillo-tv/slides/${id}`, { method: 'DELETE' });
  }

  async generateZilloTvSlidePrompts(id: string) {
    return this.request<{ slide: any }>(`/zillo-tv/slides/${id}/generate-prompts`, { method: 'POST' });
  }

  async generateZilloTvSlideImage(id: string, hint?: string) {
    return this.request<{ slide: any }>(`/zillo-tv/slides/${id}/generate-image`, {
      method: 'POST',
      body: hint ? JSON.stringify({ hint }) : undefined,
    });
  }

  async generateZilloTvSlideVideo(id: string, hint?: string) {
    return this.request<any>(`/zillo-tv/slides/${id}/generate-video`, {
      method: 'POST',
      body: hint ? JSON.stringify({ hint }) : undefined,
    });
  }

  async checkZilloTvVideoStatus(videoId: number, slideId: string) {
    return this.request<any>(`/zillo-tv/slides/video-status/${videoId}?slide_id=${slideId}`);
  }

  async getZilloTvSlideAssets(id: string) {
    return this.request<{ images: { url: string; filename: string; created: number }[]; videos: { url: string; filename: string; created: number }[] }>(
      `/zillo-tv/slides/${id}/assets`
    );
  }

  async setZilloTvSlideAsset(id: string, url: string) {
    return this.request<{ slide: any }>(`/zillo-tv/slides/${id}/assets`, {
      method: 'PUT',
      body: JSON.stringify({ url }),
    });
  }

  async deleteZilloTvSlideAsset(id: string, filename: string) {
    return this.request<null>(`/zillo-tv/slides/${id}/assets/${encodeURIComponent(filename)}`, { method: 'DELETE' });
  }

  async updateZilloTvCategoryConfig(items: { slide_category: string; subcategory: string; is_enabled: boolean }[]) {
    return this.request<{ config: any[] }>('/zillo-tv/category-config', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  async uploadZilloTvPresentation(name: string, file: File, category?: string) {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('file', file);
    if (category) formData.append('category', category);

    // Upload directly to backend (bypass Vite proxy which crashes on large files)
    const backendUrl = window.location.hostname === 'localhost'
      ? 'http://localhost:3000/api'
      : API_URL;

    const headers: Record<string, string> = {};
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;

    const response = await fetch(`${backendUrl}/zillo-tv/presentations/upload`, {
      method: 'POST',
      body: formData,
      headers,
    });

    return response.json();
  }

  async createZilloTvFromTemplate(name: string, template: string, data: any, photos: File[] = []) {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('template', template);
    formData.append('data', JSON.stringify(data));
    photos.forEach(p => formData.append('photos', p));

    const backendUrl = window.location.hostname === 'localhost'
      ? 'http://localhost:3000/api'
      : API_URL;

    const headers: Record<string, string> = {};
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;

    const response = await fetch(`${backendUrl}/zillo-tv/presentations/create-from-template`, {
      method: 'POST',
      body: formData,
      headers,
    });

    return response.json();
  }

  async getZilloTvPresentations() {
    return this.request<{ items: any[] }>('/zillo-tv/presentations');
  }

  async getZilloTvPresentation(id: string) {
    return this.request<any>(`/zillo-tv/presentations/${id}`);
  }

  async updateZilloTvPresentation(id: string, data: Record<string, unknown>) {
    return this.request<any>(`/zillo-tv/presentations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteZilloTvPresentation(id: string) {
    return this.request<void>(`/zillo-tv/presentations/${id}`, {
      method: 'DELETE',
    });
  }

  async updateZilloTvPptxSlide(presentationId: string, slideId: string, data: Record<string, unknown>) {
    return this.request<any>(`/zillo-tv/presentations/${presentationId}/slides/${slideId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async uploadZilloTvSlideImage(presentationId: string, slideId: string, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    return this.request<{ imageUrl: string }>(`/zillo-tv/presentations/${presentationId}/slides/${slideId}/upload-image`, {
      method: 'POST',
      body: formData,
    });
  }

  // Playlists
  async getZilloTvPlaylists() {
    return this.request<{ items: any[] }>('/zillo-tv/playlists');
  }

  async createZilloTvPlaylist(data: { name: string; description?: string; color?: string }) {
    return this.request<any>('/zillo-tv/playlists', { method: 'POST', body: JSON.stringify(data) });
  }

  async getZilloTvPlaylistById(id: string) {
    return this.request<any>(`/zillo-tv/playlists/${id}`);
  }

  async updateZilloTvPlaylist2(id: string, data: Record<string, unknown>) {
    return this.request<any>(`/zillo-tv/playlists/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteZilloTvPlaylist(id: string) {
    return this.request<void>(`/zillo-tv/playlists/${id}`, { method: 'DELETE' });
  }

  async getZilloTvQrCodes() {
    return this.request<{ items: any[] }>('/zillo-tv/qr-codes');
  }

  async createZilloTvQrCode(data: { label: string; url: string; subtitle?: string }) {
    return this.request<any>('/zillo-tv/qr-codes', { method: 'POST', body: JSON.stringify(data) });
  }

  async deleteZilloTvQrCode(id: string) {
    return this.request<void>(`/zillo-tv/qr-codes/${id}`, { method: 'DELETE' });
  }

  async updateZilloTvPlaylistEntries(id: string, entries: { presentationId?: string; clipType?: string; config?: Record<string, any>; position: number }[]) {
    return this.request<any>(`/zillo-tv/playlists/${id}/entries`, { method: 'PUT', body: JSON.stringify({ entries }) });
  }

  // Schedule
  async getZilloTvSchedule(deviceId?: string) {
    const q = deviceId ? `?deviceId=${deviceId}` : '';
    return this.request<{ items: any[] }>(`/zillo-tv/schedule${q}`);
  }

  async createZilloTvScheduleBlock(data: Record<string, unknown>) {
    return this.request<any>('/zillo-tv/schedule', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateZilloTvScheduleBlock(id: string, data: Record<string, unknown>) {
    return this.request<any>(`/zillo-tv/schedule/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteZilloTvScheduleBlock(id: string) {
    return this.request<void>(`/zillo-tv/schedule/${id}`, { method: 'DELETE' });
  }

  // ── ZilloTV Videos ──

  async getZilloTvVideoCategories() {
    return this.request<{ items: any[] }>('/zillo-tv/videos/categories');
  }

  async getZilloTvVideos(filters?: { category?: string; sourceType?: string; search?: string }) {
    const q = new URLSearchParams();
    if (filters?.category) q.set('category', filters.category);
    if (filters?.sourceType) q.set('sourceType', filters.sourceType);
    if (filters?.search) q.set('search', filters.search);
    const qs = q.toString();
    return this.request<{ items: any[] }>(`/zillo-tv/videos${qs ? `?${qs}` : ''}`);
  }

  async getZilloTvVideo(id: string) {
    return this.request<any>(`/zillo-tv/videos/${id}`);
  }

  async createZilloTvVideo(data: FormData) {
    return this.request<any>('/zillo-tv/videos', { method: 'POST', body: data });
  }

  async updateZilloTvVideo(id: string, data: Record<string, unknown>) {
    return this.request<any>(`/zillo-tv/videos/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteZilloTvVideo(id: string) {
    return this.request<void>(`/zillo-tv/videos/${id}`, { method: 'DELETE' });
  }

  // ── ZilloTV VOD ──

  async startZilloTvVod(deviceId: string, videoId: string) {
    return this.request<{ vodSessionId: string }>(`/zillo-tv/devices/${deviceId}/vod`, {
      method: 'POST', body: JSON.stringify({ videoId }),
    });
  }

  async stopZilloTvVod(deviceId: string) {
    return this.request<void>(`/zillo-tv/devices/${deviceId}/vod`, { method: 'DELETE' });
  }

  async getZilloTvDeviceActivity(deviceId: string) {
    return this.request<{ activity: null | 'vod' | 'screen_share' }>(`/zillo-tv/devices/${deviceId}/activity`);
  }

  // ── ZilloTV Remote (device-token auth) ──

  async createZilloTvPairingCode(type: 'player' | 'remote' = 'player') {
    return this.request<{ device: any }>('/zillo-tv/devices/create-pairing', {
      method: 'POST', body: JSON.stringify({ type }),
    });
  }

  async getZilloTvRemoteDevices(remoteToken: string) {
    return this.request<{ devices: any[] }>('/zillo-tv/remote/devices', {
      headers: { 'X-Device-Token': remoteToken },
    });
  }

  async getZilloTvRemoteVideos(remoteToken: string) {
    return this.request<{ items: any[] }>('/zillo-tv/remote/videos', {
      headers: { 'X-Device-Token': remoteToken },
    });
  }

  async startZilloTvRemoteVod(remoteToken: string, deviceId: string, videoId: string) {
    return this.request<{ vodSessionId: string }>(`/zillo-tv/remote/devices/${deviceId}/vod`, {
      method: 'POST', body: JSON.stringify({ videoId }),
      headers: { 'X-Device-Token': remoteToken },
    });
  }

  async stopZilloTvRemoteVod(remoteToken: string, deviceId: string) {
    return this.request<void>(`/zillo-tv/remote/devices/${deviceId}/vod`, {
      method: 'DELETE',
      headers: { 'X-Device-Token': remoteToken },
    });
  }

  async getZilloTvRemoteDeviceActivity(remoteToken: string, deviceId: string) {
    return this.request<{ activity: null | 'vod' | 'screen_share' }>(`/zillo-tv/remote/devices/${deviceId}/activity`, {
      headers: { 'X-Device-Token': remoteToken },
    });
  }

  async checkZilloTvRemotePairing(code: string) {
    return this.request<{ paired: boolean; device_token?: string }>(`/zillo-tv/remote/pairing-status/${code}`);
  }

  // ── ZilloTV Admin — Dynamic Clips ──

  async getZilloTvAdminRecettes() {
    return this.request<{ recettes: any[] }>('/zillo-tv/admin/recettes');
  }

  async updateZilloTvAdminRecette(id: string, data: Record<string, unknown>) {
    return this.request<{ recette: any }>(`/zillo-tv/admin/recettes/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteZilloTvAdminRecette(id: string) {
    return this.request<void>(`/zillo-tv/admin/recettes/${id}`, { method: 'DELETE' });
  }

  async searchPexelsForRecette(id: string, query: string) {
    return this.request<{ photos: any[] }>(`/zillo-tv/admin/recettes/${id}/image-pexels`, {
      method: 'POST', body: JSON.stringify({ query }),
    });
  }

  async updateRecetteImage(id: string, imageUrl: string) {
    return this.request<{ image_path: string }>(`/zillo-tv/admin/recettes/${id}/image`, {
      method: 'PUT', body: JSON.stringify({ imageUrl }),
    });
  }

  async getZilloTvAdminSaison() {
    return this.request<{ items: any[] }>('/zillo-tv/admin/saison');
  }

  async createZilloTvAdminSaison(data: { name: string; type: string; months: number[] }) {
    return this.request<{ item: any }>('/zillo-tv/admin/saison', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateZilloTvAdminSaison(id: string, data: Record<string, unknown>) {
    return this.request<{ item: any }>(`/zillo-tv/admin/saison/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteZilloTvAdminSaison(id: string) {
    return this.request<void>(`/zillo-tv/admin/saison/${id}`, { method: 'DELETE' });
  }

  async updateSaisonImage(id: string, imageUrl: string) {
    return this.request<{ image_path: string }>(`/zillo-tv/admin/saison/${id}/image`, {
      method: 'PUT', body: JSON.stringify({ imageUrl }),
    });
  }

  async getZilloTvAdminSaviezVous() {
    return this.request<{ items: any[] }>('/zillo-tv/admin/saviezvous');
  }

  async createZilloTvAdminSaviezVous(data: { text: string; category?: string; icon?: string }) {
    return this.request<{ item: any }>('/zillo-tv/admin/saviezvous', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateZilloTvAdminSaviezVous(id: string, data: Record<string, unknown>) {
    return this.request<{ item: any }>(`/zillo-tv/admin/saviezvous/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteZilloTvAdminSaviezVous(id: string) {
    return this.request<void>(`/zillo-tv/admin/saviezvous/${id}`, { method: 'DELETE' });
  }

  // ── ZilloTV Admin — Citations ──

  async getZilloTvAdminCitations() {
    return this.request<{ items: any[] }>('/zillo-tv/admin/citations');
  }

  async createZilloTvAdminCitation(data: { text: string; author?: string }) {
    return this.request<{ item: any }>('/zillo-tv/admin/citations', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateZilloTvAdminCitation(id: string, data: Record<string, unknown>) {
    return this.request<{ item: any }>(`/zillo-tv/admin/citations/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteZilloTvAdminCitation(id: string) {
    return this.request<void>(`/zillo-tv/admin/citations/${id}`, { method: 'DELETE' });
  }

  // ── ZilloTV Admin — Conseils ──

  async getZilloTvAdminConseils() {
    return this.request<{ items: any[] }>('/zillo-tv/admin/conseils');
  }

  async createZilloTvAdminConseil(data: { text: string; icon?: string }) {
    return this.request<{ item: any }>('/zillo-tv/admin/conseils', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateZilloTvAdminConseil(id: string, data: Record<string, unknown>) {
    return this.request<{ item: any }>(`/zillo-tv/admin/conseils/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteZilloTvAdminConseil(id: string) {
    return this.request<void>(`/zillo-tv/admin/conseils/${id}`, { method: 'DELETE' });
  }

  async searchPexels(query: string) {
    return this.request<{ photos: any[] }>('/zillo-tv/admin/pexels-search', {
      method: 'POST', body: JSON.stringify({ query }),
    });
  }
}

// ── Smart Order types ──────────────────────────────────────────

export interface SmartOrderSettings {
  id: string;
  account_id: string;
  auto_generate: boolean;
  generation_day: number;
  generation_time: string;
  order_horizon_days: number;
  optimize_shipping: boolean;
  notify_on_generation: boolean;
}

export interface SmartOrderLine {
  id: string;
  suggestion_id: string;
  product_id: string;
  variant_id: string | null;
  supplier_id: string;
  suggested_quantity: number;
  adjusted_quantity: number | null;
  current_stock: number;
  optimal_stock: number | null;
  avg_monthly_consumption: number;
  unit_price: number | null;
  estimated_line_total: number;
  priority: 'critical' | 'high' | 'normal' | 'fill_franco';
  reason: string;
  ai_explanation: string | null;
  decision: 'pending' | 'accepted' | 'modified' | 'rejected';
  rejection_reason: string | null;
  product_name: string;
  variant_label: string | null;
  supplier_name: string;
  stock_unit: string;
}

export interface SmartOrderSuggestion {
  id: string;
  account_id: string;
  generated_at: string;
  generation_type: 'scheduled' | 'manual';
  status: 'pending' | 'reviewed' | 'ordered' | 'dismissed';
  total_suppliers: number;
  total_lines: number;
  total_estimated_cost: number;
  reviewed_at: string | null;
  reviewed_by: string | null;
  lines?: SmartOrderLine[];
}

export interface SmartOrderPreference {
  id: string;
  account_id: string;
  product_id: string | null;
  variant_id: string | null;
  supplier_id: string | null;
  rule_type: string;
  rule_value: Record<string, unknown>;
  learned_from_rejections: number;
  confidence: number;
  is_active: boolean;
  product_name: string | null;
  variant_label: string | null;
  supplier_name: string | null;
}

// ── Anomaly types ──────────────────────────────────────────────

export type AnomalyType =
  | 'consumption_spike'
  | 'consumption_drop'
  | 'price_deviation'
  | 'expiration_waste'
  | 'inventory_drift';

export type AnomalySeverity = 'critical' | 'high' | 'medium' | 'low';
export type AnomalyStatus = 'new' | 'acknowledged' | 'investigating' | 'resolved' | 'false_positive';

export interface StockAnomaly {
  id: string;
  account_id: string;
  anomaly_type: AnomalyType;
  severity: AnomalySeverity;
  product_id: string | null;
  variant_id: string | null;
  lot_id: string | null;
  title: string;
  description: string;
  ai_analysis: string | null;
  anomaly_data: Record<string, unknown>;
  status: AnomalyStatus;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_comment: string | null;
  resolution_action: string | null;
  detected_at: string;
  detection_method: 'realtime' | 'scheduled' | 'inventory';
  detection_score: number | null;
  product_name?: string;
  variant_label?: string | null;
}

export interface AnomalyStats {
  new_count: string;
  critical_count: string;
  high_count: string;
  this_week: string;
  false_positives: string;
  total: string;
}

// ── Chat types ─────────────────────────────────────────────────

export interface ChatSession {
  id: string;
  title: string | null;
  messages_count: number;
  tools_used: string[];
  started_at: string;
  last_message_at: string;
}

export interface ChatMessage {
  id: string;
  session_id?: string;
  role: 'user' | 'assistant';
  content: string;
  tool_calls?: unknown;
  tool_results?: unknown;
  created_at: string;
}

// ── Auto-Tag types ─────────────────────────────────────────────

export interface ProductTags {
  generic_type: string;
  tags: string[];
  brand: string | null;
}

export interface AutoTagStatus {
  total_products: number;
  tagged: {
    ai_auto: number;
    ai_batch: number;
    manual: number;
    community: number;
    none: number;
  };
  coverage_percent: number;
  corrections_count: number;
  by_type: Record<string, number>;
}

export interface UntaggedProduct {
  id: string;
  name: string;
  manufacturer: string | null;
  created_at: string;
}

export interface GenericTypeDefinition {
  generic_type: string;
  label_fr: string;
  label_en: string;
  icon: string;
  search_synonyms: string[];
  product_count: number;
}

export interface ConceptSearchResult {
  concept: string;
  resolved_type: string | null;
  matched_products: number;
  products: Array<{
    id: string;
    name: string;
    manufacturer: string | null;
    generic_type: string | null;
    brand: string | null;
    tags: string[];
    variant_label: string | null;
    stock_unit: string;
    current_stock: number;
    match_reason: string;
  }>;
}

export interface BackfillResult {
  status: string;
  total: number;
  processed: number;
  errors: number;
  by_type: Record<string, number>;
  duration_seconds: number;
}

// ── Cart types ─────────────────────────────────────────────────

export interface CartItemInput {
  product_id?: string;
  product_search?: string;
  variant_id?: string;
  packaging_id?: string;
  supplier_id?: string;
  supplier_search?: string;
  quantity: number;
  source?: string;
  promo_discount_percent?: number;
  notes?: string;
}

export interface CartItem {
  id: string;
  product_id: string;
  product_name: string;
  variant_id: string | null;
  variant_label: string | null;
  packaging_id: string | null;
  supplier_id: string;
  supplier_name: string;
  supplier_ref: string | null;
  quantity: number;
  unit_price: number;
  line_total: number;
  promo_discount_percent: number | null;
  promo_unit_price: number | null;
  effective_price: number;
  source: string;
  notes: string | null;
  created_at: string;
}

export interface CartSupplierGroup {
  supplier_id: string;
  supplier_name: string;
  items_count: number;
  total: number;
  free_shipping_minimum: number | null;
  free_shipping_reached: boolean;
  free_shipping_remaining: number;
  items: CartItem[];
}

export interface CartViewResult {
  success?: boolean;
  empty?: boolean;
  message?: string;
  suppliers?: CartSupplierGroup[];
  total_items?: number;
  grand_total?: number;
}

export interface CartSummaryResult {
  suppliers_count: number;
  total_items: number;
  grand_total: number;
}

export interface CartAddResult {
  success: boolean;
  action?: string;
  cart_item_id?: string;
  product_name?: string;
  quantity?: number;
  unit_price?: number;
  line_total?: number;
  promo?: string | null;
  supplier_name?: string;
  cart_summary?: {
    items_count: number;
    total: number;
    free_shipping_minimum: number | null;
    free_shipping_reached: boolean;
    free_shipping_remaining: number | null;
  };
  error?: string;
  options?: Array<{ product_id: string; variant_id: string | null; name: string; current_stock: number }>;
}

export interface CartValidateResult {
  success: boolean;
  orders_created?: Array<{
    purchase_order_id: string;
    reference: string;
    supplier: string;
    total: number;
    lines_count: number;
    order_email: string | null;
  }>;
  total_orders?: number;
  grand_total?: number;
  error?: string;
}

export const api = new ApiClient();
export default api;

// ── Journal types ──────────────────────────────────────────────

export interface JournalEntry {
  id: string;
  event_type: string;
  entity_type: string;
  entity_id: string | null;
  entity_name: string | null;
  details: Record<string, any> | null;
  performed_by: string | null;
  performed_by_name: string | null;
  created_at: string;
  source: string | null;
}

// ── Registries types ────────────────────────────────────────────

export interface ManufacturerCabinetEntry {
  id: string;
  account_id: string;
  account_name: string;
  name: string;
  website: string | null;
  country: string | null;
  notes: string | null;
  created_at: string;
  product_count: number;
}

export interface ManufacturerRegistryEntry {
  id: string;
  name: string;
  website: string | null;
  country: string | null;
  is_public: boolean;
  is_active: boolean;
  logo_url?: string | null;
  created_at: string;
  updated_at: string | null;
  product_count: number;
}

export interface SupplierRegistryEntry {
  id: string;
  name: string;
  website: string | null;
  product_search_url: string | null;
  logo_url: string | null;
  country: string | null;
  is_public: boolean;
  is_active: boolean;
  created_by_account_id: string | null;
  created_by_account_name: string | null;
  created_at: string;
  updated_at: string | null;
  cabinet_count: number;
}

export interface CategoryEntry {
  id: string;
  name: string;
  code: string;
  color: string | null;
  description: string | null;
  default_stock_unit: string;
  default_alert_threshold: number;
  display_order: number;
  is_active: boolean;
  parent_id: string | null;
}

export interface CatalogEntry {
  id: string;
  name: string;
  manufacturer: string | null;
  manufacturer_ref: string | null;
  barcode: string | null;
  category_id: string | null;
  category_name: string | null;
  description: string | null;
  usage_count: number;
  is_active: boolean;
  variant_count: string;
  created_at: string;
  updated_at: string;
}

export interface CatalogVariantEntry {
  id: string;
  catalog_id: string;
  label: string;
  barcode: string | null;
  manufacturer_ref: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ── Équipe types ────────────────────────────────────────────────

export interface EquipeEmploye {
  id: string;
  account_id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  adresse_postale: string;
  telephone: string;
  email_personnel: string | null;
  email_professionnel: string;
  photo_url: string | null;
  nationalite: string | null;
  numero_rpps: string | null;
  numero_adeli: string | null;
  numero_securite_sociale: string | null;
  poste: string;
  poste_libelle: string | null;
  specialisation: string | null;
  date_entree: string;
  date_sortie: string | null;
  motif_sortie: string | null;
  statut: string;
  contrat_type: string;
  contrat_date_debut: string;
  contrat_date_fin: string | null;
  temps_type: string;
  heures_hebdo: number;
  taux_revenu: number | null;
  taux_horaire: number | null;
  taux_pas: number;
  est_salarie: boolean;
  pointage_requis: boolean;
  couleur: string | null;
  lien_utilisateur_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface EquipeDocument {
  id: string;
  employe_id: string;
  entite_id: string;
  type: string;
  nom_fichier: string | null;
  date_emission: string | null;
  date_expiration: string | null;
  notes: string | null;
  alerte_j90: boolean;
  alerte_j60: boolean;
  alerte_j30: boolean;
  alerte_j15: boolean;
  alerte_j7: boolean;
  created_at: string;
}

export interface PointageEvent {
  id: string;
  employe_id: string;
  entite_id: string;
  type: string;
  horodatage: string;
  methode: string;
  validation: string;
  motif_manuel: string | null;
  est_correction: boolean;
  created_at: string;
  nom?: string;
  prenom?: string;
}

export interface EquipeAlerte {
  id: string;
  employe_id: string | null;
  entite_id: string;
  type: string;
  statut: string;
  titre: string;
  message: string;
  date_echeance: string | null;
  acquittee_par_id: string | null;
  acquittee_le: string | null;
  created_at: string;
  nom?: string;
  prenom?: string;
}

export interface EquipeRole {
  id: string;
  entite_id: string;
  nom: string;
  description: string | null;
  est_systeme: boolean;
  permissions: Array<{ id: string; module: string; ressource: string; action: string }>;
  created_at: string;
  updated_at: string;
}

// ── Studio Audio types ──────────────────────────────────────────

export interface StudioVoice {
  voice_id: string;
  name: string;
  category: string;
  labels: Record<string, string>;
  preview_url: string | null;
}

export interface StudioTemplate {
  id: string;
  type: string;
  titre: string;
  texte: string;
}

export interface StudioGeneratedMessage {
  id: string;
  account_id: string;
  titre: string;
  texte: string;
  type_message: string;
  voice_id: string;
  voice_name: string;
  model_id: string;
  musique_fond_id: string | null;
  volume_voix: number;
  volume_musique: number;
  fichier_voix: string | null;
  fichier_final: string | null;
  duree_secondes: number | null;
  statut: 'brouillon' | 'en_generation' | 'genere' | 'erreur';
  erreur: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  musique_nom?: string | null;
}

export interface StudioBackgroundMusic {
  id: string;
  account_id: string | null;
  nom: string;
  description: string | null;
  fichier: string;
  duree_secondes: number | null;
  categorie: string;
  is_global: boolean;
  licence_type: string;
  licence_source: string | null;
  licence_url: string | null;
  licence_details: string | null;
  created_at: string;
}

export interface RiDocument {
  id: string;
  accountId: string;
  patientId: string;
  documentTypeKey: string;
  templateId?: string;
  content: Record<string, unknown>;
  status: 'draft' | 'final';
  createdBy: string;
  finalizedAt?: string;
  pdfPath?: string;
  audioPath?: string;
  transcription?: string;
  createdAt: string;
  updatedAt: string;
  patientName?: string;
  documentTypeLabel?: string;
  createdByName?: string;
}

export interface RiDocumentVersion {
  id: string;
  documentId: string;
  versionNumber: number;
  content: Record<string, unknown>;
  savedBy: string;
  savedAt: string;
  reason: 'manual' | 'finalized';
  savedByName?: string;
}

export interface RiChecklistItem {
  id: string;
  categoryId: string;
  templateId: string;
  name: string;
  itemType: 'checkbox' | 'quantity' | 'select';
  productId?: string;
  productName?: string;
  defaultQty?: number;
  unit?: string;
  selectOptions: string[];
  isRequired: boolean;
  position: number;
}

export interface RiChecklistCategory {
  id: string;
  templateId: string;
  name: string;
  color: string;
  position: number;
  items?: RiChecklistItem[];
}

export interface RiChecklistTemplate {
  id: string;
  accountId: string;
  name: string;
  description?: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  categories?: RiChecklistCategory[];
}

export interface RiPreopChecklistItem {
  id: string;
  checklistId: string;
  templateItemId?: string;
  value: Record<string, unknown>;
  name?: string;
  itemType?: 'checkbox' | 'quantity' | 'select';
  productId?: string;
  productName?: string;
  defaultQty?: number;
  unit?: string;
  selectOptions?: string[];
  isRequired?: boolean;
  position?: number;
  categoryName?: string;
  categoryColor?: string;
  stockAvailable?: number;
}

export interface RiPreopChecklist {
  id: string;
  accountId: string;
  patientId: string;
  templateId?: string;
  plannedDate: string;
  status: 'draft' | 'validated' | 'completed';
  plannedImplants: unknown[];
  notes?: string;
  preparedBy?: string;
  validatedBy?: string;
  validatedAt?: string;
  pdfPath?: string;
  createdAt: string;
  updatedAt: string;
  patientName?: string;
  templateName?: string;
  preparedByName?: string;
  validatedByName?: string;
  items?: RiPreopChecklistItem[];
}
